/**
 * Dynamic Permutation Engine for ORCA Global Pedigree (v2.0)
 * 
 * This module generates case-specific pedigree permutations from kinship data
 * using canonical template matching based on documented business rules.
 * 
 * Business Rules Applied:
 * 1. LR Threshold: Only include hypotheses with LR > 1
 * 2. All Tested Individuals Required
 * 3. Reverse Directional Hypotheses (when age unavailable)
 * 4. Canonical Template Matching (not brute-force cartesian products)
 * 5. Biological Constraints (gender roles, no role conflicts)
 * 6. Untested Placeholder Individuals
 * 7. Disjointed Structures (only for Parent/Child and Full Siblings)
 * 8. Kinship Graph-Driven Generation
 * 9. Case-Specific Dynamic Generation
 */

export interface TestedIndividual {
  id: string;
  name: string;
  gender: 'M' | 'F';
  age?: string | null;
}

export interface KinshipPair {
  relative1: TestedIndividual;
  relative2: TestedIndividual;
  relationship: string;
  parentChildLR: number;
  fullSiblingLR: number;
  extendedFamilyLR: number;
}

export interface PedigreeNode {
  id: string;
  gender: 'male' | 'female';
  parents: { id: string; type: string }[];
  children: { id: string; type: string }[];
  spouses: { id: string; type: string }[];
  siblings: { id: string; type: string }[];
  data: {
    name: string;
    label?: string;
    isIndex?: boolean;
    isUntested?: boolean;
    kinshipId?: string;  // Canonical kinship ID for LR lookup (only for tested individuals)
    age?: string | null; // Age of the individual for display
  };
}

/**
 * Represents an asserted relationship in a pedigree permutation.
 * Used for composite likelihood scoring.
 */
export interface AssertedRelationship {
  individual1: string;  // Name of first individual
  individual2: string;  // Name of second individual
  relationshipType: 'PC' | 'FS' | 'EXT';  // Parent/Child, Full Sibling, Extended
  lr: number;  // The pairwise LR value
  logLR: number;  // log10(LR) for scoring
}

/**
 * Likelihood score details for a pedigree permutation.
 */
export interface LikelihoodScoreDetails {
  compositeScore: number;  // Sum of log10(LR) values
  assertedRelationships: AssertedRelationship[];  // Relationships used in scoring
  edgeCount: number;  // Number of edges scored
  missingEdges: string[];  // Edges that couldn't be scored (no LR data)
  isApproximate: boolean;  // Always true for composite scoring
  method: 'composite-log-likelihood';  // Scoring method identifier
}

export interface Permutation {
  id: number;
  nodes: PedigreeNode[];
  nodesB?: PedigreeNode[];
  rootId: string;
  rootIdB?: string;
  label: string;
  type: string;
  category: 'parentChild' | 'fullSibling' | 'extended';
  details: string;
  // Likelihood scoring fields (for ranking)
  likelihoodScore?: LikelihoodScoreDetails;
}

const LR_THRESHOLD = 1;

function createNodeId(name: string, prefix: string = ''): string {
  return `${prefix}${name.toLowerCase().replace(/[^a-z0-9]/g, '_')}`;
}

function genderToNodeGender(gender: 'M' | 'F'): 'male' | 'female' {
  return gender === 'M' ? 'male' : 'female';
}

export function extractTestedIndividuals(kinshipPairs: KinshipPair[]): TestedIndividual[] {
  const individuals = new Map<string, TestedIndividual>();
  
  kinshipPairs.forEach(pair => {
    if (!individuals.has(pair.relative1.id)) {
      individuals.set(pair.relative1.id, pair.relative1);
    }
    if (!individuals.has(pair.relative2.id)) {
      individuals.set(pair.relative2.id, pair.relative2);
    }
  });
  
  return Array.from(individuals.values());
}

/**
 * LR value lookup keyed by sorted pair of individual IDs.
 * Key format: "id1|id2" where id1 < id2 alphabetically.
 */
interface LRLookup {
  pc: Map<string, number>;  // Parent/Child LR values
  fs: Map<string, number>;  // Full Sibling LR values
  ext: Map<string, number>; // Extended Family LR values
}

/**
 * A connected component of the kinship graph.
 * Contains individuals who are connected via any LR > 1 edges.
 */
interface KinshipConnectedComponent {
  members: TestedIndividual[];
  memberIds: Set<string>;
  hasPcEdges: boolean;
  hasFsEdges: boolean;
  hasExtEdges: boolean;
}

interface KinshipGraph {
  individuals: TestedIndividual[];
  individualById: Map<string, TestedIndividual>;
  pcEdges: Map<string, Set<string>>; // id -> set of ids with PC LR > 1
  fsEdges: Map<string, Set<string>>; // id -> set of ids with FS LR > 1
  extEdges: Map<string, Set<string>>; // id -> set of ids with Extended LR > 1
  males: TestedIndividual[];
  females: TestedIndividual[];
  lrLookup: LRLookup;  // Actual LR values for scoring
  nameById: Map<string, string>;  // id -> name lookup
  idByName: Map<string, string>;  // name (lowercase) -> id lookup for scoring
  connectedComponents: KinshipConnectedComponent[];  // Disjoint kinship components
}

/**
 * Creates a sorted key for LR lookup from two IDs.
 * Ensures consistent key regardless of argument order.
 */
function makeLRKey(id1: string, id2: string): string {
  return id1 < id2 ? `${id1}|${id2}` : `${id2}|${id1}`;
}

/**
 * Finds connected components in the kinship graph using union-find.
 * Components are formed by any LR > 1 edges (PC ∪ FS ∪ EXT).
 */
function findKinshipConnectedComponents(
  individuals: TestedIndividual[],
  pcEdges: Map<string, Set<string>>,
  fsEdges: Map<string, Set<string>>,
  extEdges: Map<string, Set<string>>
): KinshipConnectedComponent[] {
  // Union-Find data structure
  const parent = new Map<string, string>();
  const rank = new Map<string, number>();
  
  // Initialize each individual as its own parent
  individuals.forEach(ind => {
    parent.set(ind.id, ind.id);
    rank.set(ind.id, 0);
  });
  
  // Find with path compression
  function find(x: string): string {
    if (parent.get(x) !== x) {
      parent.set(x, find(parent.get(x)!));
    }
    return parent.get(x)!;
  }
  
  // Union by rank
  function union(x: string, y: string): void {
    const rootX = find(x);
    const rootY = find(y);
    if (rootX === rootY) return;
    
    const rankX = rank.get(rootX) || 0;
    const rankY = rank.get(rootY) || 0;
    
    if (rankX < rankY) {
      parent.set(rootX, rootY);
    } else if (rankX > rankY) {
      parent.set(rootY, rootX);
    } else {
      parent.set(rootY, rootX);
      rank.set(rootX, rankX + 1);
    }
  }
  
  // Union all edges (any LR > 1 creates connection)
  individuals.forEach(ind => {
    const id = ind.id;
    
    // PC edges
    pcEdges.get(id)?.forEach(otherId => union(id, otherId));
    // FS edges  
    fsEdges.get(id)?.forEach(otherId => union(id, otherId));
    // EXT edges
    extEdges.get(id)?.forEach(otherId => union(id, otherId));
  });
  
  // Group by root
  const componentMap = new Map<string, TestedIndividual[]>();
  individuals.forEach(ind => {
    const root = find(ind.id);
    if (!componentMap.has(root)) {
      componentMap.set(root, []);
    }
    componentMap.get(root)!.push(ind);
  });
  
  // Build component objects with metadata
  const components: KinshipConnectedComponent[] = [];
  componentMap.forEach(members => {
    const memberIds = new Set(members.map(m => m.id));
    
    // Check which edge types exist within this component
    let hasPcEdges = false;
    let hasFsEdges = false;
    let hasExtEdges = false;
    
    members.forEach(m => {
      pcEdges.get(m.id)?.forEach(otherId => {
        if (memberIds.has(otherId)) hasPcEdges = true;
      });
      fsEdges.get(m.id)?.forEach(otherId => {
        if (memberIds.has(otherId)) hasFsEdges = true;
      });
      extEdges.get(m.id)?.forEach(otherId => {
        if (memberIds.has(otherId)) hasExtEdges = true;
      });
    });
    
    components.push({
      members,
      memberIds,
      hasPcEdges,
      hasFsEdges,
      hasExtEdges
    });
  });
  
  // Sort by size (largest first)
  components.sort((a, b) => b.members.length - a.members.length);
  
  return components;
}

export function buildKinshipGraph(kinshipPairs: KinshipPair[]): KinshipGraph {
  const individuals = extractTestedIndividuals(kinshipPairs);
  const individualById = new Map<string, TestedIndividual>();
  const nameById = new Map<string, string>();
  const idByName = new Map<string, string>();
  individuals.forEach(i => {
    individualById.set(i.id, i);
    nameById.set(i.id, i.name);
    idByName.set(i.name.toLowerCase(), i.id);
  });
  
  const pcEdges = new Map<string, Set<string>>();
  const fsEdges = new Map<string, Set<string>>();
  const extEdges = new Map<string, Set<string>>();
  
  // LR value storage for scoring
  const lrLookup: LRLookup = {
    pc: new Map<string, number>(),
    fs: new Map<string, number>(),
    ext: new Map<string, number>()
  };
  
  individuals.forEach(i => {
    pcEdges.set(i.id, new Set());
    fsEdges.set(i.id, new Set());
    extEdges.set(i.id, new Set());
  });
  
  kinshipPairs.forEach(pair => {
    const id1 = pair.relative1.id;
    const id2 = pair.relative2.id;
    const key = makeLRKey(id1, id2);
    
    // Store all LR values (even below threshold) for comprehensive scoring
    if (pair.parentChildLR > 0) {
      lrLookup.pc.set(key, pair.parentChildLR);
    }
    if (pair.fullSiblingLR > 0) {
      lrLookup.fs.set(key, pair.fullSiblingLR);
    }
    if (pair.extendedFamilyLR > 0) {
      lrLookup.ext.set(key, pair.extendedFamilyLR);
    }
    
    // Edge existence (LR > threshold)
    if (pair.parentChildLR > LR_THRESHOLD) {
      pcEdges.get(id1)?.add(id2);
      pcEdges.get(id2)?.add(id1);
    }
    if (pair.fullSiblingLR > LR_THRESHOLD) {
      fsEdges.get(id1)?.add(id2);
      fsEdges.get(id2)?.add(id1);
    }
    if (pair.extendedFamilyLR > LR_THRESHOLD) {
      extEdges.get(id1)?.add(id2);
      extEdges.get(id2)?.add(id1);
    }
  });
  
  const males = individuals.filter(i => i.gender === 'M');
  const females = individuals.filter(i => i.gender === 'F');
  
  // Compute connected components (individuals connected via any LR > 1 edges)
  const connectedComponents = findKinshipConnectedComponents(individuals, pcEdges, fsEdges, extEdges);
  
  return { individuals, individualById, pcEdges, fsEdges, extEdges, males, females, lrLookup, nameById, idByName, connectedComponents };
}

function hasEdge(edges: Map<string, Set<string>>, id1: string, id2: string): boolean {
  return edges.get(id1)?.has(id2) ?? false;
}

// ============================================================================
// COMPOSITE LIKELIHOOD SCORING
// ============================================================================

/**
 * Extracts asserted parent-child relationships from pedigree nodes.
 * Analyzes parent[] arrays of each node to find PC relationships.
 * Only includes relationships between TESTED individuals (not placeholders).
 */
function extractAssertedPCRelationships(
  nodes: PedigreeNode[],
  graph: KinshipGraph
): { name1: string; name2: string; id1: string; id2: string }[] {
  const relationships: { name1: string; name2: string; id1: string; id2: string }[] = [];
  const seenPairs = new Set<string>();
  
  nodes.forEach(node => {
    const childName = node.data?.name;
    // Skip untested nodes
    if (!childName || node.data?.isUntested) return;
    
    // Use idByName for direct lookup (more reliable than iteration)
    const childId = graph.idByName.get(childName.toLowerCase());
    if (!childId) return;  // Not a tested individual
    
    // Check parents
    node.parents.forEach(parentRef => {
      const parentNode = nodes.find(n => n.id === parentRef.id);
      // Skip untested parents
      if (!parentNode || parentNode.data?.isUntested) return;
      
      const parentName = parentNode.data?.name;
      if (!parentName) return;
      
      // Use idByName for direct lookup
      const parentId = graph.idByName.get(parentName.toLowerCase());
      if (!parentId) return;  // Not a tested individual
      
      // Create unique key to avoid duplicates
      const key = makeLRKey(childId, parentId);
      if (!seenPairs.has(key)) {
        seenPairs.add(key);
        relationships.push({ name1: childName, name2: parentName, id1: childId, id2: parentId });
      }
    });
  });
  
  return relationships;
}

/**
 * Extracts asserted full sibling relationships from pedigree nodes.
 * Siblings share the same parents (both have same parent IDs).
 * Only includes relationships between TESTED individuals (not placeholders).
 */
function extractAssertedFSRelationships(
  nodes: PedigreeNode[],
  graph: KinshipGraph
): { name1: string; name2: string; id1: string; id2: string }[] {
  const relationships: { name1: string; name2: string; id1: string; id2: string }[] = [];
  const seenPairs = new Set<string>();
  
  // Group tested nodes by their parent set
  const nodesByParents = new Map<string, PedigreeNode[]>();
  
  nodes.forEach(node => {
    // Skip untested nodes
    if (node.data?.isUntested) return;
    // Skip nodes without valid names in kinship data
    const nodeId = graph.idByName.get(node.data?.name?.toLowerCase() || '');
    if (!nodeId) return;
    
    const parentIds = node.parents.map(p => p.id).sort().join(',');
    if (parentIds && node.parents.length === 2) {  // Full siblings need 2 parents
      if (!nodesByParents.has(parentIds)) {
        nodesByParents.set(parentIds, []);
      }
      nodesByParents.get(parentIds)!.push(node);
    }
  });
  
  // For each group with 2+ children, they are full siblings
  nodesByParents.forEach(siblings => {
    if (siblings.length < 2) return;
    
    for (let i = 0; i < siblings.length; i++) {
      for (let j = i + 1; j < siblings.length; j++) {
        const sib1 = siblings[i];
        const sib2 = siblings[j];
        
        // Already filtered for untested above, but double-check
        if (sib1.data?.isUntested || sib2.data?.isUntested) continue;
        
        const name1 = sib1.data?.name;
        const name2 = sib2.data?.name;
        if (!name1 || !name2) continue;
        
        // Use idByName for direct lookup
        const id1 = graph.idByName.get(name1.toLowerCase());
        const id2 = graph.idByName.get(name2.toLowerCase());
        
        if (!id1 || !id2) continue;  // Not tested individuals
        
        const key = makeLRKey(id1, id2);
        if (!seenPairs.has(key)) {
          seenPairs.add(key);
          relationships.push({ name1, name2, id1, id2 });
        }
      }
    }
  });
  
  return relationships;
}

/**
 * Computes the composite log-likelihood score for a pedigree permutation.
 * 
 * Formula: Score = Σ log₁₀(LR) for all asserted relationships
 * 
 * This is an APPROXIMATION since pairwise LRs are not independent.
 * For rigorous analysis, use full Elston-Stewart algorithm.
 */
function computeCompositeLikelihoodScore(
  nodes: PedigreeNode[],
  nodesB: PedigreeNode[] | undefined,
  permutationType: string,
  graph: KinshipGraph
): LikelihoodScoreDetails {
  const assertedRelationships: AssertedRelationship[] = [];
  const missingEdges: string[] = [];
  
  // Combine all nodes
  const allNodes = nodesB ? [...nodes, ...nodesB] : nodes;
  
  // Extract asserted PC relationships
  const pcRels = extractAssertedPCRelationships(allNodes, graph);
  pcRels.forEach(rel => {
    const key = makeLRKey(rel.id1, rel.id2);
    const lr = graph.lrLookup.pc.get(key);
    
    if (lr && lr > 0) {
      assertedRelationships.push({
        individual1: rel.name1,
        individual2: rel.name2,
        relationshipType: 'PC',
        lr,
        logLR: Math.log10(lr)
      });
    } else {
      missingEdges.push(`PC:${rel.name1}-${rel.name2}`);
    }
  });
  
  // Extract asserted FS relationships
  const fsRels = extractAssertedFSRelationships(allNodes, graph);
  fsRels.forEach(rel => {
    const key = makeLRKey(rel.id1, rel.id2);
    const lr = graph.lrLookup.fs.get(key);
    
    if (lr && lr > 0) {
      assertedRelationships.push({
        individual1: rel.name1,
        individual2: rel.name2,
        relationshipType: 'FS',
        lr,
        logLR: Math.log10(lr)
      });
    } else {
      // For full siblings, this is expected if they're asserted but not tested
      missingEdges.push(`FS:${rel.name1}-${rel.name2}`);
    }
  });
  
  // Calculate composite score
  const compositeScore = assertedRelationships.reduce((sum, rel) => sum + rel.logLR, 0);
  
  return {
    compositeScore,
    assertedRelationships,
    edgeCount: assertedRelationships.length,
    missingEdges,
    isApproximate: true,
    method: 'composite-log-likelihood'
  };
}

function includesAllTestedIndividuals(
  nodes: PedigreeNode[],
  nodesB: PedigreeNode[] | undefined,
  testedNames: Set<string>
): boolean {
  const presentNames = new Set<string>();
  
  const addNames = (nodeList: PedigreeNode[]) => {
    nodeList.forEach(node => {
      if (!node.data?.isUntested && node.data?.name) {
        presentNames.add(node.data.name.toLowerCase());
      }
    });
  };
  
  addNames(nodes);
  if (nodesB) addNames(nodesB);
  
  const testedArray = Array.from(testedNames);
  for (let i = 0; i < testedArray.length; i++) {
    if (!presentNames.has(testedArray[i])) {
      return false;
    }
  }
  
  return true;
}

/**
 * Extract the names of tested individuals from a permutation's nodes
 */
function getTestedNamesFromPermutation(
  nodes: PedigreeNode[],
  nodesB: PedigreeNode[] | undefined
): Set<string> {
  const testedNames = new Set<string>();
  
  const extractNames = (nodeList: PedigreeNode[]) => {
    nodeList.forEach(node => {
      if (!node.data?.isUntested && node.data?.name) {
        testedNames.add(node.data.name.toLowerCase());
      }
    });
  };
  
  extractNames(nodes);
  if (nodesB) extractNames(nodesB);
  
  return testedNames;
}

/**
 * Check that all tested individuals in the permutation have at least one
 * kinship edge to another tested individual in the permutation.
 * This validates that we only include individuals with actual kinship support.
 */
function hasInternalKinshipSupport(
  permTestedNames: Set<string>,
  graph: KinshipGraph
): boolean {
  const { pcEdges, fsEdges, extEdges, idByName } = graph;
  
  // If only one individual, it's valid (single individual has no pairs to check)
  if (permTestedNames.size <= 1) return true;
  
  const namesArray = Array.from(permTestedNames);
  
  // Each tested individual must have at least one kinship edge to another
  // tested individual in this permutation
  for (let i = 0; i < namesArray.length; i++) {
    const name = namesArray[i];
    const id = idByName.get(name);
    if (!id) continue;
    
    let hasEdgeToPermMember = false;
    
    for (let j = 0; j < namesArray.length; j++) {
      if (i === j) continue;
      
      const otherName = namesArray[j];
      const otherId = idByName.get(otherName);
      if (!otherId) continue;
      
      // Check for any kinship edge
      if (hasEdge(pcEdges, id, otherId) ||
          hasEdge(fsEdges, id, otherId) ||
          hasEdge(extEdges, id, otherId)) {
        hasEdgeToPermMember = true;
        break;
      }
    }
    
    if (!hasEdgeToPermMember) {
      // This individual has no kinship edge to anyone else in the permutation
      return false;
    }
  }
  
  return true;
}

// ============================================================================
// HELPER: Find standard parent/child pairs based on kinship graph
// ============================================================================

interface ParentChildAssignment {
  father: TestedIndividual;
  mother: TestedIndividual;
  childM: TestedIndividual;
  childF: TestedIndividual;
}

/**
 * Generic parent-children cluster found in the kinship graph.
 * Supports any number of children and flexible parent gender.
 */
interface ParentCluster {
  parent: TestedIndividual;
  children: TestedIndividual[];
  pcEdgeCount: number; // Number of PC edges from parent to children
}

/**
 * Find all potential parent clusters in the kinship graph.
 * A parent cluster is an individual with PC edges to multiple other individuals.
 */
function findParentClusters(graph: KinshipGraph): ParentCluster[] {
  const { individuals, pcEdges } = graph;
  const clusters: ParentCluster[] = [];
  
  for (const individual of individuals) {
    const childIds = pcEdges.get(individual.id) ?? new Set();
    if (childIds.size >= 1) {
      const children = Array.from(childIds)
        .map(id => graph.individualById.get(id))
        .filter((i): i is TestedIndividual => i !== undefined);
      
      if (children.length >= 1) {
        clusters.push({
          parent: individual,
          children,
          pcEdgeCount: children.length
        });
      }
    }
  }
  
  // Sort by PC edge count descending (parents with more children first)
  clusters.sort((a, b) => b.pcEdgeCount - a.pcEdgeCount);
  
  return clusters;
}

/**
 * Find sibling clusters from FS edges.
 * A sibling cluster is a set of individuals connected by FS edges.
 */
function findSiblingClusters(graph: KinshipGraph): TestedIndividual[][] {
  const { individuals, fsEdges } = graph;
  const visited = new Set<string>();
  const clusters: TestedIndividual[][] = [];
  
  for (const individual of individuals) {
    if (visited.has(individual.id)) continue;
    
    // BFS to find all siblings in this cluster
    const cluster: TestedIndividual[] = [];
    const queue = [individual];
    
    while (queue.length > 0) {
      const current = queue.shift()!;
      if (visited.has(current.id)) continue;
      visited.add(current.id);
      cluster.push(current);
      
      const sibIds = fsEdges.get(current.id) ?? new Set();
      for (const sibId of sibIds) {
        if (!visited.has(sibId)) {
          const sib = graph.individualById.get(sibId);
          if (sib) queue.push(sib);
        }
      }
    }
    
    if (cluster.length >= 2) {
      clusters.push(cluster);
    }
  }
  
  return clusters;
}

/**
 * Identify standard parent pair by finding which M+F pair has PC edges to the other M+F pair.
 * For legacy 2M+2F cases - kept for backward compatibility.
 * Criteria (in order):
 * 1. Prefer pairs where BOTH parents have PC edges to BOTH children (complete coverage)
 * 2. If tied, prefer pairs where children have FS edge between them (siblings indicator)
 * 3. If still tied, use deterministic alphabetical ordering
 */
function findParentChildAssignment(graph: KinshipGraph): ParentChildAssignment | null {
  const { males, females, pcEdges, fsEdges } = graph;
  
  // For 2M+2F cases, use the legacy algorithm
  if (males.length !== 2 || females.length !== 2) {
    // For non-2M+2F cases, try to construct an assignment from parent clusters
    return findFlexibleParentChildAssignment(graph);
  }
  
  const m1 = males[0], m2 = males[1];
  const f1 = females[0], f2 = females[1];
  
  // Try all 4 possible parent pair combinations
  const combinations: { father: TestedIndividual; mother: TestedIndividual }[] = [
    { father: m1, mother: f1 },
    { father: m1, mother: f2 },
    { father: m2, mother: f1 },
    { father: m2, mother: f2 }
  ];
  
  const scored = combinations.map(combo => {
    const { father, mother } = combo;
    const childM = males.find(m => m.id !== father.id)!;
    const childF = females.find(f => f.id !== mother.id)!;
    
    // Count PC edges from each parent to each child
    const fatherToChildM = hasEdge(pcEdges, father.id, childM.id);
    const fatherToChildF = hasEdge(pcEdges, father.id, childF.id);
    const motherToChildM = hasEdge(pcEdges, mother.id, childM.id);
    const motherToChildF = hasEdge(pcEdges, mother.id, childF.id);
    
    // Score: total PC edges from parents to children
    const score = (fatherToChildM ? 1 : 0) + (fatherToChildF ? 1 : 0) + 
                  (motherToChildM ? 1 : 0) + (motherToChildF ? 1 : 0);
    
    // Complete coverage: both parents have PC to both children (ideal scenario)
    const hasCompleteCoverage = (fatherToChildM || fatherToChildF) && (motherToChildM || motherToChildF);
    
    // FS edge between children indicates they're likely actual siblings
    const childrenHaveFsEdge = hasEdge(fsEdges, childM.id, childF.id);
    
    return { ...combo, childM, childF, score, hasCompleteCoverage, childrenHaveFsEdge };
  });
  
  // Sort by: 1) complete coverage, 2) score descending, 3) children FS edge, 4) alphabetical
  scored.sort((a, b) => {
    // Prefer complete coverage (each parent has at least one PC edge to children)
    if (a.hasCompleteCoverage !== b.hasCompleteCoverage) return a.hasCompleteCoverage ? -1 : 1;
    // Then by score (total PC edges)
    if (b.score !== a.score) return b.score - a.score;
    // Then prefer children with FS edge
    if (a.childrenHaveFsEdge !== b.childrenHaveFsEdge) return a.childrenHaveFsEdge ? -1 : 1;
    // Finally deterministic alphabetical ordering
    return a.father.name.localeCompare(b.father.name);
  });
  
  // Return highest scoring assignment
  return scored[0];
}

/**
 * Flexible parent-child assignment for non-2M+2F cases.
 * Uses parent clusters to construct a valid assignment with untested placeholders if needed.
 * Returns null if no valid assignment can be found.
 */
function findFlexibleParentChildAssignment(graph: KinshipGraph): ParentChildAssignment | null {
  const { males, females, pcEdges } = graph;
  const parentClusters = findParentClusters(graph);
  
  // Find the parent with most PC edges (likely the primary parent)
  if (parentClusters.length === 0) return null;
  
  // Sort by number of PC edges descending
  const sortedClusters = [...parentClusters].sort((a, b) => b.pcEdgeCount - a.pcEdgeCount);
  const primaryParent = sortedClusters[0];
  
  // Need at least 2 PC edges to have a meaningful family structure
  if (primaryParent.pcEdgeCount < 2) return null;
  
  // The primary parent becomes mother (if F) or father (if M)
  let father: TestedIndividual | null = null;
  let mother: TestedIndividual | null = null;
  
  if (primaryParent.parent.gender === 'F') {
    mother = primaryParent.parent;
    // Look for a tested male to be father
    // First check if any of the children could be the father (has PC edges to other children)
    const potentialFathers = males.filter(m => m.id !== mother!.id);
    if (potentialFathers.length > 0) {
      // Prefer males with PC edges to the same children as mother
      const scoredFathers = potentialFathers.map(f => ({
        father: f,
        sharedChildren: primaryParent.children.filter(c => hasEdge(pcEdges, f.id, c.id)).length
      })).sort((a, b) => b.sharedChildren - a.sharedChildren);
      
      // Use the male with most shared children, or just the first male
      father = scoredFathers[0]?.father ?? potentialFathers[0];
    }
  } else {
    father = primaryParent.parent;
    // Look for a tested female to be mother
    const potentialMothers = females.filter(f => f.id !== father!.id);
    if (potentialMothers.length > 0) {
      // Prefer females with PC edges to the same children as father
      const scoredMothers = potentialMothers.map(m => ({
        mother: m,
        sharedChildren: primaryParent.children.filter(c => hasEdge(pcEdges, m.id, c.id)).length
      })).sort((a, b) => b.sharedChildren - a.sharedChildren);
      
      mother = scoredMothers[0]?.mother ?? potentialMothers[0];
    }
  }
  
  // If we don't have both tested parents, we can't use the 2M+2F template
  // Return null and let the flexible generators handle this case
  if (!father || !mother) return null;
  
  // Find children that are not the parents
  const parentIds = new Set([father.id, mother.id]);
  const childCandidates = graph.individuals.filter(i => !parentIds.has(i.id));
  
  // Score children by PC edges from parents
  const scoredChildren = childCandidates.map(c => ({
    child: c,
    score: (hasEdge(pcEdges, father!.id, c.id) ? 1 : 0) + (hasEdge(pcEdges, mother!.id, c.id) ? 1 : 0)
  })).sort((a, b) => b.score - a.score);
  
  // We need at least 2 children
  if (scoredChildren.length < 2) return null;
  
  // Get the two highest-scoring children
  const child1 = scoredChildren[0].child;
  const child2 = scoredChildren[1].child;
  
  // Assign to childM/childF based on actual gender
  let childM: TestedIndividual;
  let childF: TestedIndividual;
  
  if (child1.gender === 'M' && child2.gender === 'F') {
    childM = child1;
    childF = child2;
  } else if (child1.gender === 'F' && child2.gender === 'M') {
    childM = child2;
    childF = child1;
  } else if (child1.gender === 'M' && child2.gender === 'M') {
    // Both male - use them as is (template will adapt)
    childM = child1;
    childF = child2; // Actually male, but using the slot
  } else {
    // Both female
    childM = child1; // Actually female, but using the slot  
    childF = child2;
  }
  
  return { father, mother, childM, childF };
}

/**
 * Generate flexible parent-child permutations for non-standard gender distributions.
 * This handles cases like 1M+3F where the traditional 2M+2F template doesn't apply.
 * 
 * Key insight: Only group children as full siblings if they have FS edge support.
 * Children without FS support become half-siblings (different untested parent).
 */
function generateFlexibleParentChildPermutations(graph: KinshipGraph): Permutation[] {
  const permutations: Permutation[] = [];
  const { individuals, pcEdges, fsEdges } = graph;
  const parentClusters = findParentClusters(graph);
  
  // Find individuals with 2+ PC edges (strong parent candidates)
  const strongParents = parentClusters.filter(c => c.pcEdgeCount >= 2);
  
  if (strongParents.length === 0) return permutations;
  
  // Generate a family structure for each strong parent
  for (const cluster of strongParents) {
    const testedParent = cluster.parent;
    const allChildren = cluster.children;
    
    // Need at least 2 children for a family
    if (allChildren.length < 2) continue;
    
    // Group children into FS-connected clusters (full siblings share both parents)
    // Use union-find to cluster children with FS edges
    const childFsClusters = clusterByFsEdges(allChildren, fsEdges);
    
    // If all children are in one FS cluster, they're all full siblings
    // If they're in multiple clusters, they're half-siblings (share testedParent only)
    
    if (childFsClusters.length === 1 && childFsClusters[0].length === allChildren.length) {
      // All children have FS support with each other - single family unit
      const children = childFsClusters[0];
      const prefix = `flex_pc_${testedParent.name.replace(/\s+/g, '_').toLowerCase()}_`;
      const untestedSpouseId = prefix + 'untested_spouse';
      const testedParentId = createNodeId(testedParent.name, prefix);
      
      const spouseGender = testedParent.gender === 'M' ? 'female' : 'male';
      const parentLabel = testedParent.gender === 'M' ? 'Father' : 'Mother';
      const spouseLabel = testedParent.gender === 'M' ? 'Mother' : 'Father';
      
      // Build child nodes with sibling references only to FS-connected children
      const childNodes: PedigreeNode[] = children.map((child, idx) => ({
        id: createNodeId(child.name, prefix),
        gender: genderToNodeGender(child.gender),
        parents: [
          { id: testedParent.gender === 'M' ? testedParentId : untestedSpouseId, type: 'blood' },
          { id: testedParent.gender === 'F' ? testedParentId : untestedSpouseId, type: 'blood' }
        ],
        children: [],
        spouses: [],
        siblings: children.filter(c => c.id !== child.id).map(c => ({ 
          id: createNodeId(c.name, prefix), 
          type: 'blood' 
        })),
        data: { name: child.name, label: `Child ${idx + 1}`, isIndex: true }
      }));
      
      const nodes: PedigreeNode[] = [
        {
          id: testedParentId,
          gender: genderToNodeGender(testedParent.gender),
          parents: [],
          children: children.map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' })),
          spouses: [{ id: untestedSpouseId, type: 'married' }],
          siblings: [],
          data: { name: testedParent.name, label: parentLabel, isIndex: true }
        },
        {
          id: untestedSpouseId,
          gender: spouseGender,
          parents: [],
          children: children.map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' })),
          spouses: [{ id: testedParentId, type: 'married' }],
          siblings: [],
          data: { name: 'Untested ' + spouseLabel, isUntested: true }
        },
        ...childNodes
      ];
      
      const childNames = children.map(c => c.name.split(' ')[0]).join(', ');
      
      permutations.push({
        id: 0,
        nodes,
        rootId: testedParentId,
        label: `Family: ${testedParent.name.split(' ')[0]} = Parent of ${children.length}`,
        type: 'Flexible Family',
        category: 'parentChild',
        details: `${testedParent.name} is parent of ${childNames}`
      });
    } else {
      // Children are in multiple FS clusters - half-sibling structure
      // Create a pedigree with testedParent having multiple spouses
      const prefix = `flex_hs_${testedParent.name.replace(/\s+/g, '_').toLowerCase()}_`;
      const testedParentId = createNodeId(testedParent.name, prefix);
      const parentLabel = testedParent.gender === 'M' ? 'Father' : 'Mother';
      const spouseLabel = testedParent.gender === 'M' ? 'Mother' : 'Father';
      const spouseGender = testedParent.gender === 'M' ? 'female' : 'male';
      
      const nodes: PedigreeNode[] = [];
      const spouseIds: string[] = [];
      const allChildNodes: PedigreeNode[] = [];
      
      // Create an untested spouse for each FS cluster
      childFsClusters.forEach((cluster, spouseIdx) => {
        const spouseId = `${prefix}spouse_${spouseIdx}`;
        spouseIds.push(spouseId);
        
        // Create child nodes for this cluster
        cluster.forEach((child, childIdx) => {
          const childId = createNodeId(child.name, prefix);
          
          // Siblings are only within this FS cluster
          const siblingRefs = cluster
            .filter(c => c.id !== child.id)
            .map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' as const }));
          
          allChildNodes.push({
            id: childId,
            gender: genderToNodeGender(child.gender),
            parents: [
              { id: testedParent.gender === 'M' ? testedParentId : spouseId, type: 'blood' },
              { id: testedParent.gender === 'F' ? testedParentId : spouseId, type: 'blood' }
            ],
            children: [],
            spouses: [],
            siblings: siblingRefs,
            data: { name: child.name, label: `Child`, isIndex: true }
          });
        });
        
        // Create the untested spouse
        nodes.push({
          id: spouseId,
          gender: spouseGender,
          parents: [],
          children: cluster.map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' })),
          spouses: [{ id: testedParentId, type: 'married' }],
          siblings: [],
          data: { name: `Untested ${spouseLabel} ${spouseIdx + 1}`, isUntested: true }
        });
      });
      
      // Create the tested parent node
      const parentNode: PedigreeNode = {
        id: testedParentId,
        gender: genderToNodeGender(testedParent.gender),
        parents: [],
        children: allChildren.map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' })),
        spouses: spouseIds.map(id => ({ id, type: 'married' })),
        siblings: [],
        data: { name: testedParent.name, label: parentLabel, isIndex: true }
      };
      
      nodes.unshift(parentNode);
      nodes.push(...allChildNodes);
      
      const childNames = allChildren.map(c => c.name.split(' ')[0]).join(', ');
      const clusterDescr = childFsClusters.map((c, i) => 
        c.map(ch => ch.name.split(' ')[0]).join(' & ')
      ).join('; ');
      
      permutations.push({
        id: 0,
        nodes,
        rootId: testedParentId,
        label: `Half-Siblings: ${testedParent.name.split(' ')[0]} = Parent`,
        type: 'Half-Sibling Family',
        category: 'parentChild',
        details: `${testedParent.name} is parent of half-siblings: ${clusterDescr}`
      });
    }
  }
  
  return permutations;
}

/**
 * Generate a pure sibling-only permutation for a cluster.
 * Creates siblings under untested parents - no other individuals included.
 * This ensures we generate valid permutations for sibling pairs without
 * trying to force unrelated individuals into the same pedigree.
 */
function generatePureSiblingPermutation(
  cluster: TestedIndividual[],
  graph: KinshipGraph
): Permutation | null {
  if (cluster.length < 2) return null;
  
  const prefix = `pure_sibs_${cluster[0].name.replace(/\s+/g, '_').toLowerCase()}_`;
  const fatherId = prefix + 'father';
  const motherId = prefix + 'mother';
  
  const nodes: PedigreeNode[] = [];
  
  // Add untested parents
  nodes.push({
    id: fatherId,
    gender: 'male',
    parents: [],
    children: cluster.map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' })),
    spouses: [{ id: motherId, type: 'married' }],
    siblings: [],
    data: { name: 'Untested Father', label: 'Father', isUntested: true }
  });
  
  nodes.push({
    id: motherId,
    gender: 'female',
    parents: [],
    children: cluster.map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' })),
    spouses: [{ id: fatherId, type: 'married' }],
    siblings: [],
    data: { name: 'Untested Mother', label: 'Mother', isUntested: true }
  });
  
  // Add siblings
  cluster.forEach((ind, idx) => {
    nodes.push({
      id: createNodeId(ind.name, prefix),
      gender: genderToNodeGender(ind.gender),
      parents: [
        { id: fatherId, type: 'blood' },
        { id: motherId, type: 'blood' }
      ],
      children: [],
      spouses: [],
      siblings: cluster.filter(s => s.id !== ind.id).map(s => ({
        id: createNodeId(s.name, prefix),
        type: 'blood' as const
      })),
      data: { name: ind.name, label: `Sibling ${idx + 1}`, isIndex: true }
    });
  });
  
  const names = cluster.map(c => c.name.split(' ')[0]).join(' & ');
  
  return {
    id: 0,
    nodes,
    rootId: fatherId,
    label: `Full Siblings: ${names}`,
    type: 'Full Siblings',
    category: 'fullSibling',
    details: `${cluster.length} full siblings with untested parents`
  };
}

/**
 * Generate flexible Full Sibling permutations for non-quartet cases.
 * UPDATED: Creates sibling structures with ONLY individuals who have kinship edges.
 * Does NOT force all individuals into the permutation - only those with evidence.
 */
function generateFlexibleFullSiblingPermutations(graph: KinshipGraph): Permutation[] {
  const permutations: Permutation[] = [];
  const { individuals, fsEdges, pcEdges, extEdges } = graph;
  
  // Find all FS clusters using STRICT union-find (excludes parent-child pairs)
  // This prevents parent-child being clustered as siblings
  const fsClusters = clusterByStrictFsEdges(individuals, fsEdges, pcEdges);
  
  // Generate a Full Sibling permutation for each cluster with 2+ members
  for (const cluster of fsClusters) {
    if (cluster.length < 2) continue;
    
    // Find individuals NOT in this sibling cluster
    const nonSiblings = individuals.filter(ind => !cluster.some(c => c.id === ind.id));
    
    // Find a parent candidate: someone with PC edges to siblings in the cluster
    // IMPORTANT: Only include if they have kinship evidence to cluster members
    let testedParent: TestedIndividual | null = null;
    for (const nonSib of nonSiblings) {
      const hasPC = cluster.some(sib => hasEdge(pcEdges, nonSib.id, sib.id));
      if (hasPC) {
        testedParent = nonSib;
        break;
      }
    }
    
    // FIRST: Generate a pure sibling-only permutation (siblings + untested parents)
    // This is always valid and doesn't require including non-related individuals
    const pureSiblingPerm = generatePureSiblingPermutation(cluster, graph);
    if (pureSiblingPerm) {
      permutations.push(pureSiblingPerm);
    }
    
    // If no tested parent with PC edges to this cluster, skip the extended version
    // (the pure sibling permutation above is sufficient)
    if (!testedParent) continue;
    
    const prefix = `flex_fs_${cluster[0].name.replace(/\s+/g, '_').toLowerCase()}_`;
    
    // If we have a tested parent, use them; otherwise use untested parents
    const fatherId = prefix + 'father';
    const motherId = prefix + 'mother';
    
    const siblingNodes: PedigreeNode[] = cluster.map((ind, idx) => {
      // Determine parent IDs based on tested parent gender
      let parentRefs: { id: string; type: 'blood' }[];
      if (testedParent) {
        const testedParentId = createNodeId(testedParent.name, prefix);
        const untestedId = testedParent.gender === 'M' ? motherId : fatherId;
        parentRefs = [
          { id: testedParent.gender === 'M' ? testedParentId : untestedId, type: 'blood' },
          { id: testedParent.gender === 'F' ? testedParentId : untestedId, type: 'blood' }
        ];
      } else {
        parentRefs = [
          { id: fatherId, type: 'blood' },
          { id: motherId, type: 'blood' }
        ];
      }
      
      return {
        id: createNodeId(ind.name, prefix),
        gender: genderToNodeGender(ind.gender),
        parents: parentRefs,
        children: [],
        spouses: [],
        siblings: cluster.filter(s => s.id !== ind.id).map(s => ({
          id: createNodeId(s.name, prefix),
          type: 'blood' as const
        })),
        data: { name: ind.name, label: `Sibling ${idx + 1}`, isIndex: true }
      };
    });
    
    const nodes: PedigreeNode[] = [];
    
    // Add tested parent if available
    if (testedParent) {
      const testedParentId = createNodeId(testedParent.name, prefix);
      const untestedSpouseId = testedParent.gender === 'M' ? motherId : fatherId;
      const spouseGender = testedParent.gender === 'M' ? 'female' : 'male';
      
      nodes.push({
        id: testedParentId,
        gender: genderToNodeGender(testedParent.gender),
        parents: [],
        children: cluster.map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' })),
        spouses: [{ id: untestedSpouseId, type: 'married' }],
        siblings: [],
        data: { name: testedParent.name, label: testedParent.gender === 'M' ? 'Father' : 'Mother', isIndex: true }
      });
      
      nodes.push({
        id: untestedSpouseId,
        gender: spouseGender,
        parents: [],
        children: cluster.map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' })),
        spouses: [{ id: testedParentId, type: 'married' }],
        siblings: [],
        data: { name: 'Untested ' + (spouseGender === 'male' ? 'Father' : 'Mother'), isUntested: true }
      });
      
      // Add remaining non-siblings as half-siblings (children of tested parent with different spouse)
      const otherNonSibs = nonSiblings.filter(ns => ns.id !== testedParent!.id);
      if (otherNonSibs.length > 0) {
        const otherSpouseId = prefix + 'spouse2';
        const otherSpouseGender = testedParent.gender === 'M' ? 'female' : 'male';
        
        nodes.push({
          id: otherSpouseId,
          gender: otherSpouseGender,
          parents: [],
          children: otherNonSibs.map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' })),
          spouses: [{ id: createNodeId(testedParent.name, prefix), type: 'married' }],
          siblings: [],
          data: { name: 'Untested Spouse 2', isUntested: true }
        });
        
        // Add the other non-siblings as half-siblings
        for (const otherSib of otherNonSibs) {
          nodes.push({
            id: createNodeId(otherSib.name, prefix),
            gender: genderToNodeGender(otherSib.gender),
            parents: [
              { id: testedParent.gender === 'M' ? createNodeId(testedParent.name, prefix) : otherSpouseId, type: 'blood' },
              { id: testedParent.gender === 'F' ? createNodeId(testedParent.name, prefix) : otherSpouseId, type: 'blood' }
            ],
            children: [],
            spouses: [],
            siblings: [],
            data: { name: otherSib.name, label: 'Half-Sibling', isIndex: true }
          });
        }
        
        // Update tested parent's children to include all
        const parentNode = nodes.find(n => n.id === createNodeId(testedParent!.name, prefix));
        if (parentNode) {
          parentNode.children = [
            ...cluster.map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' as const })),
            ...otherNonSibs.map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' as const }))
          ];
          parentNode.spouses = [
            { id: testedParent.gender === 'M' ? motherId : fatherId, type: 'married' },
            { id: otherSpouseId, type: 'married' }
          ];
        }
      }
    } else {
      // No tested parent - use untested parents
      nodes.push({
        id: fatherId,
        gender: 'male',
        parents: [],
        children: cluster.map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' })),
        spouses: [{ id: motherId, type: 'married' }],
        siblings: [],
        data: { name: 'Untested Father', isUntested: true }
      });
      nodes.push({
        id: motherId,
        gender: 'female',
        parents: [],
        children: cluster.map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' })),
        spouses: [{ id: fatherId, type: 'married' }],
        siblings: [],
        data: { name: 'Untested Mother', isUntested: true }
      });
    }
    
    nodes.push(...siblingNodes);
    
    const siblingNames = cluster.map(c => c.name.split(' ')[0]).join(' & ');
    
    permutations.push({
      id: 0,
      nodes,
      rootId: testedParent ? createNodeId(testedParent.name, prefix) : fatherId,
      label: `Full Siblings: ${siblingNames}`,
      type: 'Flexible Siblings',
      category: 'fullSibling',
      details: `${siblingNames} are full siblings${testedParent ? ` (${testedParent.name.split(' ')[0]} is parent)` : ' (untested parents)'}`
    });
  }
  
  return permutations;
}

/**
 * Generate flexible Extended permutations for non-quartet cases.
 * Creates grandparent/linear structures based on PC chains.
 * Includes ALL tested individuals by adding remaining as siblings/children.
 */
function generateFlexibleExtendedPermutations(graph: KinshipGraph): Permutation[] {
  const permutations: Permutation[] = [];
  const { individuals, pcEdges, fsEdges, idByName } = graph;
  
  // Find all valid 3-node PC chains: A -> B -> C where all have PC edges
  const chains: Array<[TestedIndividual, TestedIndividual, TestedIndividual]> = [];
  
  for (const a of individuals) {
    const aNeighbors = pcEdges.get(a.id);
    if (!aNeighbors) continue;
    
    for (const bId of aNeighbors) {
      const b = individuals.find(i => i.id === bId);
      if (!b) continue;
      
      const bNeighbors = pcEdges.get(b.id);
      if (!bNeighbors) continue;
      
      for (const cId of bNeighbors) {
        if (cId === a.id) continue; // Avoid backtracking
        
        const c = individuals.find(i => i.id === cId);
        if (!c) continue;
        
        // Valid chain: A -> B -> C
        chains.push([a, b, c]);
      }
    }
  }
  
  // Generate one permutation per chain (including both directions)
  const seenChains = new Set<string>();
  
  for (const [grandparent, parent, grandchild] of chains) {
    const chainKey = [grandparent.id, parent.id, grandchild.id].join('|');
    
    if (seenChains.has(chainKey)) continue;
    seenChains.add(chainKey);
    
    // Find individuals NOT in the chain
    const chainIds = new Set([grandparent.id, parent.id, grandchild.id]);
    const otherIndividuals = individuals.filter(ind => !chainIds.has(ind.id));
    
    const prefix = `flex_ext_${grandparent.name.replace(/\s+/g, '_').toLowerCase()}_${grandchild.name.replace(/\s+/g, '_').toLowerCase()}_`;
    
    // Build the linear chain: grandparent -> parent -> grandchild
    const gpId = createNodeId(grandparent.name, prefix);
    const pId = createNodeId(parent.name, prefix);
    const gcId = createNodeId(grandchild.name, prefix);
    const spouseGpId = prefix + 'spouse_gp';
    const spousePId = prefix + 'spouse_p';
    
    const gpGender = genderToNodeGender(grandparent.gender);
    const pGender = genderToNodeGender(parent.gender);
    const gcGender = genderToNodeGender(grandchild.gender);
    const spouseGpGender = gpGender === 'male' ? 'female' : 'male';
    const spousePGender = pGender === 'male' ? 'female' : 'male';
    
    const gpLabel = grandparent.gender === 'M' ? 'Grandfather' : 'Grandmother';
    const gcLabel = grandchild.gender === 'M' ? 'Grandson' : 'Granddaughter';
    
    // Determine where to place other individuals
    // Check if they have PC edges to parent (making them siblings of grandchild)
    const childrenOfParent: TestedIndividual[] = [grandchild];
    const otherPlacements: TestedIndividual[] = [];
    
    for (const other of otherIndividuals) {
      if (hasEdge(pcEdges, parent.id, other.id)) {
        // Other individual has PC edge to parent - place as sibling of grandchild
        childrenOfParent.push(other);
      } else {
        otherPlacements.push(other);
      }
    }
    
    const nodes: PedigreeNode[] = [
      {
        id: gpId,
        gender: gpGender,
        parents: [],
        children: [{ id: pId, type: 'blood' }],
        spouses: [{ id: spouseGpId, type: 'married' }],
        siblings: [],
        data: { name: grandparent.name, label: gpLabel, isIndex: true }
      },
      {
        id: spouseGpId,
        gender: spouseGpGender,
        parents: [],
        children: [{ id: pId, type: 'blood' }],
        spouses: [{ id: gpId, type: 'married' }],
        siblings: [],
        data: { name: 'Untested Spouse', isUntested: true }
      },
      {
        id: pId,
        gender: pGender,
        parents: [{ id: gpId, type: 'blood' }, { id: spouseGpId, type: 'blood' }],
        children: childrenOfParent.map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' as const })),
        spouses: [{ id: spousePId, type: 'married' }],
        siblings: [],
        data: { name: parent.name, label: 'Parent', isIndex: true }
      },
      {
        id: spousePId,
        gender: spousePGender,
        parents: [],
        children: childrenOfParent.map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' as const })),
        spouses: [{ id: pId, type: 'married' }],
        siblings: [],
        data: { name: 'Untested Spouse', isUntested: true }
      }
    ];
    
    // Add all children of parent (grandchild and siblings)
    for (let i = 0; i < childrenOfParent.length; i++) {
      const child = childrenOfParent[i];
      const childNodeId = createNodeId(child.name, prefix);
      const isGrandchild = child.id === grandchild.id;
      
      // Find siblings within children of parent
      const siblings = childrenOfParent.filter(c => c.id !== child.id);
      
      nodes.push({
        id: childNodeId,
        gender: genderToNodeGender(child.gender),
        parents: [{ id: pId, type: 'blood' }, { id: spousePId, type: 'blood' }],
        children: [],
        spouses: [],
        siblings: siblings.map(s => ({ id: createNodeId(s.name, prefix), type: 'blood' as const })),
        data: { name: child.name, label: isGrandchild ? gcLabel : 'Sibling', isIndex: true }
      });
    }
    
    // Handle remaining individuals not yet placed
    if (otherPlacements.length > 0) {
      // Place them as additional children of grandparent (aunt/uncle of grandchild)
      for (const other of otherPlacements) {
        const otherId = createNodeId(other.name, prefix);
        
        nodes.push({
          id: otherId,
          gender: genderToNodeGender(other.gender),
          parents: [{ id: gpId, type: 'blood' }, { id: spouseGpId, type: 'blood' }],
          children: [],
          spouses: [],
          siblings: [{ id: pId, type: 'blood' }],
          data: { name: other.name, label: 'Aunt/Uncle', isIndex: true }
        });
        
        // Update grandparent's children
        const gpNode = nodes.find(n => n.id === gpId);
        if (gpNode) {
          gpNode.children.push({ id: otherId, type: 'blood' });
        }
        const spouseGpNode = nodes.find(n => n.id === spouseGpId);
        if (spouseGpNode) {
          spouseGpNode.children.push({ id: otherId, type: 'blood' });
        }
        
        // Update parent's siblings
        const pNode = nodes.find(n => n.id === pId);
        if (pNode) {
          pNode.siblings.push({ id: otherId, type: 'blood' });
        }
      }
    }
    
    permutations.push({
      id: 0,
      nodes,
      rootId: gpId,
      label: `Extended: ${grandparent.name.split(' ')[0]} -> ${parent.name.split(' ')[0]} -> ${grandchild.name.split(' ')[0]}`,
      type: 'Flexible Linear',
      category: 'extended',
      details: `${grandparent.name} is ${gpLabel.toLowerCase()} of ${grandchild.name}`
    });
  }
  
  return permutations;
}

/**
 * Generate multi-avuncular permutations.
 * 
 * This handles cases where:
 * - Two tested parents (male+female) with PC edges to multiple children
 * - One or more additional tested individuals who are siblings of a parent (avuncular relatives)
 * - All children have FS edges between each other (full siblings)
 * 
 * Structure:
 * - Generation 1: Untested grandparents
 * - Generation 2: Parent + avuncular siblings (children of grandparents) + spouse of parent
 * - Generation 3: Children of parent and spouse
 */
function generateMultiAvuncularPermutations(graph: KinshipGraph): Permutation[] {
  const permutations: Permutation[] = [];
  const { individuals, pcEdges, fsEdges, extEdges, lrLookup } = graph;
  
  // Step 1: Find nuclear families (2 tested parents with shared children)
  const nuclearFamilies = findNuclearFamilies(graph);
  
  for (const family of nuclearFamilies) {
    const { father, mother, children } = family;
    
    // RULE 24: Calculate minimum parent PC LR to children for comparison
    // This helps distinguish true parents from avuncular relatives with weak PC edges
    let minParentPcLr = Infinity;
    for (const child of children) {
      const fatherKey = makeLRKey(father.id, child.id);
      const motherKey = makeLRKey(mother.id, child.id);
      const fatherPc = lrLookup.pc.get(fatherKey) || 0;
      const motherPc = lrLookup.pc.get(motherKey) || 0;
      if (fatherPc > 0) minParentPcLr = Math.min(minParentPcLr, fatherPc);
      if (motherPc > 0) minParentPcLr = Math.min(minParentPcLr, motherPc);
    }
    
    // Step 2: Find avuncular relatives - individuals with FS to a parent
    // RULE 24: Allow weak PC edges if they're orders of magnitude weaker than parent PC
    const paternalUncles: TestedIndividual[] = [];
    const maternalUncles: TestedIndividual[] = [];
    
    for (const ind of individuals) {
      if (ind.id === father.id || ind.id === mother.id) continue;
      if (children.some(c => c.id === ind.id)) continue;
      
      // Check if this individual has FS edge to father (paternal side)
      const hasFsToFather = hasEdge(fsEdges, ind.id, father.id);
      // Check if this individual has FS edge to mother (maternal side)
      const hasFsToMother = hasEdge(fsEdges, ind.id, mother.id);
      
      // RULE 24: Check if this individual has SIGNIFICANT PC edges to children
      // A PC edge is "significant" only if it's within 4 orders of magnitude of parent's PC
      // This allows uncles with weak PC edges (e.g., 1207 vs 44 billion) to be correctly identified
      let hasSignificantPcToChildren = false;
      for (const child of children) {
        if (hasEdge(pcEdges, ind.id, child.id)) {
          const key = makeLRKey(ind.id, child.id);
          const indPcLr = lrLookup.pc.get(key) || 0;
          // If uncle's PC is > 1/10000 of parent's PC, consider it significant
          if (indPcLr > minParentPcLr / 10000) {
            hasSignificantPcToChildren = true;
            break;
          }
        }
      }
      
      if (hasFsToFather && !hasSignificantPcToChildren) {
        paternalUncles.push(ind);
      }
      if (hasFsToMother && !hasSignificantPcToChildren) {
        maternalUncles.push(ind);
      }
    }
    
    // Only generate if we have avuncular relatives
    if (paternalUncles.length === 0 && maternalUncles.length === 0) continue;
    
    // Step 3: Build the multi-avuncular pedigree
    const prefix = `avunc_${father.name.replace(/\s+/g, '_').toLowerCase()}_${mother.name.replace(/\s+/g, '_').toLowerCase()}_`;
    
    // Node IDs
    const grandpaId = prefix + 'grandpa';
    const grandmaId = prefix + 'grandma';
    const fatherId = createNodeId(father.name, prefix);
    const motherId = createNodeId(mother.name, prefix);
    
    // Build nodes array
    const nodes: PedigreeNode[] = [];
    
    // All paternal relatives (father + paternal uncles) are children of grandparents
    const paternalSiblings = [father, ...paternalUncles];
    const paternalSiblingIds = paternalSiblings.map(s => createNodeId(s.name, prefix));
    
    // Grandparents (untested)
    nodes.push({
      id: grandpaId,
      gender: 'male',
      parents: [],
      children: paternalSiblingIds.map(id => ({ id, type: 'blood' })),
      spouses: [{ id: grandmaId, type: 'married' }],
      siblings: [],
      data: { name: 'Untested Grandfather', isUntested: true }
    });
    
    nodes.push({
      id: grandmaId,
      gender: 'female',
      parents: [],
      children: paternalSiblingIds.map(id => ({ id, type: 'blood' })),
      spouses: [{ id: grandpaId, type: 'married' }],
      siblings: [],
      data: { name: 'Untested Grandmother', isUntested: true }
    });
    
    // Child IDs
    const childIds = children.map(c => createNodeId(c.name, prefix));
    
    // Father node
    nodes.push({
      id: fatherId,
      gender: 'male',
      parents: [{ id: grandpaId, type: 'blood' }, { id: grandmaId, type: 'blood' }],
      children: childIds.map(id => ({ id, type: 'blood' })),
      spouses: [{ id: motherId, type: 'married' }],
      siblings: paternalUncles.map(u => ({ id: createNodeId(u.name, prefix), type: 'blood' })),
      data: { name: father.name, label: 'Father', isIndex: true }
    });
    
    // Mother node (spouse, not child of grandparents)
    nodes.push({
      id: motherId,
      gender: 'female',
      parents: [],
      children: childIds.map(id => ({ id, type: 'blood' })),
      spouses: [{ id: fatherId, type: 'married' }],
      siblings: [],
      data: { name: mother.name, label: 'Mother', isIndex: true }
    });
    
    // Paternal uncle nodes
    for (const uncle of paternalUncles) {
      const uncleId = createNodeId(uncle.name, prefix);
      const uncleLabel = uncle.gender === 'M' ? 'Paternal Uncle' : 'Paternal Aunt';
      
      // Siblings include father and other uncles
      const uncleSiblings = paternalSiblings
        .filter(s => s.id !== uncle.id)
        .map(s => ({ id: createNodeId(s.name, prefix), type: 'blood' as const }));
      
      nodes.push({
        id: uncleId,
        gender: genderToNodeGender(uncle.gender),
        parents: [{ id: grandpaId, type: 'blood' }, { id: grandmaId, type: 'blood' }],
        children: [],
        spouses: [],
        siblings: uncleSiblings,
        data: { name: uncle.name, label: uncleLabel, isIndex: true }
      });
    }
    
    // Child nodes
    for (let i = 0; i < children.length; i++) {
      const child = children[i];
      const childId = createNodeId(child.name, prefix);
      
      // Siblings are other children
      const childSiblings = children
        .filter(c => c.id !== child.id)
        .map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' as const }));
      
      nodes.push({
        id: childId,
        gender: genderToNodeGender(child.gender),
        parents: [{ id: fatherId, type: 'blood' }, { id: motherId, type: 'blood' }],
        children: [],
        spouses: [],
        siblings: childSiblings,
        data: { name: child.name, label: 'Child', isIndex: true }
      });
    }
    
    // Build label
    const uncleNames = paternalUncles.map(u => u.name.split(' ')[0]).join(', ');
    const childNames = children.map(c => c.name.split(' ')[0]).join(', ');
    
    permutations.push({
      id: 0,
      nodes,
      rootId: grandpaId,
      label: `Multi-Avuncular: ${father.name.split(' ')[0]} + ${uncleNames} (siblings)`,
      type: 'Multi-Avuncular',
      category: 'extended' as const,
      details: `${father.name} & ${mother.name} are parents of ${childNames}; ${uncleNames} ${paternalUncles.length > 1 ? 'are' : 'is'} paternal ${paternalUncles.length > 1 ? 'uncles/aunts' : 'uncle/aunt'}`
    });
  }
  
  return permutations;
}

/**
 * Find nuclear families: pairs of tested male+female individuals who both have PC edges 
 * to multiple shared children, where the children have FS edges between each other.
 */
function findNuclearFamilies(graph: KinshipGraph): Array<{father: TestedIndividual, mother: TestedIndividual, children: TestedIndividual[]}> {
  const { individuals, pcEdges, fsEdges } = graph;
  const families: Array<{father: TestedIndividual, mother: TestedIndividual, children: TestedIndividual[]}> = [];
  const seenPairs = new Set<string>();
  
  const males = individuals.filter(i => i.gender === 'M');
  const females = individuals.filter(i => i.gender === 'F');
  
  for (const male of males) {
    for (const female of females) {
      // Find children that have PC edges to BOTH parents
      const maleChildren = individuals.filter(i => 
        i.id !== male.id && hasEdge(pcEdges, male.id, i.id)
      );
      const femaleChildren = individuals.filter(i => 
        i.id !== female.id && hasEdge(pcEdges, female.id, i.id)
      );
      
      // Shared children = individuals with PC to both
      const sharedChildren = maleChildren.filter(mc => 
        femaleChildren.some(fc => fc.id === mc.id)
      );
      
      // Need at least 2 shared children
      if (sharedChildren.length < 2) continue;
      
      // Check if children have FS edges between each other (full siblings)
      let allChildrenAreFs = true;
      for (let i = 0; i < sharedChildren.length && allChildrenAreFs; i++) {
        for (let j = i + 1; j < sharedChildren.length && allChildrenAreFs; j++) {
          if (!hasEdge(fsEdges, sharedChildren[i].id, sharedChildren[j].id)) {
            allChildrenAreFs = false;
          }
        }
      }
      
      if (!allChildrenAreFs) continue;
      
      // Avoid duplicate pairs
      const pairKey = [male.id, female.id].sort().join('-');
      if (seenPairs.has(pairKey)) continue;
      seenPairs.add(pairKey);
      
      families.push({
        father: male,
        mother: female,
        children: sharedChildren
      });
    }
  }
  
  return families;
}

/**
 * Cluster children by FS (Full Sibling) edges using union-find.
 * Children with FS edges between them end up in the same cluster.
 */
function clusterByFsEdges(
  children: TestedIndividual[], 
  fsEdges: Map<string, Set<string>>
): TestedIndividual[][] {
  if (children.length === 0) return [];
  if (children.length === 1) return [[children[0]]];
  
  // Initialize union-find
  const parent = new Map<string, string>();
  children.forEach(c => parent.set(c.id, c.id));
  
  const find = (id: string): string => {
    if (parent.get(id) !== id) {
      parent.set(id, find(parent.get(id)!));
    }
    return parent.get(id)!;
  };
  
  const union = (id1: string, id2: string) => {
    const root1 = find(id1);
    const root2 = find(id2);
    if (root1 !== root2) {
      parent.set(root1, root2);
    }
  };
  
  // Union children that have FS edges
  for (let i = 0; i < children.length; i++) {
    for (let j = i + 1; j < children.length; j++) {
      if (hasEdge(fsEdges, children[i].id, children[j].id)) {
        union(children[i].id, children[j].id);
      }
    }
  }
  
  // Group by root
  const clusters = new Map<string, TestedIndividual[]>();
  for (const child of children) {
    const root = find(child.id);
    if (!clusters.has(root)) {
      clusters.set(root, []);
    }
    clusters.get(root)!.push(child);
  }
  
  return Array.from(clusters.values());
}

/**
 * Cluster individuals as siblings, EXCLUDING pairs with strong PC evidence.
 * This is stricter than clusterByFsEdges - only clusters pairs that are 
 * "sibling-like" (FS >> PC or no PC evidence).
 * Used by flexible generators to avoid clustering parent-child as siblings.
 */
function clusterByStrictFsEdges(
  children: TestedIndividual[], 
  fsEdges: Map<string, Set<string>>,
  pcEdges: Map<string, Set<string>>
): TestedIndividual[][] {
  if (children.length === 0) return [];
  if (children.length === 1) return [[children[0]]];
  
  // Initialize union-find
  const parent = new Map<string, string>();
  children.forEach(c => parent.set(c.id, c.id));
  
  const find = (id: string): string => {
    if (parent.get(id) !== id) {
      parent.set(id, find(parent.get(id)!));
    }
    return parent.get(id)!;
  };
  
  const union = (id1: string, id2: string) => {
    const root1 = find(id1);
    const root2 = find(id2);
    if (root1 !== root2) {
      parent.set(root1, root2);
    }
  };
  
  // Union children that have FS edges BUT NOT PC edges
  // This excludes parent-child pairs from being clustered as siblings
  for (let i = 0; i < children.length; i++) {
    for (let j = i + 1; j < children.length; j++) {
      const hasFs = hasEdge(fsEdges, children[i].id, children[j].id);
      const hasPc = hasEdge(pcEdges, children[i].id, children[j].id);
      
      // Only cluster if FS evidence exists AND no PC evidence
      // This prevents parent-child from being treated as siblings
      if (hasFs && !hasPc) {
        union(children[i].id, children[j].id);
      }
    }
  }
  
  // Group by root
  const clusters = new Map<string, TestedIndividual[]>();
  for (const child of children) {
    const root = find(child.id);
    if (!clusters.has(root)) {
      clusters.set(root, []);
    }
    clusters.get(root)!.push(child);
  }
  
  return Array.from(clusters.values());
}

/**
 * Find ALL valid non-overlapping PC pair combinations for disjointed permutations.
 * Returns all pairs of PC edges where the 4 individuals are distinct.
 */
function findAllNonOverlappingPCPairs(graph: KinshipGraph): Array<{
  pair1: { parent: TestedIndividual; child: TestedIndividual };
  pair2: { parent: TestedIndividual; child: TestedIndividual };
}> {
  const { individuals, pcEdges } = graph;
  const results: Array<{
    pair1: { parent: TestedIndividual; child: TestedIndividual };
    pair2: { parent: TestedIndividual; child: TestedIndividual };
  }> = [];
  
  // Collect all PC edges as pairs
  const allPCPairs: Array<[TestedIndividual, TestedIndividual]> = [];
  for (const ind1 of individuals) {
    for (const ind2 of individuals) {
      if (ind1.id !== ind2.id && hasEdge(pcEdges, ind1.id, ind2.id)) {
        // Only add once per pair (canonical order by ID)
        if (ind1.id < ind2.id) {
          allPCPairs.push([ind1, ind2]);
        }
      }
    }
  }
  
  // Find all non-overlapping pairs of PC edges
  for (let i = 0; i < allPCPairs.length; i++) {
    for (let j = i + 1; j < allPCPairs.length; j++) {
      const [a1, a2] = allPCPairs[i];
      const [b1, b2] = allPCPairs[j];
      
      // Check no overlap in individuals
      const ids = new Set([a1.id, a2.id, b1.id, b2.id]);
      if (ids.size === 4) {
        // Generate both directional orientations for each pair
        // Forward: first individual is parent for each
        results.push({
          pair1: { parent: a1, child: a2 },
          pair2: { parent: b1, child: b2 }
        });
      }
    }
  }
  
  return results;
}

// ============================================================================
// GRAPH-DRIVEN TEMPLATE GENERATORS
// ============================================================================

/**
 * Generate canonical Parent/Child permutations.
 * For 4 individuals (2M, 2F), generates exactly:
 * - 1 Standard Trio: Parent pair with most PC edges as parents
 * - 1 Reverse Trio: The other pair as parents
 * - 1 Disjointed: Same-gender PC pairs
 * - 1 Disjointed Reverse: Reversed same-gender PC pairs
 */
function generateParentChildPermutations(graph: KinshipGraph): Permutation[] {
  const permutations: Permutation[] = [];
  const assignment = findParentChildAssignment(graph);
  
  if (!assignment) return permutations;
  
  const { father, mother, childM, childF } = assignment;
  const children = [childM, childF];
  
  // Standard Trio
  {
    const prefix = 'trio_';
    const fatherId = createNodeId(father.name, prefix);
    const motherId = createNodeId(mother.name, prefix);
    
    const nodes: PedigreeNode[] = [
      {
        id: fatherId,
        gender: 'male',
        parents: [],
        children: children.map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' })),
        spouses: [{ id: motherId, type: 'married' }],
        siblings: [],
        data: { name: father.name, label: 'Father', isIndex: true }
      },
      {
        id: motherId,
        gender: 'female',
        parents: [],
        children: children.map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' })),
        spouses: [{ id: fatherId, type: 'married' }],
        siblings: [],
        data: { name: mother.name, label: 'Mother', isIndex: true }
      }
    ];
    
    children.forEach((child, idx) => {
      const siblingIds = children
        .filter(c => c.id !== child.id)
        .map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' }));
      
      nodes.push({
        id: createNodeId(child.name, prefix),
        gender: genderToNodeGender(child.gender),
        parents: [{ id: fatherId, type: 'blood' }, { id: motherId, type: 'blood' }],
        children: [],
        spouses: [],
        siblings: siblingIds,
        data: { name: child.name, label: `Child ${idx + 1}`, isIndex: true }
      });
    });
    
    const fInit = father.name.split(' ')[0][0];
    const mInit = mother.name.split(' ')[0][0];
    
    permutations.push({
      id: 0,
      nodes,
      rootId: fatherId,
      label: `Standard Trio (${fInit}&${mInit} = Parents)`,
      type: 'Trio',
      category: 'parentChild',
      details: `${father.name} & ${mother.name} are parents`
    });
  }
  
  // Reverse Trio: children become parents
  {
    const prefix = 'rtrio_';
    const revFather = childM;
    const revMother = childF;
    const revChildren = [father, mother];
    const fatherId = createNodeId(revFather.name, prefix);
    const motherId = createNodeId(revMother.name, prefix);
    
    const nodes: PedigreeNode[] = [
      {
        id: fatherId,
        gender: 'male',
        parents: [],
        children: revChildren.map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' })),
        spouses: [{ id: motherId, type: 'married' }],
        siblings: [],
        data: { name: revFather.name, label: 'Father', isIndex: true }
      },
      {
        id: motherId,
        gender: 'female',
        parents: [],
        children: revChildren.map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' })),
        spouses: [{ id: fatherId, type: 'married' }],
        siblings: [],
        data: { name: revMother.name, label: 'Mother', isIndex: true }
      }
    ];
    
    revChildren.forEach((child, idx) => {
      const siblingIds = revChildren
        .filter(c => c.id !== child.id)
        .map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' }));
      
      nodes.push({
        id: createNodeId(child.name, prefix),
        gender: genderToNodeGender(child.gender),
        parents: [{ id: fatherId, type: 'blood' }, { id: motherId, type: 'blood' }],
        children: [],
        spouses: [],
        siblings: siblingIds,
        data: { name: child.name, label: `Child ${idx + 1}`, isIndex: true }
      });
    });
    
    const fInit = revFather.name.split(' ')[0][0];
    const mInit = revMother.name.split(' ')[0][0];
    
    permutations.push({
      id: 0,
      nodes,
      rootId: fatherId,
      label: `Reverse Trio (${fInit}&${mInit} = Parents)`,
      type: 'Trio (Rev)',
      category: 'parentChild',
      details: `${revFather.name} & ${revMother.name} are parents (reverse)`
    });
  }
  
  // Disjointed: Generate ALL valid non-overlapping PC pair combinations
  const createDisjointedPair = (parent: TestedIndividual, child: TestedIndividual, prefix: string): PedigreeNode[] => {
    const parentId = createNodeId(parent.name, prefix);
    const spouseId = prefix + 'spouse';
    const childId = createNodeId(child.name, prefix);
    
    return [
      {
        id: parentId,
        gender: genderToNodeGender(parent.gender),
        parents: [],
        children: [{ id: childId, type: 'blood' }],
        spouses: [{ id: spouseId, type: 'married' }],
        siblings: [],
        data: { name: parent.name, label: 'Parent', isIndex: true }
      },
      {
        id: spouseId,
        gender: parent.gender === 'M' ? 'female' : 'male',
        parents: [],
        children: [{ id: childId, type: 'blood' }],
        spouses: [{ id: parentId, type: 'married' }],
        siblings: [],
        data: { name: 'Untested', isUntested: true }
      },
      {
        id: childId,
        gender: genderToNodeGender(child.gender),
        parents: parent.gender === 'M' ? 
          [{ id: parentId, type: 'blood' }, { id: spouseId, type: 'blood' }] :
          [{ id: spouseId, type: 'blood' }, { id: parentId, type: 'blood' }],
        children: [],
        spouses: [],
        siblings: [],
        data: { name: child.name, label: 'Child', isIndex: true }
      }
    ];
  };
  
  // Find ALL valid non-overlapping PC pair combinations
  const nonOverlappingPairs = findAllNonOverlappingPCPairs(graph);
  const addedDisjointedLabels = new Set<string>();
  
  nonOverlappingPairs.forEach((combo, idx) => {
    const { pair1, pair2 } = combo;
    
    // Generate Forward direction
    const p1Init = pair1.parent.name.split(' ')[0];
    const c1Init = pair1.child.name.split(' ')[0];
    const p2Init = pair2.parent.name.split(' ')[0];
    const c2Init = pair2.child.name.split(' ')[0];
    
    // Create canonical label for deduplication
    const fwdPairs = [`${p1Init}-${c1Init}`, `${p2Init}-${c2Init}`].sort().join('|');
    const revPairs = [`${c1Init}-${p1Init}`, `${c2Init}-${p2Init}`].sort().join('|');
    
    if (!addedDisjointedLabels.has(fwdPairs)) {
      addedDisjointedLabels.add(fwdPairs);
      
      const prefixA = `dj${idx}_a_`;
      const prefixB = `dj${idx}_b_`;
      const nodesA = createDisjointedPair(pair1.parent, pair1.child, prefixA);
      const nodesB = createDisjointedPair(pair2.parent, pair2.child, prefixB);
      
      permutations.push({
        id: 0,
        nodes: nodesA,
        nodesB: nodesB,
        rootId: createNodeId(pair1.parent.name, prefixA),
        rootIdB: createNodeId(pair2.parent.name, prefixB),
        label: `Disjointed: ${p1Init}/${c1Init} & ${p2Init}/${c2Init}`,
        type: 'Disjointed',
        category: 'parentChild',
        details: `${pair1.parent.name}/${pair1.child.name} and ${pair2.parent.name}/${pair2.child.name}`
      });
    }
    
    // Generate Reverse direction
    if (!addedDisjointedLabels.has(revPairs)) {
      addedDisjointedLabels.add(revPairs);
      
      const prefixA = `djr${idx}_a_`;
      const prefixB = `djr${idx}_b_`;
      const nodesA = createDisjointedPair(pair1.child, pair1.parent, prefixA);
      const nodesB = createDisjointedPair(pair2.child, pair2.parent, prefixB);
      
      permutations.push({
        id: 0,
        nodes: nodesA,
        nodesB: nodesB,
        rootId: createNodeId(pair1.child.name, prefixA),
        rootIdB: createNodeId(pair2.child.name, prefixB),
        label: `Disjointed (Rev): ${c1Init}/${p1Init} & ${c2Init}/${p2Init}`,
        type: 'Disjointed',
        category: 'parentChild',
        details: `${pair1.child.name}/${pair1.parent.name} and ${pair2.child.name}/${pair2.parent.name}`
      });
    }
  });
  
  return permutations;
}

/**
 * Generate canonical Full Sibling permutations.
 * - 1 Connected: The children from trio are siblings
 * - 1 Reverse: The parents from trio become siblings
 * - 3 Disjointed partitions
 */
function generateFullSiblingPermutations(graph: KinshipGraph): Permutation[] {
  const permutations: Permutation[] = [];
  const assignment = findParentChildAssignment(graph);
  
  if (!assignment) return permutations;
  
  const { father, mother, childM, childF } = assignment;
  const { individuals, fsEdges } = graph;
  
  // Full Siblings (Standard): childM & childF are siblings, parents are father & mother
  if (hasEdge(fsEdges, childM.id, childF.id)) {
    const prefix = 'fs_';
    const siblings = [childM, childF];
    const fatherId = createNodeId(father.name, prefix);
    const motherId = createNodeId(mother.name, prefix);
    
    const nodes: PedigreeNode[] = [
      {
        id: fatherId,
        gender: 'male',
        parents: [],
        children: siblings.map(s => ({ id: createNodeId(s.name, prefix), type: 'blood' })),
        spouses: [{ id: motherId, type: 'married' }],
        siblings: [],
        data: { name: father.name, label: 'Father', isIndex: true }
      },
      {
        id: motherId,
        gender: 'female',
        parents: [],
        children: siblings.map(s => ({ id: createNodeId(s.name, prefix), type: 'blood' })),
        spouses: [{ id: fatherId, type: 'married' }],
        siblings: [],
        data: { name: mother.name, label: 'Mother', isIndex: true }
      }
    ];
    
    siblings.forEach((sib, idx) => {
      const siblingRefs = siblings
        .filter(s => s.id !== sib.id)
        .map(s => ({ id: createNodeId(s.name, prefix), type: 'blood' }));
      
      nodes.push({
        id: createNodeId(sib.name, prefix),
        gender: genderToNodeGender(sib.gender),
        parents: [{ id: fatherId, type: 'blood' }, { id: motherId, type: 'blood' }],
        children: [],
        spouses: [],
        siblings: siblingRefs,
        data: { name: sib.name, label: `Full Sibling ${idx + 1}`, isIndex: true }
      });
    });
    
    const s1Name = childM.name.split(' ')[0];
    const s2Name = childF.name.split(' ')[0];
    
    permutations.push({
      id: 0,
      nodes,
      rootId: fatherId,
      label: `Full Siblings (${s1Name} & ${s2Name})`,
      type: 'Full-Siblings',
      category: 'fullSibling',
      details: `${siblings.map(s => s.name).join(' & ')} are full siblings`
    });
  }
  
  // Full Siblings (Reverse): father & mother as siblings, childM & childF as parents
  {
    const prefix = 'fsr_';
    const reverseSiblings = [father, mother];
    const revFatherId = createNodeId(childM.name, prefix);
    const revMotherId = createNodeId(childF.name, prefix);
    
    const nodes: PedigreeNode[] = [
      {
        id: revFatherId,
        gender: 'male',
        parents: [],
        children: reverseSiblings.map(s => ({ id: createNodeId(s.name, prefix), type: 'blood' })),
        spouses: [{ id: revMotherId, type: 'married' }],
        siblings: [],
        data: { name: childM.name, label: 'Father', isIndex: true }
      },
      {
        id: revMotherId,
        gender: 'female',
        parents: [],
        children: reverseSiblings.map(s => ({ id: createNodeId(s.name, prefix), type: 'blood' })),
        spouses: [{ id: revFatherId, type: 'married' }],
        siblings: [],
        data: { name: childF.name, label: 'Mother', isIndex: true }
      }
    ];
    
    reverseSiblings.forEach((sib, idx) => {
      const siblingRefs = reverseSiblings
        .filter(s => s.id !== sib.id)
        .map(s => ({ id: createNodeId(s.name, prefix), type: 'blood' }));
      
      nodes.push({
        id: createNodeId(sib.name, prefix),
        gender: genderToNodeGender(sib.gender),
        parents: [{ id: revFatherId, type: 'blood' }, { id: revMotherId, type: 'blood' }],
        children: [],
        spouses: [],
        siblings: siblingRefs,
        data: { name: sib.name, label: `Full Sibling ${idx + 1}`, isIndex: true }
      });
    });
    
    const s1Name = father.name.split(' ')[0];
    const s2Name = mother.name.split(' ')[0];
    
    permutations.push({
      id: 0,
      nodes,
      rootId: revFatherId,
      label: `Full Siblings (${s1Name} & ${s2Name}) - Reverse`,
      type: 'Full-Siblings',
      category: 'fullSibling',
      details: `${reverseSiblings.map(s => s.name).join(' & ')} are full siblings (reverse)`
    });
  }
  
  // Disjointed Full Sibling Partitions: Only generate partitions where BOTH pairs have FS support
  // Rule 12: Never assert sibling relationships without LR support
  const allFour = [...individuals].sort((a, b) => a.name.localeCompare(b.name));
  
  // Generate all possible 2-2 partitions (there are exactly 3 for 4 individuals)
  const allPartitions: [TestedIndividual[], TestedIndividual[]][] = [
    [[allFour[0], allFour[1]], [allFour[2], allFour[3]]], // Partition 1: [0,1] and [2,3]
    [[allFour[0], allFour[2]], [allFour[1], allFour[3]]], // Partition 2: [0,2] and [1,3]
    [[allFour[0], allFour[3]], [allFour[1], allFour[2]]]  // Partition 3: [0,3] and [1,2]
  ];
  
  // Filter to only partitions where BOTH pairs have FS LR > 1 support
  const validPartitions = allPartitions.filter(([pair1, pair2]) => {
    const pair1HasFs = hasEdge(fsEdges, pair1[0].id, pair1[1].id);
    const pair2HasFs = hasEdge(fsEdges, pair2[0].id, pair2[1].id);
    return pair1HasFs && pair2HasFs;
  });
  
  validPartitions.forEach((partition, idx) => {
    const [pair1, pair2] = partition;
    const prefix1 = `fsd${idx}_a_`;
    const prefix2 = `fsd${idx}_b_`;
    
    // Family A: First pair with untested parents
    const untestedFatherAId = prefix1 + 'untested_father';
    const untestedMotherAId = prefix1 + 'untested_mother';
    
    const nodesA: PedigreeNode[] = [
      {
        id: untestedFatherAId,
        gender: 'male',
        parents: [],
        children: pair1.map(s => ({ id: createNodeId(s.name, prefix1), type: 'blood' })),
        spouses: [{ id: untestedMotherAId, type: 'married' }],
        siblings: [],
        data: { name: 'Untested Father', isUntested: true }
      },
      {
        id: untestedMotherAId,
        gender: 'female',
        parents: [],
        children: pair1.map(s => ({ id: createNodeId(s.name, prefix1), type: 'blood' })),
        spouses: [{ id: untestedFatherAId, type: 'married' }],
        siblings: [],
        data: { name: 'Untested Mother', isUntested: true }
      }
    ];
    
    pair1.forEach((sib, sibIdx) => {
      const siblingRefs = pair1
        .filter(s => s.id !== sib.id)
        .map(s => ({ id: createNodeId(s.name, prefix1), type: 'blood' }));
      
      nodesA.push({
        id: createNodeId(sib.name, prefix1),
        gender: genderToNodeGender(sib.gender),
        parents: [{ id: untestedFatherAId, type: 'blood' }, { id: untestedMotherAId, type: 'blood' }],
        children: [],
        spouses: [],
        siblings: siblingRefs,
        data: { name: sib.name, label: `Sibling ${sibIdx + 1}`, isIndex: true }
      });
    });
    
    // Family B: Second pair with untested parents
    const untestedFatherBId = prefix2 + 'untested_father';
    const untestedMotherBId = prefix2 + 'untested_mother';
    
    const nodesB: PedigreeNode[] = [
      {
        id: untestedFatherBId,
        gender: 'male',
        parents: [],
        children: pair2.map(s => ({ id: createNodeId(s.name, prefix2), type: 'blood' })),
        spouses: [{ id: untestedMotherBId, type: 'married' }],
        siblings: [],
        data: { name: 'Untested Father', isUntested: true }
      },
      {
        id: untestedMotherBId,
        gender: 'female',
        parents: [],
        children: pair2.map(s => ({ id: createNodeId(s.name, prefix2), type: 'blood' })),
        spouses: [{ id: untestedFatherBId, type: 'married' }],
        siblings: [],
        data: { name: 'Untested Mother', isUntested: true }
      }
    ];
    
    pair2.forEach((sib, sibIdx) => {
      const siblingRefs = pair2
        .filter(s => s.id !== sib.id)
        .map(s => ({ id: createNodeId(s.name, prefix2), type: 'blood' }));
      
      nodesB.push({
        id: createNodeId(sib.name, prefix2),
        gender: genderToNodeGender(sib.gender),
        parents: [{ id: untestedFatherBId, type: 'blood' }, { id: untestedMotherBId, type: 'blood' }],
        children: [],
        spouses: [],
        siblings: siblingRefs,
        data: { name: sib.name, label: `Sibling ${sibIdx + 1}`, isIndex: true }
      });
    });
    
    const pair1Names = pair1.map(p => p.name.split(' ')[0]).join(' & ');
    const pair2Names = pair2.map(p => p.name.split(' ')[0]).join(' & ');
    
    permutations.push({
      id: 0,
      nodes: nodesA,
      nodesB: nodesB,
      rootId: untestedFatherAId,
      rootIdB: untestedFatherBId,
      label: `Disjointed Full Sibs: ${pair1Names} | ${pair2Names}`,
      type: 'Disjointed',
      category: 'fullSibling',
      details: `${pair1.map(p => p.name).join(' & ')} | ${pair2.map(p => p.name).join(' & ')}`
    });
  });
  
  return permutations;
}

/**
 * Generate canonical Extended Family permutations.
 * - Multiple Linear (all valid 3-generation chains)
 * - 2 Avuncular (forward and reverse)
 * - 2 Grandparent (forward and reverse)
 * - 4 Half-Siblings (2 forward, 2 reverse)
 */
function generateExtendedPermutations(graph: KinshipGraph): Permutation[] {
  const permutations: Permutation[] = [];
  const assignment = findParentChildAssignment(graph);
  
  if (!assignment) return permutations;
  
  const { father, mother, childM, childF } = assignment;
  const { individuals, pcEdges, fsEdges, extEdges } = graph;
  
  // Alias for clarity based on test expectations
  // Standard: Shane(father) & Crystal(mother) = parents, Calab(childM) & Abigail(childF) = children
  const parentM = father;
  const parentF = mother;
  
  // 1. Linear: Find ALL valid 3-generation chains
  // A valid chain needs: GP -> Parent (PC), Parent -> Grandchild (PC), Spouse -> Grandchild (PC)
  // This ensures all 4 tested individuals are included in the pedigree
  const addedLinearChains = new Set<string>();
  
  // Try all possible grandparent-parent-grandchild combinations
  for (const grandparent of individuals) {
    for (const parent of individuals) {
      if (parent.id === grandparent.id) continue;
      
      // Check GP -> Parent PC edge
      if (!hasEdge(pcEdges, grandparent.id, parent.id)) continue;
      
      for (const grandchild of individuals) {
        if (grandchild.id === grandparent.id || grandchild.id === parent.id) continue;
        
        // Check Parent -> Grandchild PC edge
        if (!hasEdge(pcEdges, parent.id, grandchild.id)) continue;
        
        // Find the spouse (the 4th tested individual)
        const spouse = individuals.find(i => 
          i.id !== grandparent.id && 
          i.id !== parent.id && 
          i.id !== grandchild.id
        );
        
        if (!spouse) continue;
        
        // Check Spouse -> Grandchild PC edge (ensures spouse is co-parent)
        if (!hasEdge(pcEdges, spouse.id, grandchild.id)) continue;
        
        // Rule 5: Biological constraints - parent and spouse must have opposite genders
        // Same-sex parent pairs are biologically invalid for genetic kinship
        if (parent.gender === spouse.gender) continue;
        
        // Create canonical chain ID for deduplication
        const chainId = `${grandparent.id}-${parent.id}-${grandchild.id}`;
        if (addedLinearChains.has(chainId)) continue;
        addedLinearChains.add(chainId);
        
        const prefix = `lin_${addedLinearChains.size}_`;
        const gpLabel = grandparent.gender === 'M' ? 'Grandfather' : 'Grandmother';
        const parentLabel = parent.gender === 'M' ? 'Father' : 'Mother';
        const spouseLabel = spouse.gender === 'M' ? 'Father' : 'Mother';
        
        const nodes: PedigreeNode[] = [
          {
            id: createNodeId(grandparent.name, prefix),
            gender: genderToNodeGender(grandparent.gender),
            parents: [],
            children: [{ id: createNodeId(parent.name, prefix), type: 'blood' }],
            spouses: [{ id: prefix + 'untested_gp', type: 'married' }],
            siblings: [],
            data: { name: grandparent.name, label: gpLabel, isIndex: true }
          },
          {
            id: prefix + 'untested_gp',
            gender: grandparent.gender === 'M' ? 'female' : 'male',
            parents: [],
            children: [{ id: createNodeId(parent.name, prefix), type: 'blood' }],
            spouses: [{ id: createNodeId(grandparent.name, prefix), type: 'married' }],
            siblings: [],
            data: { name: 'Untested', isUntested: true }
          },
          {
            id: createNodeId(parent.name, prefix),
            gender: genderToNodeGender(parent.gender),
            parents: [
              { id: grandparent.gender === 'M' ? createNodeId(grandparent.name, prefix) : prefix + 'untested_gp', type: 'blood' },
              { id: grandparent.gender === 'F' ? createNodeId(grandparent.name, prefix) : prefix + 'untested_gp', type: 'blood' }
            ],
            children: [{ id: createNodeId(grandchild.name, prefix), type: 'blood' }],
            spouses: [{ id: createNodeId(spouse.name, prefix), type: 'married' }],
            siblings: [],
            data: { name: parent.name, label: parentLabel, isIndex: true }
          },
          {
            id: createNodeId(spouse.name, prefix),
            gender: genderToNodeGender(spouse.gender),
            parents: [],
            children: [{ id: createNodeId(grandchild.name, prefix), type: 'blood' }],
            spouses: [{ id: createNodeId(parent.name, prefix), type: 'married' }],
            siblings: [],
            data: { name: spouse.name, label: spouseLabel, isIndex: true }
          },
          {
            id: createNodeId(grandchild.name, prefix),
            gender: genderToNodeGender(grandchild.gender),
            parents: [
              { id: parent.gender === 'M' ? createNodeId(parent.name, prefix) : createNodeId(spouse.name, prefix), type: 'blood' },
              { id: parent.gender === 'F' ? createNodeId(parent.name, prefix) : createNodeId(spouse.name, prefix), type: 'blood' }
            ],
            children: [],
            spouses: [],
            siblings: [],
            data: { name: grandchild.name, label: 'Grandchild', isIndex: true }
          }
        ];
        
        const gpInit = grandparent.name.split(' ')[0];
        const parentInit = parent.name.split(' ')[0];
        const gcInit = grandchild.name.split(' ')[0];
        
        permutations.push({
          id: 0,
          nodes,
          rootId: createNodeId(grandparent.name, prefix),
          label: `Linear: ${gpInit} -> ${parentInit} -> ${gcInit}`,
          type: 'Linear',
          category: 'extended',
          details: `${grandparent.name} is ${gpLabel.toLowerCase()} of ${grandchild.name}`
        });
      }
    }
  }
  
  // 2. Avuncular: parentM is Uncle (sibling of UNTESTED SPOUSE, who is co-parent with parentF)
  // Correct biological structure:
  // - parentM (Uncle/Shane) is sibling of Untested Spouse (both children of untested grandparents)
  // - Untested Spouse + parentF (Crystal) are parents of the children
  // - Uncle is uncle through his sibling (Untested Spouse), NOT through parentF
  // Valid when parentF has PC edges to both children (she's their biological parent)
  // INVALID when parentM (the uncle) has PC edges to children - this demotes 1st degree to 2nd degree
  const parentFIsValidParent = hasEdge(pcEdges, parentF.id, childM.id) && hasEdge(pcEdges, parentF.id, childF.id);
  const parentMHasPCToChildren = hasEdge(pcEdges, parentM.id, childM.id) || hasEdge(pcEdges, parentM.id, childF.id);
  if (parentFIsValidParent && !parentMHasPCToChildren) {
    const prefix = 'av_';
    
    const nodes: PedigreeNode[] = [
      // Untested grandparents (parents of Uncle and Untested Spouse)
      {
        id: prefix + 'gp1',
        gender: 'male',
        parents: [],
        children: [{ id: createNodeId(parentM.name, prefix), type: 'blood' }, { id: prefix + 'spouse', type: 'blood' }],
        spouses: [{ id: prefix + 'gp2', type: 'married' }],
        siblings: [],
        data: { name: 'Untested GF', isUntested: true }
      },
      {
        id: prefix + 'gp2',
        gender: 'female',
        parents: [],
        children: [{ id: createNodeId(parentM.name, prefix), type: 'blood' }, { id: prefix + 'spouse', type: 'blood' }],
        spouses: [{ id: prefix + 'gp1', type: 'married' }],
        siblings: [],
        data: { name: 'Untested GM', isUntested: true }
      },
      // Uncle (tested) - sibling of Untested Spouse
      {
        id: createNodeId(parentM.name, prefix),
        gender: 'male',
        parents: [{ id: prefix + 'gp1', type: 'blood' }, { id: prefix + 'gp2', type: 'blood' }],
        children: [],
        spouses: [],
        siblings: [{ id: prefix + 'spouse', type: 'blood' }],
        data: { name: parentM.name, label: 'Uncle', isIndex: true }
      },
      // Untested Spouse - sibling of Uncle, married to parentF
      {
        id: prefix + 'spouse',
        gender: 'male',
        parents: [{ id: prefix + 'gp1', type: 'blood' }, { id: prefix + 'gp2', type: 'blood' }],
        children: [{ id: createNodeId(childM.name, prefix), type: 'blood' }, { id: createNodeId(childF.name, prefix), type: 'blood' }],
        spouses: [{ id: createNodeId(parentF.name, prefix), type: 'married' }],
        siblings: [{ id: createNodeId(parentM.name, prefix), type: 'blood' }],
        data: { name: 'Untested Spouse', isUntested: true }
      },
      // Parent (tested female) - married to Untested Spouse
      {
        id: createNodeId(parentF.name, prefix),
        gender: 'female',
        parents: [],
        children: [{ id: createNodeId(childM.name, prefix), type: 'blood' }, { id: createNodeId(childF.name, prefix), type: 'blood' }],
        spouses: [{ id: prefix + 'spouse', type: 'married' }],
        siblings: [],
        data: { name: parentF.name, label: 'Parent', isIndex: true }
      },
      // Nephew (tested)
      {
        id: createNodeId(childM.name, prefix),
        gender: 'male',
        parents: [{ id: prefix + 'spouse', type: 'blood' }, { id: createNodeId(parentF.name, prefix), type: 'blood' }],
        children: [],
        spouses: [],
        siblings: [{ id: createNodeId(childF.name, prefix), type: 'blood' }],
        data: { name: childM.name, label: 'Nephew', isIndex: true }
      },
      // Niece (tested)
      {
        id: createNodeId(childF.name, prefix),
        gender: 'female',
        parents: [{ id: prefix + 'spouse', type: 'blood' }, { id: createNodeId(parentF.name, prefix), type: 'blood' }],
        children: [],
        spouses: [],
        siblings: [{ id: createNodeId(childM.name, prefix), type: 'blood' }],
        data: { name: childF.name, label: 'Niece', isIndex: true }
      }
    ];
    
    permutations.push({
      id: 0,
      nodes,
      rootId: prefix + 'gp1',
      label: `Avuncular: ${parentM.name.split(' ')[0]} is Uncle`,
      type: 'Avuncular',
      category: 'extended',
      details: `${parentM.name} is uncle (sibling of Untested Spouse married to ${parentF.name})`
    });
  }
  
  // 3. Avuncular Reverse: childM is Uncle (sibling of UNTESTED SPOUSE, who is co-parent with childF)
  // Valid when childF has PC edges to both "nephews" (parentM & parentF)
  // For Corbett: Calab is uncle, Abigail is parent of Shane & Crystal
  // INVALID when childM (the uncle) has PC edges to nephews - this demotes 1st degree to 2nd degree
  const childFIsValidParent = hasEdge(pcEdges, childF.id, parentM.id) || hasEdge(pcEdges, childF.id, parentF.id);
  const hasFsSupportForReverse = hasEdge(extEdges, childM.id, childF.id) || hasEdge(fsEdges, childM.id, childF.id);
  const childMHasPCToNephews = hasEdge(pcEdges, childM.id, parentM.id) || hasEdge(pcEdges, childM.id, parentF.id);
  if ((childFIsValidParent || hasFsSupportForReverse) && !childMHasPCToNephews) {
    const prefix = 'avr_';
    
    const nodes: PedigreeNode[] = [
      // Untested grandparents (parents of Uncle and Untested Spouse)
      {
        id: prefix + 'gp1',
        gender: 'male',
        parents: [],
        children: [{ id: createNodeId(childM.name, prefix), type: 'blood' }, { id: prefix + 'spouse', type: 'blood' }],
        spouses: [{ id: prefix + 'gp2', type: 'married' }],
        siblings: [],
        data: { name: 'Untested GF', isUntested: true }
      },
      {
        id: prefix + 'gp2',
        gender: 'female',
        parents: [],
        children: [{ id: createNodeId(childM.name, prefix), type: 'blood' }, { id: prefix + 'spouse', type: 'blood' }],
        spouses: [{ id: prefix + 'gp1', type: 'married' }],
        siblings: [],
        data: { name: 'Untested GM', isUntested: true }
      },
      // Uncle (tested) - sibling of Untested Spouse
      {
        id: createNodeId(childM.name, prefix),
        gender: 'male',
        parents: [{ id: prefix + 'gp1', type: 'blood' }, { id: prefix + 'gp2', type: 'blood' }],
        children: [],
        spouses: [],
        siblings: [{ id: prefix + 'spouse', type: 'blood' }],
        data: { name: childM.name, label: 'Uncle', isIndex: true }
      },
      // Untested Spouse - sibling of Uncle, married to childF
      {
        id: prefix + 'spouse',
        gender: 'male',
        parents: [{ id: prefix + 'gp1', type: 'blood' }, { id: prefix + 'gp2', type: 'blood' }],
        children: [{ id: createNodeId(parentM.name, prefix), type: 'blood' }, { id: createNodeId(parentF.name, prefix), type: 'blood' }],
        spouses: [{ id: createNodeId(childF.name, prefix), type: 'married' }],
        siblings: [{ id: createNodeId(childM.name, prefix), type: 'blood' }],
        data: { name: 'Untested Spouse', isUntested: true }
      },
      // Parent (tested female) - married to Untested Spouse
      {
        id: createNodeId(childF.name, prefix),
        gender: 'female',
        parents: [],
        children: [{ id: createNodeId(parentM.name, prefix), type: 'blood' }, { id: createNodeId(parentF.name, prefix), type: 'blood' }],
        spouses: [{ id: prefix + 'spouse', type: 'married' }],
        siblings: [],
        data: { name: childF.name, label: 'Parent', isIndex: true }
      },
      // Nephew (tested)
      {
        id: createNodeId(parentM.name, prefix),
        gender: 'male',
        parents: [{ id: prefix + 'spouse', type: 'blood' }, { id: createNodeId(childF.name, prefix), type: 'blood' }],
        children: [],
        spouses: [],
        siblings: [{ id: createNodeId(parentF.name, prefix), type: 'blood' }],
        data: { name: parentM.name, label: 'Nephew', isIndex: true }
      },
      // Niece (tested)
      {
        id: createNodeId(parentF.name, prefix),
        gender: 'female',
        parents: [{ id: prefix + 'spouse', type: 'blood' }, { id: createNodeId(childF.name, prefix), type: 'blood' }],
        children: [],
        spouses: [],
        siblings: [{ id: createNodeId(parentM.name, prefix), type: 'blood' }],
        data: { name: parentF.name, label: 'Niece', isIndex: true }
      }
    ];
    
    permutations.push({
      id: 0,
      nodes,
      rootId: prefix + 'gp1',
      label: `Avuncular (Reverse): ${childM.name.split(' ')[0]} is Uncle`,
      type: 'Avuncular',
      category: 'extended',
      details: `${childM.name} is uncle (sibling of Untested Spouse married to ${childF.name})`
    });
  }
  
  // 4. Avuncular Aunt Forward: parentF is Aunt (sibling of UNTESTED SPOUSE, who is co-parent with parentM)
  // Same structure as uncle but with female as the aunt
  // Valid when parentM has PC edges to both children (he's their biological parent)
  // INVALID when parentF (the aunt) has PC edges to children - this demotes 1st degree to 2nd degree
  const parentMIsValidParent = hasEdge(pcEdges, parentM.id, childM.id) && hasEdge(pcEdges, parentM.id, childF.id);
  const parentFHasPCToChildren = hasEdge(pcEdges, parentF.id, childM.id) || hasEdge(pcEdges, parentF.id, childF.id);
  if (parentMIsValidParent && !parentFHasPCToChildren) {
    const prefix = 'ava_';
    
    const nodes: PedigreeNode[] = [
      // Untested grandparents (parents of Aunt and Untested Spouse)
      {
        id: prefix + 'gp1',
        gender: 'male',
        parents: [],
        children: [{ id: createNodeId(parentF.name, prefix), type: 'blood' }, { id: prefix + 'spouse', type: 'blood' }],
        spouses: [{ id: prefix + 'gp2', type: 'married' }],
        siblings: [],
        data: { name: 'Untested GF', isUntested: true }
      },
      {
        id: prefix + 'gp2',
        gender: 'female',
        parents: [],
        children: [{ id: createNodeId(parentF.name, prefix), type: 'blood' }, { id: prefix + 'spouse', type: 'blood' }],
        spouses: [{ id: prefix + 'gp1', type: 'married' }],
        siblings: [],
        data: { name: 'Untested GM', isUntested: true }
      },
      // Aunt (tested female) - sibling of Untested Spouse
      {
        id: createNodeId(parentF.name, prefix),
        gender: 'female',
        parents: [{ id: prefix + 'gp1', type: 'blood' }, { id: prefix + 'gp2', type: 'blood' }],
        children: [],
        spouses: [],
        siblings: [{ id: prefix + 'spouse', type: 'blood' }],
        data: { name: parentF.name, label: 'Aunt', isIndex: true }
      },
      // Untested Spouse (female) - sibling of Aunt, married to parentM
      {
        id: prefix + 'spouse',
        gender: 'female',
        parents: [{ id: prefix + 'gp1', type: 'blood' }, { id: prefix + 'gp2', type: 'blood' }],
        children: [{ id: createNodeId(childM.name, prefix), type: 'blood' }, { id: createNodeId(childF.name, prefix), type: 'blood' }],
        spouses: [{ id: createNodeId(parentM.name, prefix), type: 'married' }],
        siblings: [{ id: createNodeId(parentF.name, prefix), type: 'blood' }],
        data: { name: 'Untested Spouse', isUntested: true }
      },
      // Parent (tested male) - married to Untested Spouse
      {
        id: createNodeId(parentM.name, prefix),
        gender: 'male',
        parents: [],
        children: [{ id: createNodeId(childM.name, prefix), type: 'blood' }, { id: createNodeId(childF.name, prefix), type: 'blood' }],
        spouses: [{ id: prefix + 'spouse', type: 'married' }],
        siblings: [],
        data: { name: parentM.name, label: 'Parent', isIndex: true }
      },
      // Nephew (tested)
      {
        id: createNodeId(childM.name, prefix),
        gender: 'male',
        parents: [{ id: createNodeId(parentM.name, prefix), type: 'blood' }, { id: prefix + 'spouse', type: 'blood' }],
        children: [],
        spouses: [],
        siblings: [{ id: createNodeId(childF.name, prefix), type: 'blood' }],
        data: { name: childM.name, label: 'Nephew', isIndex: true }
      },
      // Niece (tested)
      {
        id: createNodeId(childF.name, prefix),
        gender: 'female',
        parents: [{ id: createNodeId(parentM.name, prefix), type: 'blood' }, { id: prefix + 'spouse', type: 'blood' }],
        children: [],
        spouses: [],
        siblings: [{ id: createNodeId(childM.name, prefix), type: 'blood' }],
        data: { name: childF.name, label: 'Niece', isIndex: true }
      }
    ];
    
    permutations.push({
      id: 0,
      nodes,
      rootId: prefix + 'gp1',
      label: `Avuncular: ${parentF.name.split(' ')[0]} is Aunt`,
      type: 'Avuncular',
      category: 'extended',
      details: `${parentF.name} is aunt (sibling of Untested Spouse married to ${parentM.name})`
    });
  }
  
  // 5. Avuncular Aunt Reverse: childF is Aunt (sibling of UNTESTED SPOUSE, who is co-parent with childM)
  // Valid when childM has PC edges to both "nephews" (parentM & parentF)
  // INVALID when childF (the aunt) has PC edges to nephews - this demotes 1st degree to 2nd degree
  const childMIsValidParent = hasEdge(pcEdges, childM.id, parentM.id) || hasEdge(pcEdges, childM.id, parentF.id);
  const hasFsSupportForAuntReverse = hasEdge(extEdges, childM.id, childF.id) || hasEdge(fsEdges, childM.id, childF.id);
  const childFHasPCToNephews = hasEdge(pcEdges, childF.id, parentM.id) || hasEdge(pcEdges, childF.id, parentF.id);
  if ((childMIsValidParent || hasFsSupportForAuntReverse) && !childFHasPCToNephews) {
    const prefix = 'avar_';
    
    const nodes: PedigreeNode[] = [
      // Untested grandparents (parents of Aunt and Untested Spouse)
      {
        id: prefix + 'gp1',
        gender: 'male',
        parents: [],
        children: [{ id: createNodeId(childF.name, prefix), type: 'blood' }, { id: prefix + 'spouse', type: 'blood' }],
        spouses: [{ id: prefix + 'gp2', type: 'married' }],
        siblings: [],
        data: { name: 'Untested GF', isUntested: true }
      },
      {
        id: prefix + 'gp2',
        gender: 'female',
        parents: [],
        children: [{ id: createNodeId(childF.name, prefix), type: 'blood' }, { id: prefix + 'spouse', type: 'blood' }],
        spouses: [{ id: prefix + 'gp1', type: 'married' }],
        siblings: [],
        data: { name: 'Untested GM', isUntested: true }
      },
      // Aunt (tested female) - sibling of Untested Spouse
      {
        id: createNodeId(childF.name, prefix),
        gender: 'female',
        parents: [{ id: prefix + 'gp1', type: 'blood' }, { id: prefix + 'gp2', type: 'blood' }],
        children: [],
        spouses: [],
        siblings: [{ id: prefix + 'spouse', type: 'blood' }],
        data: { name: childF.name, label: 'Aunt', isIndex: true }
      },
      // Untested Spouse (female) - sibling of Aunt, married to childM
      {
        id: prefix + 'spouse',
        gender: 'female',
        parents: [{ id: prefix + 'gp1', type: 'blood' }, { id: prefix + 'gp2', type: 'blood' }],
        children: [{ id: createNodeId(parentM.name, prefix), type: 'blood' }, { id: createNodeId(parentF.name, prefix), type: 'blood' }],
        spouses: [{ id: createNodeId(childM.name, prefix), type: 'married' }],
        siblings: [{ id: createNodeId(childF.name, prefix), type: 'blood' }],
        data: { name: 'Untested Spouse', isUntested: true }
      },
      // Parent (tested male) - married to Untested Spouse
      {
        id: createNodeId(childM.name, prefix),
        gender: 'male',
        parents: [],
        children: [{ id: createNodeId(parentM.name, prefix), type: 'blood' }, { id: createNodeId(parentF.name, prefix), type: 'blood' }],
        spouses: [{ id: prefix + 'spouse', type: 'married' }],
        siblings: [],
        data: { name: childM.name, label: 'Parent', isIndex: true }
      },
      // Nephew (tested)
      {
        id: createNodeId(parentM.name, prefix),
        gender: 'male',
        parents: [{ id: createNodeId(childM.name, prefix), type: 'blood' }, { id: prefix + 'spouse', type: 'blood' }],
        children: [],
        spouses: [],
        siblings: [{ id: createNodeId(parentF.name, prefix), type: 'blood' }],
        data: { name: parentM.name, label: 'Nephew', isIndex: true }
      },
      // Niece (tested)
      {
        id: createNodeId(parentF.name, prefix),
        gender: 'female',
        parents: [{ id: createNodeId(childM.name, prefix), type: 'blood' }, { id: prefix + 'spouse', type: 'blood' }],
        children: [],
        spouses: [],
        siblings: [{ id: createNodeId(parentM.name, prefix), type: 'blood' }],
        data: { name: parentF.name, label: 'Niece', isIndex: true }
      }
    ];
    
    permutations.push({
      id: 0,
      nodes,
      rootId: prefix + 'gp1',
      label: `Avuncular (Reverse): ${childF.name.split(' ')[0]} is Aunt`,
      type: 'Avuncular',
      category: 'extended',
      details: `${childF.name} is aunt (sibling of Untested Spouse married to ${childM.name})`
    });
  }
  
  // 6. Grandparent Forward: parentM & parentF are grandparents of childM & childF
  if (hasEdge(pcEdges, parentM.id, childM.id) && hasEdge(pcEdges, parentF.id, childF.id)) {
    const prefix = 'gp_';
    
    const nodes: PedigreeNode[] = [
      {
        id: createNodeId(parentM.name, prefix),
        gender: 'male',
        parents: [],
        children: [{ id: prefix + 'untested_parent', type: 'blood' }],
        spouses: [{ id: createNodeId(parentF.name, prefix), type: 'married' }],
        siblings: [],
        data: { name: parentM.name, label: 'Grandfather', isIndex: true }
      },
      {
        id: createNodeId(parentF.name, prefix),
        gender: 'female',
        parents: [],
        children: [{ id: prefix + 'untested_parent', type: 'blood' }],
        spouses: [{ id: createNodeId(parentM.name, prefix), type: 'married' }],
        siblings: [],
        data: { name: parentF.name, label: 'Grandmother', isIndex: true }
      },
      {
        id: prefix + 'untested_parent',
        gender: 'female',
        parents: [{ id: createNodeId(parentM.name, prefix), type: 'blood' }, { id: createNodeId(parentF.name, prefix), type: 'blood' }],
        children: [{ id: createNodeId(childM.name, prefix), type: 'blood' }, { id: createNodeId(childF.name, prefix), type: 'blood' }],
        spouses: [{ id: prefix + 'untested_spouse', type: 'married' }],
        siblings: [],
        data: { name: 'Untested Parent', isUntested: true }
      },
      {
        id: prefix + 'untested_spouse',
        gender: 'male',
        parents: [],
        children: [{ id: createNodeId(childM.name, prefix), type: 'blood' }, { id: createNodeId(childF.name, prefix), type: 'blood' }],
        spouses: [{ id: prefix + 'untested_parent', type: 'married' }],
        siblings: [],
        data: { name: 'Untested Father', isUntested: true }
      },
      {
        id: createNodeId(childM.name, prefix),
        gender: 'male',
        parents: [{ id: prefix + 'untested_parent', type: 'blood' }, { id: prefix + 'untested_spouse', type: 'blood' }],
        children: [],
        spouses: [],
        siblings: [{ id: createNodeId(childF.name, prefix), type: 'blood' }],
        data: { name: childM.name, label: 'Grandchild 1', isIndex: true }
      },
      {
        id: createNodeId(childF.name, prefix),
        gender: 'female',
        parents: [{ id: prefix + 'untested_parent', type: 'blood' }, { id: prefix + 'untested_spouse', type: 'blood' }],
        children: [],
        spouses: [],
        siblings: [{ id: createNodeId(childM.name, prefix), type: 'blood' }],
        data: { name: childF.name, label: 'Grandchild 2', isIndex: true }
      }
    ];
    
    permutations.push({
      id: 0,
      nodes,
      rootId: createNodeId(parentM.name, prefix),
      label: `Grandparent: ${parentM.name.split(' ')[0]} & ${parentF.name.split(' ')[0]} -> ? -> ${childM.name.split(' ')[0]} & ${childF.name.split(' ')[0]}`,
      type: 'Grandparent',
      category: 'extended',
      details: `${parentM.name} & ${parentF.name} are grandparents of ${childM.name} & ${childF.name}`
    });
  }
  
  // 5. Grandparent Reverse: childM & childF are grandparents of parentM & parentF
  if (hasEdge(pcEdges, childM.id, parentM.id) && hasEdge(pcEdges, childF.id, parentF.id)) {
    const prefix = 'gpr_';
    
    const nodes: PedigreeNode[] = [
      {
        id: createNodeId(childM.name, prefix),
        gender: 'male',
        parents: [],
        children: [{ id: prefix + 'untested_parent', type: 'blood' }],
        spouses: [{ id: createNodeId(childF.name, prefix), type: 'married' }],
        siblings: [],
        data: { name: childM.name, label: 'Grandfather', isIndex: true }
      },
      {
        id: createNodeId(childF.name, prefix),
        gender: 'female',
        parents: [],
        children: [{ id: prefix + 'untested_parent', type: 'blood' }],
        spouses: [{ id: createNodeId(childM.name, prefix), type: 'married' }],
        siblings: [],
        data: { name: childF.name, label: 'Grandmother', isIndex: true }
      },
      {
        id: prefix + 'untested_parent',
        gender: 'female',
        parents: [{ id: createNodeId(childM.name, prefix), type: 'blood' }, { id: createNodeId(childF.name, prefix), type: 'blood' }],
        children: [{ id: createNodeId(parentM.name, prefix), type: 'blood' }, { id: createNodeId(parentF.name, prefix), type: 'blood' }],
        spouses: [{ id: prefix + 'untested_spouse', type: 'married' }],
        siblings: [],
        data: { name: 'Untested Parent', isUntested: true }
      },
      {
        id: prefix + 'untested_spouse',
        gender: 'male',
        parents: [],
        children: [{ id: createNodeId(parentM.name, prefix), type: 'blood' }, { id: createNodeId(parentF.name, prefix), type: 'blood' }],
        spouses: [{ id: prefix + 'untested_parent', type: 'married' }],
        siblings: [],
        data: { name: 'Untested Father', isUntested: true }
      },
      {
        id: createNodeId(parentM.name, prefix),
        gender: 'male',
        parents: [{ id: prefix + 'untested_parent', type: 'blood' }, { id: prefix + 'untested_spouse', type: 'blood' }],
        children: [],
        spouses: [],
        siblings: [{ id: createNodeId(parentF.name, prefix), type: 'blood' }],
        data: { name: parentM.name, label: 'Grandchild 1', isIndex: true }
      },
      {
        id: createNodeId(parentF.name, prefix),
        gender: 'female',
        parents: [{ id: prefix + 'untested_parent', type: 'blood' }, { id: prefix + 'untested_spouse', type: 'blood' }],
        children: [],
        spouses: [],
        siblings: [{ id: createNodeId(parentM.name, prefix), type: 'blood' }],
        data: { name: parentF.name, label: 'Grandchild 2', isIndex: true }
      }
    ];
    
    permutations.push({
      id: 0,
      nodes,
      rootId: createNodeId(childM.name, prefix),
      label: `Grandparent (Reverse): ${childM.name.split(' ')[0]} & ${childF.name.split(' ')[0]} -> ? -> ${parentM.name.split(' ')[0]} & ${parentF.name.split(' ')[0]}`,
      type: 'Grandparent',
      category: 'extended',
      details: `${childM.name} & ${childF.name} are grandparents of ${parentM.name} & ${parentF.name} (reverse hypothesis)`
    });
  }
  
  // 6-9. Half-Siblings
  // Half-Sibs: childM & childF share Father parentM
  if (hasEdge(pcEdges, parentM.id, childM.id) && hasEdge(pcEdges, parentM.id, childF.id)) {
    const prefix = 'hs1_';
    
    const nodes: PedigreeNode[] = [
      {
        id: createNodeId(parentM.name, prefix),
        gender: 'male',
        parents: [],
        children: [{ id: createNodeId(childM.name, prefix), type: 'blood' }, { id: createNodeId(childF.name, prefix), type: 'blood' }],
        spouses: [{ id: prefix + 'mother1', type: 'married' }, { id: createNodeId(parentF.name, prefix), type: 'married' }],
        siblings: [],
        data: { name: parentM.name, label: 'Shared Father', isIndex: true }
      },
      {
        id: prefix + 'mother1',
        gender: 'female',
        parents: [],
        children: [{ id: createNodeId(childM.name, prefix), type: 'blood' }],
        spouses: [{ id: createNodeId(parentM.name, prefix), type: 'married' }],
        siblings: [],
        data: { name: 'Untested Mother 1', isUntested: true }
      },
      {
        id: createNodeId(parentF.name, prefix),
        gender: 'female',
        parents: [],
        children: [{ id: createNodeId(childF.name, prefix), type: 'blood' }],
        spouses: [{ id: createNodeId(parentM.name, prefix), type: 'married' }],
        siblings: [],
        data: { name: parentF.name, label: 'Mother 2', isIndex: true }
      },
      {
        id: createNodeId(childM.name, prefix),
        gender: 'male',
        parents: [{ id: createNodeId(parentM.name, prefix), type: 'blood' }, { id: prefix + 'mother1', type: 'blood' }],
        children: [],
        spouses: [],
        siblings: [],
        data: { name: childM.name, label: 'Half-Sibling 1', isIndex: true }
      },
      {
        id: createNodeId(childF.name, prefix),
        gender: 'female',
        parents: [{ id: createNodeId(parentM.name, prefix), type: 'blood' }, { id: createNodeId(parentF.name, prefix), type: 'blood' }],
        children: [],
        spouses: [],
        siblings: [],
        data: { name: childF.name, label: 'Half-Sibling 2', isIndex: true }
      }
    ];
    
    permutations.push({
      id: 0,
      nodes,
      rootId: createNodeId(parentM.name, prefix),
      label: `Half-Sibs: ${childM.name.split(' ')[0]} & ${childF.name.split(' ')[0]} (Shared Father ${parentM.name.split(' ')[0]})`,
      type: 'Half-Siblings',
      category: 'extended',
      details: `${childM.name} & ${childF.name} share Father ${parentM.name} only (2nd degree)`
    });
  }
  
  // Half-Sibs: childM & childF share Mother parentF
  if (hasEdge(pcEdges, parentF.id, childM.id) && hasEdge(pcEdges, parentF.id, childF.id)) {
    const prefix = 'hs2_';
    
    const nodes: PedigreeNode[] = [
      {
        id: createNodeId(parentF.name, prefix),
        gender: 'female',
        parents: [],
        children: [{ id: createNodeId(childM.name, prefix), type: 'blood' }, { id: createNodeId(childF.name, prefix), type: 'blood' }],
        spouses: [{ id: createNodeId(parentM.name, prefix), type: 'married' }, { id: prefix + 'father2', type: 'married' }],
        siblings: [],
        data: { name: parentF.name, label: 'Shared Mother', isIndex: true }
      },
      {
        id: createNodeId(parentM.name, prefix),
        gender: 'male',
        parents: [],
        children: [{ id: createNodeId(childM.name, prefix), type: 'blood' }],
        spouses: [{ id: createNodeId(parentF.name, prefix), type: 'married' }],
        siblings: [],
        data: { name: parentM.name, label: 'Father 1', isIndex: true }
      },
      {
        id: prefix + 'father2',
        gender: 'male',
        parents: [],
        children: [{ id: createNodeId(childF.name, prefix), type: 'blood' }],
        spouses: [{ id: createNodeId(parentF.name, prefix), type: 'married' }],
        siblings: [],
        data: { name: 'Untested Father 2', isUntested: true }
      },
      {
        id: createNodeId(childM.name, prefix),
        gender: 'male',
        parents: [{ id: createNodeId(parentM.name, prefix), type: 'blood' }, { id: createNodeId(parentF.name, prefix), type: 'blood' }],
        children: [],
        spouses: [],
        siblings: [],
        data: { name: childM.name, label: 'Half-Sibling 1', isIndex: true }
      },
      {
        id: createNodeId(childF.name, prefix),
        gender: 'female',
        parents: [{ id: prefix + 'father2', type: 'blood' }, { id: createNodeId(parentF.name, prefix), type: 'blood' }],
        children: [],
        spouses: [],
        siblings: [],
        data: { name: childF.name, label: 'Half-Sibling 2', isIndex: true }
      }
    ];
    
    permutations.push({
      id: 0,
      nodes,
      rootId: createNodeId(parentF.name, prefix),
      label: `Half-Sibs: ${childM.name.split(' ')[0]} & ${childF.name.split(' ')[0]} (Shared Mother ${parentF.name.split(' ')[0]})`,
      type: 'Half-Siblings',
      category: 'extended',
      details: `${childM.name} & ${childF.name} share Mother ${parentF.name} only (2nd degree)`
    });
  }
  
  // Half-Sibs (Reverse): parentM & parentF share Father childM
  if (hasEdge(pcEdges, childM.id, parentM.id) && hasEdge(pcEdges, childM.id, parentF.id)) {
    const prefix = 'hsr1_';
    
    const nodes: PedigreeNode[] = [
      {
        id: createNodeId(childM.name, prefix),
        gender: 'male',
        parents: [],
        children: [{ id: createNodeId(parentM.name, prefix), type: 'blood' }, { id: createNodeId(parentF.name, prefix), type: 'blood' }],
        spouses: [{ id: prefix + 'mother1', type: 'married' }, { id: createNodeId(childF.name, prefix), type: 'married' }],
        siblings: [],
        data: { name: childM.name, label: 'Shared Father', isIndex: true }
      },
      {
        id: prefix + 'mother1',
        gender: 'female',
        parents: [],
        children: [{ id: createNodeId(parentM.name, prefix), type: 'blood' }],
        spouses: [{ id: createNodeId(childM.name, prefix), type: 'married' }],
        siblings: [],
        data: { name: 'Untested Mother 1', isUntested: true }
      },
      {
        id: createNodeId(childF.name, prefix),
        gender: 'female',
        parents: [],
        children: [{ id: createNodeId(parentF.name, prefix), type: 'blood' }],
        spouses: [{ id: createNodeId(childM.name, prefix), type: 'married' }],
        siblings: [],
        data: { name: childF.name, label: 'Mother 2', isIndex: true }
      },
      {
        id: createNodeId(parentM.name, prefix),
        gender: 'male',
        parents: [{ id: createNodeId(childM.name, prefix), type: 'blood' }, { id: prefix + 'mother1', type: 'blood' }],
        children: [],
        spouses: [],
        siblings: [],
        data: { name: parentM.name, label: 'Half-Sibling 1', isIndex: true }
      },
      {
        id: createNodeId(parentF.name, prefix),
        gender: 'female',
        parents: [{ id: createNodeId(childM.name, prefix), type: 'blood' }, { id: createNodeId(childF.name, prefix), type: 'blood' }],
        children: [],
        spouses: [],
        siblings: [],
        data: { name: parentF.name, label: 'Half-Sibling 2', isIndex: true }
      }
    ];
    
    permutations.push({
      id: 0,
      nodes,
      rootId: createNodeId(childM.name, prefix),
      label: `Half-Sibs (Reverse): ${parentM.name.split(' ')[0]} & ${parentF.name.split(' ')[0]} (Shared Father ${childM.name.split(' ')[0]})`,
      type: 'Half-Siblings',
      category: 'extended',
      details: `${parentM.name} & ${parentF.name} share Father ${childM.name} only (reverse hypothesis)`
    });
  }
  
  // Half-Sibs (Reverse): parentM & parentF share Mother childF
  if (hasEdge(pcEdges, childF.id, parentM.id) && hasEdge(pcEdges, childF.id, parentF.id)) {
    const prefix = 'hsr2_';
    
    const nodes: PedigreeNode[] = [
      {
        id: createNodeId(childF.name, prefix),
        gender: 'female',
        parents: [],
        children: [{ id: createNodeId(parentM.name, prefix), type: 'blood' }, { id: createNodeId(parentF.name, prefix), type: 'blood' }],
        spouses: [{ id: createNodeId(childM.name, prefix), type: 'married' }, { id: prefix + 'father2', type: 'married' }],
        siblings: [],
        data: { name: childF.name, label: 'Shared Mother', isIndex: true }
      },
      {
        id: createNodeId(childM.name, prefix),
        gender: 'male',
        parents: [],
        children: [{ id: createNodeId(parentM.name, prefix), type: 'blood' }],
        spouses: [{ id: createNodeId(childF.name, prefix), type: 'married' }],
        siblings: [],
        data: { name: childM.name, label: 'Father 1', isIndex: true }
      },
      {
        id: prefix + 'father2',
        gender: 'male',
        parents: [],
        children: [{ id: createNodeId(parentF.name, prefix), type: 'blood' }],
        spouses: [{ id: createNodeId(childF.name, prefix), type: 'married' }],
        siblings: [],
        data: { name: 'Untested Father 2', isUntested: true }
      },
      {
        id: createNodeId(parentM.name, prefix),
        gender: 'male',
        parents: [{ id: createNodeId(childM.name, prefix), type: 'blood' }, { id: createNodeId(childF.name, prefix), type: 'blood' }],
        children: [],
        spouses: [],
        siblings: [],
        data: { name: parentM.name, label: 'Half-Sibling 1', isIndex: true }
      },
      {
        id: createNodeId(parentF.name, prefix),
        gender: 'female',
        parents: [{ id: prefix + 'father2', type: 'blood' }, { id: createNodeId(childF.name, prefix), type: 'blood' }],
        children: [],
        spouses: [],
        siblings: [],
        data: { name: parentF.name, label: 'Half-Sibling 2', isIndex: true }
      }
    ];
    
    permutations.push({
      id: 0,
      nodes,
      rootId: createNodeId(childF.name, prefix),
      label: `Half-Sibs (Reverse): ${parentM.name.split(' ')[0]} & ${parentF.name.split(' ')[0]} (Shared Mother ${childF.name.split(' ')[0]})`,
      type: 'Half-Siblings',
      category: 'extended',
      details: `${parentM.name} & ${parentF.name} share Mother ${childF.name} only (reverse hypothesis)`
    });
  }
  
  return permutations;
}

// ============================================================================
// RULE 14: SIBLING ASSERTION VALIDATION (Pruning)
// ============================================================================

/**
 * Validates sibling assertions using the kinship graph directly.
 * Returns false if any tested sibling pair lacks FS support.
 */
function hasValidSiblingAssertions(
  permutation: Permutation,
  graph: KinshipGraph
): boolean {
  const { fsEdges, individuals } = graph;
  const nodes = [...permutation.nodes, ...(permutation.nodesB || [])];
  
  // Build a name-to-ID lookup for tested individuals
  const nameToId = new Map<string, string>();
  individuals.forEach(ind => {
    nameToId.set(ind.name.toLowerCase(), ind.id);
  });
  
  // Extract all sibling pairs from the permutation
  for (const node of nodes) {
    if (node.data.isUntested) continue;
    if (!node.siblings || node.siblings.length === 0) continue;
    
    const name1 = node.data.name.toLowerCase();
    const id1 = nameToId.get(name1);
    if (!id1) continue; // Not a tested individual
    
    for (const sibling of node.siblings) {
      const sibNode = nodes.find(n => n.id === sibling.id);
      if (!sibNode || sibNode.data.isUntested) continue;
      
      const name2 = sibNode.data.name.toLowerCase();
      const id2 = nameToId.get(name2);
      if (!id2) continue; // Not a tested individual
      
      // Check if this sibling pair has FS support
      if (!hasEdge(fsEdges, id1, id2)) {
        // This permutation asserts siblings without FS support - INVALID
        return false;
      }
    }
  }
  
  return true;
}

/**
 * Helper: Check if two individuals share a tested parent via PC edges.
 * Used for half-sibling exception in Rule 15.
 * Returns true if there exists a tested individual with PC edges to BOTH id1 and id2.
 */
function hasSharedParentPCSupport(
  id1: string,
  id2: string,
  pcEdges: Map<string, Set<string>>,
  idByName: Map<string, string>
): boolean {
  // Find all tested individual IDs from the idByName map
  const allTestedIds = new Set(idByName.values());
  
  // Check if any tested individual has PC edges to BOTH id1 and id2
  for (const potentialParentId of allTestedIds) {
    if (potentialParentId === id1 || potentialParentId === id2) continue;
    
    const parentPCNeighbors = pcEdges.get(potentialParentId);
    if (!parentPCNeighbors) continue;
    
    const hasEdgeToId1 = parentPCNeighbors.has(id1) || (pcEdges.get(id1)?.has(potentialParentId) ?? false);
    const hasEdgeToId2 = parentPCNeighbors.has(id2) || (pcEdges.get(id2)?.has(potentialParentId) ?? false);
    
    if (hasEdgeToId1 && hasEdgeToId2) {
      // Found a shared parent with PC support to both individuals
      return true;
    }
  }
  
  return false;
}

/**
 * Helper: Get node ID for a given tested individual name (case-insensitive).
 */
function getNodeIdForName(nodes: PedigreeNode[], name: string): string | null {
  const node = nodes.find(n => 
    !n.data?.isUntested && 
    n.data?.name?.toLowerCase() === name.toLowerCase()
  );
  return node?.id ?? null;
}

/**
 * Helper: Check if two nodes are positioned as siblings in the pedigree.
 * Siblings share at least one parent node.
 */
function arePositionedAsSiblings(
  nodes: PedigreeNode[],
  nodeById: Map<string, PedigreeNode>,
  nodeId1: string,
  nodeId2: string
): boolean {
  const node1 = nodeById.get(nodeId1);
  const node2 = nodeById.get(nodeId2);
  
  if (!node1 || !node2) return false;
  
  // Check if they share at least one parent
  const parents1 = new Set(node1.parents.map(p => p.id));
  const parents2 = new Set(node2.parents.map(p => p.id));
  
  for (const p1 of parents1) {
    if (parents2.has(p1)) {
      return true; // They share a parent
    }
  }
  
  // Also check via sibling references
  if (node1.siblings && node1.siblings.some(s => s.id === nodeId2)) {
    return true;
  }
  if (node2.siblings && node2.siblings.some(s => s.id === nodeId1)) {
    return true;
  }
  
  return false;
}

/**
 * Helper: Check if two nodes are positioned as HALF-siblings in the pedigree.
 * Half-siblings share exactly one parent (not both parents).
 */
function arePositionedAsHalfSiblings(
  nodes: PedigreeNode[],
  nodeById: Map<string, PedigreeNode>,
  nodeId1: string,
  nodeId2: string
): boolean {
  const node1 = nodeById.get(nodeId1);
  const node2 = nodeById.get(nodeId2);
  
  if (!node1 || !node2) return false;
  
  // Get parents for each node
  const parents1 = new Set(node1.parents.map(p => p.id));
  const parents2 = new Set(node2.parents.map(p => p.id));
  
  // Count shared parents
  let sharedCount = 0;
  for (const p1 of parents1) {
    if (parents2.has(p1)) {
      sharedCount++;
    }
  }
  
  // Half-siblings share exactly 1 parent (not 0, not 2)
  // Also, they must have at least 1 different parent each
  const differentParents1 = Array.from(parents1).filter(p => !parents2.has(p));
  const differentParents2 = Array.from(parents2).filter(p => !parents1.has(p));
  
  return sharedCount === 1 && (differentParents1.length > 0 || differentParents2.length > 0);
}

/**
 * Helper: Check if two nodes are positioned as grandparent-grandchild in the pedigree.
 * Grandparent-grandchild means one is an ancestor of the other with exactly 1 intermediate generation.
 */
function arePositionedAsGrandparentGrandchild(
  nodes: PedigreeNode[],
  nodeById: Map<string, PedigreeNode>,
  nodeId1: string,
  nodeId2: string
): { isGrandparentGrandchild: boolean; middleNodeId: string | null } {
  const node1 = nodeById.get(nodeId1);
  const node2 = nodeById.get(nodeId2);
  
  if (!node1 || !node2) return { isGrandparentGrandchild: false, middleNodeId: null };
  
  // Check if node1 is grandparent of node2 (node1 -> parent -> node2)
  for (const parentRef of node2.parents) {
    const parentNode = nodeById.get(parentRef.id);
    if (!parentNode) continue;
    
    // Check if node1 is parent of this parent node
    if (parentNode.parents.some(gp => gp.id === nodeId1)) {
      return { isGrandparentGrandchild: true, middleNodeId: parentRef.id };
    }
  }
  
  // Check if node2 is grandparent of node1 (node2 -> parent -> node1)
  for (const parentRef of node1.parents) {
    const parentNode = nodeById.get(parentRef.id);
    if (!parentNode) continue;
    
    // Check if node2 is parent of this parent node
    if (parentNode.parents.some(gp => gp.id === nodeId2)) {
      return { isGrandparentGrandchild: true, middleNodeId: parentRef.id };
    }
  }
  
  return { isGrandparentGrandchild: false, middleNodeId: null };
}

/**
 * Helper: Check if grandparent-grandchild pair has transitive PC support through middle parent.
 * For the 3-gen structure to be valid, the middle parent should have PC edges to both.
 */
function hasTransitiveGrandparentSupport(
  id1: string,
  id2: string,
  middleNodeName: string | null,
  pcEdges: Map<string, Set<string>>,
  idByName: Map<string, string>
): boolean {
  if (!middleNodeName) return false;
  
  const middleId = idByName.get(middleNodeName.toLowerCase());
  if (!middleId) return false; // Middle parent must be tested
  
  // Check if middle parent has PC edges to both grandparent and grandchild
  const hasPC1 = hasEdge(pcEdges, middleId, id1);
  const hasPC2 = hasEdge(pcEdges, middleId, id2);
  
  return hasPC1 && hasPC2;
}

/**
 * Rule 15: Complete Pairwise Support Requirement
 * Every pair of tested individuals in a BLOOD relationship within the pedigree 
 * must have at least one kinship edge (PC, FS, or EXT) with LR > 1.
 * 
 * EXCLUDES: 
 * - Spouse pairs (married couples are not biologically related)
 * - Pairs in different subtrees (disjointed structures have no cross-tree relationships)
 * 
 * HALF-SIBLING EXCEPTION:
 * If two individuals both have PC edges to a shared tested parent, the half-sibling
 * relationship is supported via the parent's PC evidence, even without a direct edge.
 * 
 * GRANDPARENT EXCEPTION:
 * If two individuals are positioned as grandparent-grandchild, and a tested middle parent
 * has PC edges to both, the relationship is supported transitively through the parent.
 * 
 * This ensures we don't generate pedigrees that assert genetic relationships
 * without any pairwise kinship evidence while allowing valid multi-generation structures.
 */
function hasCompletePairwiseSupport(
  perm: Permutation,
  graph: KinshipGraph
): boolean {
  const { pcEdges, fsEdges, extEdges, idByName } = graph;
  
  // Check if this is a flexible family structure (generated for non-quartet cases)
  // The half-sibling exception applies to these types where half-siblings may share
  // a tested parent without direct kinship evidence between them
  const isFlexibleFamilyType = perm.type === 'Half-Sibling Family' || perm.type === 'Flexible Siblings';
  
  // Check if this is a 3-generation Extended structure that may need grandparent exception
  // Only apply to FLEXIBLE generators, not the standard quartet Extended generators
  // which should have direct kinship support for all pairs
  const is3GenExtendedType = perm.type === 'Flexible Linear' || 
                              perm.type === 'Flexible 3-Gen';
  
  // Check if this is a Complex Nuclear Half-Sibling Family
  // These have half-siblings sharing an UNTESTED parent, where transitive kinship
  // is established through EXT edges to a tested hub individual
  // Only exact type match - don't match on substrings
  const isComplexNuclearType = perm.type === 'Complex Nuclear Half-Sibling Family';
  
  // Helper to validate a single tree's blood-connected pairs
  const validateTree = (nodes: PedigreeNode[]): boolean => {
    // Build a lookup from node id to tested individual name
    const nodeIdToName = new Map<string, string>();
    const testedNodeIds = new Set<string>();
    const nodeById = new Map<string, PedigreeNode>();
    
    nodes.forEach(node => {
      nodeById.set(node.id, node);
      if (!node.data?.isUntested && node.data?.name) {
        const nameLower = node.data.name.toLowerCase();
        if (idByName.has(nameLower)) {
          nodeIdToName.set(node.id, nameLower);
          testedNodeIds.add(node.id);
        }
      }
    });
    
    // Build blood-connected pairs using directional traversal:
    // Two tested individuals are blood-connected if:
    // 1. One is ancestor of the other (via parent-child path)
    // 2. They share an ancestor (siblings, cousins, etc.)
    // Note: Spouses are NOT blood-related even if they share children
    const bloodConnectedPairs = new Set<string>();
    
    // For each node, find all ancestors (traverse UP through parents only)
    const ancestorsOf = new Map<string, Set<string>>();
    
    const findAncestors = (nodeId: string): Set<string> => {
      if (ancestorsOf.has(nodeId)) return ancestorsOf.get(nodeId)!;
      
      const ancestors = new Set<string>();
      const node = nodeById.get(nodeId);
      if (!node) {
        ancestorsOf.set(nodeId, ancestors);
        return ancestors;
      }
      
      // Add direct parents
      for (const parentRef of node.parents) {
        ancestors.add(parentRef.id);
        // Recursively add parent's ancestors
        const parentAncestors = findAncestors(parentRef.id);
        for (const a of parentAncestors) {
          ancestors.add(a);
        }
      }
      
      ancestorsOf.set(nodeId, ancestors);
      return ancestors;
    };
    
    // Compute ancestors for all tested nodes
    for (const nodeId of testedNodeIds) {
      findAncestors(nodeId);
    }
    
    // Find blood-connected pairs:
    for (const nodeId of testedNodeIds) {
      const name1 = nodeIdToName.get(nodeId)!;
      const myAncestors = ancestorsOf.get(nodeId) || new Set();
      
      for (const otherId of testedNodeIds) {
        if (otherId === nodeId) continue;
        
        const name2 = nodeIdToName.get(otherId)!;
        const pairKey = [name1, name2].sort().join('|');
        
        // Already added?
        if (bloodConnectedPairs.has(pairKey)) continue;
        
        // Case 1: One is ancestor of the other
        const otherAncestors = ancestorsOf.get(otherId) || new Set();
        if (myAncestors.has(otherId) || otherAncestors.has(nodeId)) {
          bloodConnectedPairs.add(pairKey);
          continue;
        }
        
        // Case 2: They share an ancestor (siblings, half-siblings, cousins, etc.)
        for (const myAnc of myAncestors) {
          if (otherAncestors.has(myAnc)) {
            bloodConnectedPairs.add(pairKey);
            break;
          }
        }
      }
    }
    
    // Check all blood-connected pairs have kinship support
    for (const pairKey of bloodConnectedPairs) {
      const [name1, name2] = pairKey.split('|');
      const id1 = idByName.get(name1);
      const id2 = idByName.get(name2);
      
      if (!id1 || !id2) continue;
      
      // Check if this pair has ANY direct kinship support (PC, FS, or EXT)
      const hasPC = hasEdge(pcEdges, id1, id2);
      const hasFS = hasEdge(fsEdges, id1, id2);
      const hasExt = hasEdge(extEdges, id1, id2);
      
      if (!hasPC && !hasFS && !hasExt) {
        // No direct kinship support - check for exceptions
        const name1NodeId = getNodeIdForName(nodes, name1);
        const name2NodeId = getNodeIdForName(nodes, name2);
        
        let exceptionApplies = false;
        
        // EXCEPTION 1: Half-sibling exception for flexible family structures
        if (isFlexibleFamilyType && name1NodeId && name2NodeId) {
          const areHalfSibs = arePositionedAsHalfSiblings(nodes, nodeById, name1NodeId, name2NodeId);
          
          if (areHalfSibs) {
            const sharedParentSupport = hasSharedParentPCSupport(id1, id2, pcEdges, idByName);
            if (sharedParentSupport) {
              exceptionApplies = true;
            }
          }
        }
        
        // EXCEPTION 2: Grandparent-grandchild exception for 3-generation structures
        // If positioned as grandparent-grandchild with a tested middle parent having PC to both
        if (!exceptionApplies && is3GenExtendedType && name1NodeId && name2NodeId) {
          const gpCheck = arePositionedAsGrandparentGrandchild(nodes, nodeById, name1NodeId, name2NodeId);
          
          if (gpCheck.isGrandparentGrandchild && gpCheck.middleNodeId) {
            // Get the middle node's name to check for PC support
            const middleNode = nodeById.get(gpCheck.middleNodeId);
            const middleName = middleNode?.data?.name || null;
            
            const transitiveSupport = hasTransitiveGrandparentSupport(
              id1, id2, middleName, pcEdges, idByName
            );
            
            if (transitiveSupport) {
              exceptionApplies = true;
            }
          }
        }
        
        // EXCEPTION 3: Also allow grandparent exception for flexible family types
        // (e.g., when a 3-gen structure is generated within a Flexible Siblings permutation)
        if (!exceptionApplies && isFlexibleFamilyType && name1NodeId && name2NodeId) {
          const gpCheck = arePositionedAsGrandparentGrandchild(nodes, nodeById, name1NodeId, name2NodeId);
          
          if (gpCheck.isGrandparentGrandchild && gpCheck.middleNodeId) {
            const middleNode = nodeById.get(gpCheck.middleNodeId);
            const middleName = middleNode?.data?.name || null;
            
            const transitiveSupport = hasTransitiveGrandparentSupport(
              id1, id2, middleName, pcEdges, idByName
            );
            
            if (transitiveSupport) {
              exceptionApplies = true;
            }
          }
        }
        
        // EXCEPTION 4: Avuncular transitive support for Multi-Avuncular structures
        // If A is an uncle/aunt and B is a nephew/niece, they may lack direct kinship
        // but have transitive support through the parent:
        // - A has FS edge to parent P (A is sibling of P)
        // - P has PC edge to B (B is child of P)
        // This means A-B is a valid avuncular relationship without direct evidence
        if (!exceptionApplies && perm.type === 'Multi-Avuncular') {
          // Check if there exists a parent P such that:
          // (A has FS to P) AND (P has PC to B) OR (B has FS to P) AND (P has PC to A)
          for (const [testName, testId] of idByName.entries()) {
            if (testId === id1 || testId === id2) continue;
            
            // Check pattern 1: id1 is uncle, id2 is nephew (id1 FS→ P, P PC→ id2)
            const id1FsToP = hasEdge(fsEdges, id1, testId);
            const pPcToId2 = hasEdge(pcEdges, testId, id2);
            
            // Check pattern 2: id2 is uncle, id1 is nephew (id2 FS→ P, P PC→ id1)
            const id2FsToP = hasEdge(fsEdges, id2, testId);
            const pPcToId1 = hasEdge(pcEdges, testId, id1);
            
            if ((id1FsToP && pPcToId2) || (id2FsToP && pPcToId1)) {
              exceptionApplies = true;
              break;
            }
          }
        }
        
        // EXCEPTION 5: Complex Nuclear Half-Sibling transitive support
        // In Complex Nuclear Half-Sibling Family structures, half-siblings share an UNTESTED parent.
        // This exception requires:
        // 1. Both individuals are positioned as children of a shared untested parent
        // 2. Both individuals have at least one kinship edge to a tested individual
        if (!exceptionApplies && isComplexNuclearType && name1NodeId && name2NodeId) {
          // Verify both individuals share an untested parent in the structure
          const node1 = nodeById.get(name1NodeId);
          const node2 = nodeById.get(name2NodeId);
          
          let shareUntestedParent = false;
          if (node1 && node2) {
            const parents1 = new Set(node1.parents.map(p => p.id));
            const parents2 = new Set(node2.parents.map(p => p.id));
            
            // Find shared parents
            for (const p1 of parents1) {
              if (parents2.has(p1)) {
                const parentNode = nodeById.get(p1);
                // Only count if parent is untested (placeholder)
                if (parentNode?.data?.isUntested) {
                  shareUntestedParent = true;
                  break;
                }
              }
            }
          }
          
          // Only apply exception if they actually share an untested parent
          if (shareUntestedParent) {
            // Both individuals need at least one kinship edge to someone in the structure
            let id1HasAnyKinship = false;
            let id2HasAnyKinship = false;
            
            for (const [testName, testId] of idByName.entries()) {
              if (testId === id1) continue;
              const hasKinship1 = hasEdge(pcEdges, id1, testId) || 
                                  hasEdge(fsEdges, id1, testId) || 
                                  hasEdge(extEdges, id1, testId);
              if (hasKinship1) {
                id1HasAnyKinship = true;
                break;
              }
            }
            
            for (const [testName, testId] of idByName.entries()) {
              if (testId === id2) continue;
              const hasKinship2 = hasEdge(pcEdges, id2, testId) || 
                                  hasEdge(fsEdges, id2, testId) || 
                                  hasEdge(extEdges, id2, testId);
              if (hasKinship2) {
                id2HasAnyKinship = true;
                break;
              }
            }
            
            // If both individuals have kinship to someone AND share an untested parent,
            // the half-sibling relationship through the shared untested parent is valid
            if (id1HasAnyKinship && id2HasAnyKinship) {
              exceptionApplies = true;
            }
          }
        }
        
        // EXCEPTION 6: Transitive kinship for extended family structures (COUSINS ONLY)
        // If A and B don't have direct kinship, but BOTH have kinship edges to
        // a shared tested individual C, then A-B can be part of the same extended family.
        // This handles cases like cousins where A↔C (extended) and B↔C (extended)
        // means A and B are related through a common relative C.
        // IMPORTANT: This exception should NOT apply to Linear/3-gen PC chains,
        // which assert direct grandparent-grandchild relationships requiring kinship support.
        const isTrueCousinStructure = (
          perm.type === 'Extended Family Network' ||
          perm.type === 'Extended Family (Cousins)' ||
          perm.type.includes('Cousins') ||
          perm.type === 'Disjointed Family'  // Disjointed uses cousin structure
        );
        
        // Explicitly EXCLUDE Linear/PC-based structures from transitive exception
        const isLinearPCStructure = (
          perm.type === 'Linear' ||
          perm.type === 'Flexible Linear' ||
          perm.type === 'Flexible 3-Gen' ||
          perm.type.startsWith('Linear:') ||
          perm.type.includes('Grandparent')
        );
        
        if (!exceptionApplies && isTrueCousinStructure && !isLinearPCStructure) {
          // Check if there exists a tested individual C such that
          // (A↔C with LR > 1) AND (B↔C with LR > 1)
          for (const [testName, testId] of idByName.entries()) {
            if (testId === id1 || testId === id2) continue;
            
            // Check if id1 has ANY kinship to testId
            const hasKinship1 = hasEdge(pcEdges, id1, testId) || 
                                hasEdge(fsEdges, id1, testId) || 
                                hasEdge(extEdges, id1, testId);
            
            // Check if id2 has ANY kinship to testId
            const hasKinship2 = hasEdge(pcEdges, id2, testId) || 
                                hasEdge(fsEdges, id2, testId) || 
                                hasEdge(extEdges, id2, testId);
            
            if (hasKinship1 && hasKinship2) {
              exceptionApplies = true;
              break;
            }
          }
        }
        
        if (!exceptionApplies) {
          // No exception applies - this pair lacks kinship support
          return false;
        }
      }
    }
    
    return true;
  };
  
  // Validate main tree (nodes)
  if (!validateTree(perm.nodes)) return false;
  
  // Validate secondary tree if present (disjointed structures)
  if (perm.nodesB && !validateTree(perm.nodesB)) return false;
  
  return true;
}

/**
 * Rule 23: Cousin Structure PC Evidence Check
 * Prevents cousin structures when PC evidence indicates a closer sibling relationship.
 * 
 * If two individuals in a "cousin" permutation both have PC edges to the same tested parent,
 * they should be siblings (not cousins) and the cousin permutation is invalid.
 * 
 * Example: If Kate has PC edges to both Jane and Zoe, generating Jane↔Zoe as "cousins"
 * is incorrect because they are siblings through their shared parent Kate.
 */
function isValidCousinStructure(
  perm: Permutation,
  graph: KinshipGraph
): boolean {
  // EXCLUDE structures that correctly use PC evidence
  // Linear and Half-Sibling structures already assert proper PC relationships
  const isValidPCStructure = (
    perm.type.includes('Linear') ||
    perm.type.includes('Half-Sib') ||
    perm.type.includes('Grandparent') ||
    perm.type.startsWith('Parent') ||
    perm.type.startsWith('Nuclear') ||
    perm.type.includes('Best-Fit') || // Best-fit structures use PC-priority logic
    perm.category === 'parentChild' ||
    perm.category === 'fullSibling'
  );
  
  if (isValidPCStructure) {
    return true; // These structures correctly use PC evidence
  }
  
  // Only apply to extended family structures that might incorrectly assert
  // cousin relationships when PC evidence shows sibling relationships
  const isExtendedCategory = perm.category === 'extended';
  
  // Check specific cousin-type names
  // NOTE: Multi-Avuncular is NOT a cousin structure - it's a 3-generation family
  // with tested parents, children, and uncles/aunts. The children correctly share
  // tested parents, which is expected behavior, not a disqualifying condition.
  const isMultiAvuncular = perm.type === 'Multi-Avuncular';
  if (isMultiAvuncular) {
    return true; // Multi-Avuncular correctly handles PC-sharing children as siblings
  }
  
  const isCousinType = (
    perm.type === 'Extended Family (Cousins)' ||
    perm.type === 'Extended Family Network' ||
    perm.type === 'Disjointed Family' ||
    perm.type.includes('Cousins') ||
    perm.type.includes('Extended')
  );
  
  if (!isExtendedCategory && !isCousinType) {
    return true; // Not a cousin-like structure, skip this check
  }
  
  const { pcEdges, idByName, individuals } = graph;
  
  // Get all tested individual names from the permutation
  const getTestedNames = (nodes: PedigreeNode[]): string[] => {
    return nodes
      .filter(node => !node.data?.isUntested && node.data?.name)
      .map(node => node.data!.name!.toLowerCase());
  };
  
  const testedNamesInPerm = new Set([
    ...getTestedNames(perm.nodes),
    ...(perm.nodesB ? getTestedNames(perm.nodesB) : [])
  ]);
  
  // Get all tested individuals that are in this permutation
  const testedInPerm = individuals.filter(ind => 
    testedNamesInPerm.has(ind.name.toLowerCase())
  );
  
  // For each pair of tested individuals in the permutation,
  // check if they BOTH have PC edges to the same tested parent
  for (let i = 0; i < testedInPerm.length; i++) {
    for (let j = i + 1; j < testedInPerm.length; j++) {
      const ind1 = testedInPerm[i];
      const ind2 = testedInPerm[j];
      
      // Get PC-connected individuals for each
      const pc1 = pcEdges.get(ind1.id) || new Set<string>();
      const pc2 = pcEdges.get(ind2.id) || new Set<string>();
      
      // Check if they share a common PC-connected tested individual
      for (const parentId of pc1) {
        if (pc2.has(parentId)) {
          // Both ind1 and ind2 have PC edges to the same individual (parentId)
          // This means they should be siblings through this shared parent, not cousins
          return false;
        }
      }
    }
  }
  
  return true;
}

// ============================================================================
// GENERALIZED N-INDIVIDUAL PERMUTATION GENERATORS
// ============================================================================

/**
 * Find potential nuclear family cores from PC edges.
 * A core is: one or two parents (must be M+F or M+placeholder or F+placeholder) + children.
 * Returns all valid parent-child configurations.
 */
interface FamilyCore {
  parents: TestedIndividual[];  // 1-2 tested parents (must be M+F or single)
  children: TestedIndividual[];  // Children with PC edges to parent(s)
  needsPlaceholder: 'father' | 'mother' | null;  // Missing parent gender
}

function findFamilyCores(graph: KinshipGraph): FamilyCore[] {
  const { individuals, pcEdges } = graph;
  const cores: FamilyCore[] = [];
  
  // Strategy: Find individuals with multiple PC edges (potential parents)
  // Group children by their PC-connected "parents"
  
  // Find all parent candidates (individuals with PC edges to 2+ others)
  const parentCandidates: TestedIndividual[] = [];
  for (const ind of individuals) {
    const pcNeighbors = Array.from(pcEdges.get(ind.id) || []);
    if (pcNeighbors.length >= 1) {
      parentCandidates.push(ind);
    }
  }
  
  // Try each individual as potential parent/child hub
  for (const hub of individuals) {
    const hubNeighbors = Array.from(pcEdges.get(hub.id) || []);
    if (hubNeighbors.length < 1) continue;  // Need at least 1 PC connection
    
    const neighbors = hubNeighbors
      .map(id => graph.individualById.get(id))
      .filter((i): i is TestedIndividual => i !== undefined);
    
    // Hub as parent: neighbors are potential children/spouse
    // Look for M+F spouse pair or single parent + placeholder
    const oppositeGender = neighbors.filter(n => n.gender !== hub.gender);
    const sameGender = neighbors.filter(n => n.gender === hub.gender);
    
    // Try hub + one opposite gender neighbor as parent pair
    for (const potentialSpouse of oppositeGender) {
      const potentialChildren = neighbors.filter(n => 
        n.id !== potentialSpouse.id && 
        hasEdge(pcEdges, potentialSpouse.id, n.id)
      );
      
      if (potentialChildren.length > 0) {
        cores.push({
          parents: [hub, potentialSpouse],
          children: potentialChildren,
          needsPlaceholder: null
        });
      }
    }
    
    // Hub as single parent (needs placeholder spouse)
    const childrenOfHub = neighbors.filter(n => {
      // A child should not have PC edges to many others (distinguishes from siblings)
      const theirNeighbors = Array.from(pcEdges.get(n.id) || []);
      return theirNeighbors.length <= 2;  // Has PC to hub + maybe one more
    });
    
    if (childrenOfHub.length > 0) {
      cores.push({
        parents: [hub],
        children: childrenOfHub,
        needsPlaceholder: hub.gender === 'M' ? 'mother' : 'father'
      });
    }
  }
  
  return cores;
}

/**
 * Generate parent-child permutations for N individuals using graph-driven discovery.
 * Works with any number of individuals and any gender distribution.
 */
function generateGeneralizedParentChildPermutations(graph: KinshipGraph): Permutation[] {
  const permutations: Permutation[] = [];
  const { individuals, pcEdges } = graph;
  
  if (individuals.length < 2) return permutations;
  
  // Find all PC-connected pairs and build family structures
  const allPCPairs: Array<{ parent: TestedIndividual; child: TestedIndividual }> = [];
  
  for (const ind1 of individuals) {
    for (const ind2 of individuals) {
      if (ind1.id < ind2.id && hasEdge(pcEdges, ind1.id, ind2.id)) {
        // Add both directions (we don't know who is parent)
        allPCPairs.push({ parent: ind1, child: ind2 });
        allPCPairs.push({ parent: ind2, child: ind1 });
      }
    }
  }
  
  // Strategy: Find "central" parent figure and build family around them
  // A central parent has PC edges to the most other individuals
  const pcCounts = new Map<string, number>();
  for (const ind of individuals) {
    const count = Array.from(pcEdges.get(ind.id) || []).length;
    pcCounts.set(ind.id, count);
  }
  
  // Sort individuals by PC edge count (descending)
  const sortedByConnectivity = [...individuals].sort((a, b) => 
    (pcCounts.get(b.id) || 0) - (pcCounts.get(a.id) || 0)
  );
  
  // Try building a family tree with the most connected individual as the central figure
  const centralFigure = sortedByConnectivity[0];
  const centralNeighbors = Array.from(pcEdges.get(centralFigure.id) || [])
    .map(id => graph.individualById.get(id))
    .filter((i): i is TestedIndividual => i !== undefined);
  
  if (centralNeighbors.length >= 1) {
    // Central figure has PC connections
    // Try: central as parent with spouse + children
    // Or: central as child with parents
    
    // Option A: Central as Parent
    // Find potential spouse (opposite gender with shared PC children)
    const oppositeGenderNeighbors = centralNeighbors.filter(n => n.gender !== centralFigure.gender);
    const sameGenderNeighbors = centralNeighbors.filter(n => n.gender === centralFigure.gender);
    
    for (const potentialSpouse of oppositeGenderNeighbors) {
      // Children are individuals with PC to BOTH central and spouse
      const sharedChildren = centralNeighbors.filter(n => 
        n.id !== potentialSpouse.id && 
        hasEdge(pcEdges, potentialSpouse.id, n.id)
      );
      
      // Also include other central neighbors as potential children
      const allPotentialChildren = centralNeighbors.filter(n => n.id !== potentialSpouse.id);
      
      if (allPotentialChildren.length >= 1) {
        // Build a family tree: central + spouse as parents
        const prefix = `gpc_${permutations.length}_`;
        const fatherId = centralFigure.gender === 'M' 
          ? createNodeId(centralFigure.name, prefix)
          : createNodeId(potentialSpouse.name, prefix);
        const motherId = centralFigure.gender === 'F'
          ? createNodeId(centralFigure.name, prefix)
          : createNodeId(potentialSpouse.name, prefix);
        const father = centralFigure.gender === 'M' ? centralFigure : potentialSpouse;
        const mother = centralFigure.gender === 'F' ? centralFigure : potentialSpouse;
        
        // Remaining individuals are children or need placement
        const childNodes: PedigreeNode[] = [];
        const includedIds = new Set([father.id, mother.id]);
        
        for (const child of allPotentialChildren) {
          includedIds.add(child.id);
          const siblingRefs = allPotentialChildren
            .filter(c => c.id !== child.id)
            .map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' as const }));
          
          childNodes.push({
            id: createNodeId(child.name, prefix),
            gender: genderToNodeGender(child.gender),
            parents: [{ id: fatherId, type: 'blood' }, { id: motherId, type: 'blood' }],
            children: [],
            spouses: [],
            siblings: siblingRefs,
            data: { name: child.name, label: 'Child', isIndex: true }
          });
        }
        
        // Find remaining individuals not yet placed
        const remaining = individuals.filter(i => !includedIds.has(i.id));
        
        // Try to place remaining individuals
        // Check if any remaining have PC edge to parents (grandchildren, grandparents, etc.)
        const canPlaceAllRemaining = remaining.every(r => {
          return hasEdge(pcEdges, r.id, father.id) || 
                 hasEdge(pcEdges, r.id, mother.id) ||
                 allPotentialChildren.some(c => hasEdge(pcEdges, r.id, c.id));
        });
        
        if (remaining.length === 0 || canPlaceAllRemaining) {
          // Add remaining as extended family members
          for (const rem of remaining) {
            // Check relationship to place appropriately
            const hasEdgeToParent = hasEdge(pcEdges, rem.id, father.id) || hasEdge(pcEdges, rem.id, mother.id);
            const hasEdgeToChild = allPotentialChildren.some(c => hasEdge(pcEdges, rem.id, c.id));
            
            if (hasEdgeToParent && !hasEdgeToChild) {
              // Grandparent generation
              const connectedParent = hasEdge(pcEdges, rem.id, father.id) ? father : mother;
              const gpSpouseId = prefix + 'gp_spouse_' + rem.id;
              
              // Add as grandparent
              childNodes.push({
                id: createNodeId(rem.name, prefix),
                gender: genderToNodeGender(rem.gender),
                parents: [],
                children: [{ id: createNodeId(connectedParent.name, prefix), type: 'blood' }],
                spouses: [{ id: gpSpouseId, type: 'married' }],
                siblings: [],
                data: { name: rem.name, label: rem.gender === 'M' ? 'Grandfather' : 'Grandmother', isIndex: true }
              });
              
              // Add untested grandparent spouse
              childNodes.push({
                id: gpSpouseId,
                gender: rem.gender === 'M' ? 'female' : 'male',
                parents: [],
                children: [{ id: createNodeId(connectedParent.name, prefix), type: 'blood' }],
                spouses: [{ id: createNodeId(rem.name, prefix), type: 'married' }],
                siblings: [],
                data: { name: 'Untested', isUntested: true }
              });
            }
          }
          
          const nodes: PedigreeNode[] = [
            {
              id: fatherId,
              gender: 'male',
              parents: [],
              children: allPotentialChildren.map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' })),
              spouses: [{ id: motherId, type: 'married' }],
              siblings: [],
              data: { name: father.name, label: 'Father', isIndex: true }
            },
            {
              id: motherId,
              gender: 'female',
              parents: [],
              children: allPotentialChildren.map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' })),
              spouses: [{ id: fatherId, type: 'married' }],
              siblings: [],
              data: { name: mother.name, label: 'Mother', isIndex: true }
            },
            ...childNodes
          ];
          
          permutations.push({
            id: 0,
            nodes,
            rootId: fatherId,
            label: `Family: ${father.name.split(' ')[0]} & ${mother.name.split(' ')[0]} = Parents`,
            type: 'Family',
            category: 'parentChild',
            details: `${father.name} & ${mother.name} are parents of ${allPotentialChildren.length} children`
          });
        }
      }
    }
    
    // Option A2: Central as single parent (all neighbors are children, spouse is untested)
    // This handles cases where there's no opposite-gender neighbor
    if (oppositeGenderNeighbors.length === 0 && sameGenderNeighbors.length > 0) {
      const prefix = `gpc_single_${permutations.length}_`;
      const centralId = createNodeId(centralFigure.name, prefix);
      const spouseId = prefix + 'untested_spouse';
      const allChildren = centralNeighbors;
      
      const childNodes: PedigreeNode[] = allChildren.map((child, idx) => {
        const siblingRefs = allChildren
          .filter(c => c.id !== child.id)
          .map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' as const }));
        
        return {
          id: createNodeId(child.name, prefix),
          gender: genderToNodeGender(child.gender),
          parents: [
            { id: centralFigure.gender === 'M' ? centralId : spouseId, type: 'blood' },
            { id: centralFigure.gender === 'F' ? centralId : spouseId, type: 'blood' }
          ],
          children: [],
          spouses: [],
          siblings: siblingRefs,
          data: { name: child.name, label: `Child ${idx + 1}`, isIndex: true }
        };
      });
      
      const nodes: PedigreeNode[] = [
        {
          id: centralId,
          gender: genderToNodeGender(centralFigure.gender),
          parents: [],
          children: allChildren.map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' })),
          spouses: [{ id: spouseId, type: 'married' }],
          siblings: [],
          data: { name: centralFigure.name, label: centralFigure.gender === 'M' ? 'Father' : 'Mother', isIndex: true }
        },
        {
          id: spouseId,
          gender: centralFigure.gender === 'M' ? 'female' : 'male',
          parents: [],
          children: allChildren.map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' })),
          spouses: [{ id: centralId, type: 'married' }],
          siblings: [],
          data: { name: 'Untested', isUntested: true }
        },
        ...childNodes
      ];
      
      // Check if all individuals are included
      const includedIds = new Set([centralFigure.id, ...allChildren.map(c => c.id)]);
      const remaining = individuals.filter(i => !includedIds.has(i.id));
      
      if (remaining.length === 0) {
        permutations.push({
          id: 0,
          nodes,
          rootId: centralId,
          label: `Family: ${centralFigure.name.split(' ')[0]} + Untested = Parents`,
          type: 'Family',
          category: 'parentChild',
          details: `${centralFigure.name} is parent of ${allChildren.length} children (with untested spouse)`
        });
      }
    }
    
    // Option B: Central as Child (each neighbor can be parent)
    // This generates reverse permutations for each potential parent
    for (const potentialParent of centralNeighbors) {
      // Central is child of potentialParent
      // Other neighbors of central become siblings or extended family
      const prefix = `gpcr_${permutations.length}_`;
      const spouseId = prefix + 'spouse';
      
      // Other neighbors that could be siblings (same generation as central)
      const otherNeighbors = centralNeighbors.filter(n => n.id !== potentialParent.id);
      
      // Build the core family: potentialParent + untested spouse as parents, central as child
      const childrenOfParent = [centralFigure];
      const includedIds = new Set([potentialParent.id, centralFigure.id]);
      
      // Add other neighbors who have PC edge to potentialParent as siblings
      for (const neighbor of otherNeighbors) {
        if (hasEdge(pcEdges, neighbor.id, potentialParent.id)) {
          childrenOfParent.push(neighbor);
          includedIds.add(neighbor.id);
        }
      }
      
      const childNodes: PedigreeNode[] = childrenOfParent.map(child => {
        const siblingRefs = childrenOfParent
          .filter(c => c.id !== child.id)
          .map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' as const }));
        
        return {
          id: createNodeId(child.name, prefix),
          gender: genderToNodeGender(child.gender),
          parents: [
            { id: potentialParent.gender === 'M' ? createNodeId(potentialParent.name, prefix) : spouseId, type: 'blood' },
            { id: potentialParent.gender === 'F' ? createNodeId(potentialParent.name, prefix) : spouseId, type: 'blood' }
          ],
          children: [],
          spouses: [],
          siblings: siblingRefs,
          data: { name: child.name, label: 'Child', isIndex: true }
        };
      });
      
      const nodes: PedigreeNode[] = [
        {
          id: createNodeId(potentialParent.name, prefix),
          gender: genderToNodeGender(potentialParent.gender),
          parents: [],
          children: childrenOfParent.map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' })),
          spouses: [{ id: spouseId, type: 'married' }],
          siblings: [],
          data: { name: potentialParent.name, label: potentialParent.gender === 'M' ? 'Father' : 'Mother', isIndex: true }
        },
        {
          id: spouseId,
          gender: potentialParent.gender === 'M' ? 'female' : 'male',
          parents: [],
          children: childrenOfParent.map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' })),
          spouses: [{ id: createNodeId(potentialParent.name, prefix), type: 'married' }],
          siblings: [],
          data: { name: 'Untested', isUntested: true }
        },
        ...childNodes
      ];
      
      // Check if all individuals are included
      const remaining = individuals.filter(i => !includedIds.has(i.id));
      
      if (remaining.length === 0) {
        permutations.push({
          id: 0,
          nodes,
          rootId: createNodeId(potentialParent.name, prefix),
          label: `PC: ${potentialParent.name.split(' ')[0]} -> ${centralFigure.name.split(' ')[0]}`,
          type: 'Parent-Child',
          category: 'parentChild',
          details: `${potentialParent.name} is parent of ${childrenOfParent.map(c => c.name).join(', ')}`
        });
      }
    }
  }
  
  // Fallback: Generate simple PC pair permutations
  // Each tested PC pair becomes a separate family unit
  if (permutations.length === 0 && allPCPairs.length > 0) {
    // Find non-overlapping pairs to create disjointed structures
    const usedPairs = new Set<string>();
    
    for (let i = 0; i < allPCPairs.length; i++) {
      const pair = allPCPairs[i];
      const pairKey = [pair.parent.id, pair.child.id].sort().join('|');
      if (usedPairs.has(pairKey)) continue;
      usedPairs.add(pairKey);
      
      const prefix = `spc_${i}_`;
      const parentId = createNodeId(pair.parent.name, prefix);
      const spouseId = prefix + 'spouse';
      const childId = createNodeId(pair.child.name, prefix);
      
      const nodes: PedigreeNode[] = [
        {
          id: parentId,
          gender: genderToNodeGender(pair.parent.gender),
          parents: [],
          children: [{ id: childId, type: 'blood' }],
          spouses: [{ id: spouseId, type: 'married' }],
          siblings: [],
          data: { name: pair.parent.name, label: 'Parent', isIndex: true }
        },
        {
          id: spouseId,
          gender: pair.parent.gender === 'M' ? 'female' : 'male',
          parents: [],
          children: [{ id: childId, type: 'blood' }],
          spouses: [{ id: parentId, type: 'married' }],
          siblings: [],
          data: { name: 'Untested', isUntested: true }
        },
        {
          id: childId,
          gender: genderToNodeGender(pair.child.gender),
          parents: [
            { id: pair.parent.gender === 'M' ? parentId : spouseId, type: 'blood' },
            { id: pair.parent.gender === 'F' ? parentId : spouseId, type: 'blood' }
          ],
          children: [],
          spouses: [],
          siblings: [],
          data: { name: pair.child.name, label: 'Child', isIndex: true }
        }
      ];
      
      permutations.push({
        id: 0,
        nodes,
        rootId: parentId,
        label: `PC: ${pair.parent.name.split(' ')[0]} -> ${pair.child.name.split(' ')[0]}`,
        type: 'Parent-Child',
        category: 'parentChild',
        details: `${pair.parent.name} is parent of ${pair.child.name}`
      });
    }
  }
  
  return permutations;
}

/**
 * Generate extended family permutations for N individuals using graph-driven discovery.
 * Handles multigenerational structures: grandparent-parent-child chains.
 */
export function generateGeneralizedExtendedPermutations(graph: KinshipGraph): Permutation[] {
  const permutations: Permutation[] = [];
  const { individuals, pcEdges, extEdges } = graph;
  
  if (individuals.length < 3) return permutations;
  
  // Find all valid 3-generation chains using PC edges
  // Pattern: GP -> P -> GC where GP-P and P-GC have PC edges
  const addedChains = new Set<string>();
  
  for (const grandparent of individuals) {
    for (const parent of individuals) {
      if (parent.id === grandparent.id) continue;
      if (!hasEdge(pcEdges, grandparent.id, parent.id)) continue;
      
      for (const grandchild of individuals) {
        if (grandchild.id === grandparent.id || grandchild.id === parent.id) continue;
        if (!hasEdge(pcEdges, parent.id, grandchild.id)) continue;
        
        // Valid chain found
        const chainId = `${grandparent.id}-${parent.id}-${grandchild.id}`;
        if (addedChains.has(chainId)) continue;
        addedChains.add(chainId);
        
        // Find remaining individuals for placement
        const remaining = individuals.filter(i => 
          i.id !== grandparent.id && i.id !== parent.id && i.id !== grandchild.id
        );
        
        // Try to place remaining as:
        // - Spouse of parent (co-parent of grandchild)
        // - Additional grandchildren
        // - Spouse of grandparent
        
        const prefix = `gext_${permutations.length}_`;
        
        // Find spouse candidates (opposite gender of parent with PC to any grandchild)
        const spouseCandidates = remaining.filter(r => 
          r.gender !== parent.gender && hasEdge(pcEdges, r.id, grandchild.id)
        );
        
        const spouse = spouseCandidates.length > 0 ? spouseCandidates[0] : null;
        let additionalRemaining = remaining.filter(r => r.id !== spouse?.id);
        
        // Find additional grandchildren (individuals with PC to parent or spouse)
        const additionalGrandchildren = additionalRemaining.filter(r =>
          hasEdge(pcEdges, parent.id, r.id) || (spouse && hasEdge(pcEdges, spouse.id, r.id))
        );
        
        const allGrandchildren = [grandchild, ...additionalGrandchildren];
        let unplaced = additionalRemaining.filter(r => 
          !additionalGrandchildren.some(gc => gc.id === r.id)
        );
        
        // Try to place remaining individuals with PC edges to grandchildren as co-parents
        // This handles blended families and half-sibling scenarios
        const coParentCandidates = unplaced.filter(r =>
          allGrandchildren.some(gc => hasEdge(pcEdges, r.id, gc.id))
        );
        
        // Place co-parents by adding them as spouses of untested individuals
        // who are the other parent of specific grandchildren
        unplaced = unplaced.filter(r => !coParentCandidates.some(cp => cp.id === r.id));
        
        // Build the pedigree
        const gpLabel = grandparent.gender === 'M' ? 'Grandfather' : 'Grandmother';
        const parentLabel = parent.gender === 'M' ? 'Father' : 'Mother';
        const gpSpouseId = prefix + 'gp_spouse';
        const spouseId = spouse ? createNodeId(spouse.name, prefix) : prefix + 'spouse';
        
        const nodes: PedigreeNode[] = [
          // Grandparent
          {
            id: createNodeId(grandparent.name, prefix),
            gender: genderToNodeGender(grandparent.gender),
            parents: [],
            children: [{ id: createNodeId(parent.name, prefix), type: 'blood' }],
            spouses: [{ id: gpSpouseId, type: 'married' }],
            siblings: [],
            data: { name: grandparent.name, label: gpLabel, isIndex: true }
          },
          // Grandparent's spouse (untested)
          {
            id: gpSpouseId,
            gender: grandparent.gender === 'M' ? 'female' : 'male',
            parents: [],
            children: [{ id: createNodeId(parent.name, prefix), type: 'blood' }],
            spouses: [{ id: createNodeId(grandparent.name, prefix), type: 'married' }],
            siblings: [],
            data: { name: 'Untested', isUntested: true }
          },
          // Parent
          {
            id: createNodeId(parent.name, prefix),
            gender: genderToNodeGender(parent.gender),
            parents: [
              { id: grandparent.gender === 'M' ? createNodeId(grandparent.name, prefix) : gpSpouseId, type: 'blood' },
              { id: grandparent.gender === 'F' ? createNodeId(grandparent.name, prefix) : gpSpouseId, type: 'blood' }
            ],
            children: allGrandchildren.map(gc => ({ id: createNodeId(gc.name, prefix), type: 'blood' })),
            spouses: [{ id: spouseId, type: 'married' }],
            siblings: [],
            data: { name: parent.name, label: parentLabel, isIndex: true }
          },
          // Parent's spouse
          spouse ? {
            id: spouseId,
            gender: genderToNodeGender(spouse.gender),
            parents: [],
            children: allGrandchildren.map(gc => ({ id: createNodeId(gc.name, prefix), type: 'blood' })),
            spouses: [{ id: createNodeId(parent.name, prefix), type: 'married' }],
            siblings: [],
            data: { name: spouse.name, label: spouse.gender === 'M' ? 'Father' : 'Mother', isIndex: true }
          } : {
            id: spouseId,
            gender: parent.gender === 'M' ? 'female' : 'male',
            parents: [],
            children: allGrandchildren.map(gc => ({ id: createNodeId(gc.name, prefix), type: 'blood' })),
            spouses: [{ id: createNodeId(parent.name, prefix), type: 'married' }],
            siblings: [],
            data: { name: 'Untested', isUntested: true }
          }
        ];
        
        // Add grandchildren
        // Only add sibling refs for pairs with FS support (Rule 14 compliance)
        const { fsEdges: fsSupportEdges } = graph;
        
        for (let i = 0; i < allGrandchildren.length; i++) {
          const gc = allGrandchildren[i];
          // Only include siblings that have FS support with this grandchild
          const siblingRefs = allGrandchildren
            .filter(s => s.id !== gc.id && hasEdge(fsSupportEdges, gc.id, s.id))
            .map(s => ({ id: createNodeId(s.name, prefix), type: 'blood' as const }));
          
          nodes.push({
            id: createNodeId(gc.name, prefix),
            gender: genderToNodeGender(gc.gender),
            parents: [
              { id: parent.gender === 'M' ? createNodeId(parent.name, prefix) : spouseId, type: 'blood' },
              { id: parent.gender === 'F' ? createNodeId(parent.name, prefix) : spouseId, type: 'blood' }
            ],
            children: [],
            spouses: [],
            siblings: siblingRefs,
            data: { name: gc.name, label: 'Grandchild', isIndex: true }
          });
        }
        
        // Add co-parents as additional family members with PC edges to grandchildren
        // This handles cases like half-siblings where a tested individual is parent of only some grandchildren
        for (const cp of coParentCandidates) {
          // Find which grandchildren this co-parent has PC edges to
          const coParentedChildren = allGrandchildren.filter(gc => hasEdge(pcEdges, cp.id, gc.id));
          
          if (coParentedChildren.length > 0) {
            // Add co-parent as a sibling of the main parent (making them uncle/aunt)
            // or as an additional parent figure with their own untested spouse
            const cpSpouseId = prefix + 'cp_spouse_' + cp.id;
            
            // Add co-parent node as sibling of parent (uncle/aunt relationship)
            // This is one valid interpretation of how they relate to the family
            nodes.push({
              id: createNodeId(cp.name, prefix),
              gender: genderToNodeGender(cp.gender),
              parents: [],
              children: coParentedChildren.map(gc => ({ id: createNodeId(gc.name, prefix), type: 'blood' })),
              spouses: [{ id: cpSpouseId, type: 'married' }],
              siblings: [],
              data: { name: cp.name, label: cp.gender === 'M' ? 'Father (Alt)' : 'Mother (Alt)', isIndex: true }
            });
            
            // Add untested spouse for co-parent
            nodes.push({
              id: cpSpouseId,
              gender: cp.gender === 'M' ? 'female' : 'male',
              parents: [],
              children: coParentedChildren.map(gc => ({ id: createNodeId(gc.name, prefix), type: 'blood' })),
              spouses: [{ id: createNodeId(cp.name, prefix), type: 'married' }],
              siblings: [],
              data: { name: 'Untested', isUntested: true }
            });
          }
        }
        
        // Add any unplaced individuals with connection hints
        for (const up of unplaced) {
          // Check if they have PC edge to grandparent (great-grandparent)
          if (hasEdge(pcEdges, up.id, grandparent.id)) {
            const ggpSpouseId = prefix + 'ggp_spouse';
            nodes.push({
              id: createNodeId(up.name, prefix),
              gender: genderToNodeGender(up.gender),
              parents: [],
              children: [{ id: createNodeId(grandparent.name, prefix), type: 'blood' }],
              spouses: [{ id: ggpSpouseId, type: 'married' }],
              siblings: [],
              data: { name: up.name, label: up.gender === 'M' ? 'Great-Grandfather' : 'Great-Grandmother', isIndex: true }
            });
            nodes.push({
              id: ggpSpouseId,
              gender: up.gender === 'M' ? 'female' : 'male',
              parents: [],
              children: [{ id: createNodeId(grandparent.name, prefix), type: 'blood' }],
              spouses: [{ id: createNodeId(up.name, prefix), type: 'married' }],
              siblings: [],
              data: { name: 'Untested', isUntested: true }
            });
            
            // Update grandparent's parents
            const gpNode = nodes.find(n => n.id === createNodeId(grandparent.name, prefix));
            if (gpNode) {
              gpNode.parents = [
                { id: up.gender === 'M' ? createNodeId(up.name, prefix) : ggpSpouseId, type: 'blood' },
                { id: up.gender === 'F' ? createNodeId(up.name, prefix) : ggpSpouseId, type: 'blood' }
              ];
            }
          }
        }
        
        const gpInit = grandparent.name.split(' ')[0];
        const pInit = parent.name.split(' ')[0];
        const gcInit = grandchild.name.split(' ')[0];
        
        permutations.push({
          id: 0,
          nodes,
          rootId: createNodeId(grandparent.name, prefix),
          label: `Extended: ${gpInit} -> ${pInit} -> ${gcInit}`,
          type: 'Multigenerational',
          category: 'extended',
          details: `${grandparent.name} is ${gpLabel.toLowerCase()} of ${grandchild.name}`
        });
      }
    }
  }
  
  return permutations;
}

// ============================================================================
// RULE 18: BLENDED HALF-SIBLING FAMILY (UNTESTED SHARED PARENT)
// ============================================================================

/**
 * Rule 18: Blended Half-Sibling Family with Untested Shared Parent
 * 
 * Detects and generates permutations for blended families where:
 * - Multiple tested parents (all same gender, e.g., MO1 and MO2)
 * - Each tested parent has children with PC support
 * - Children of different tested parents are half-siblings (share untested parent)
 * - The shared parent (e.g., AF) is untested
 * 
 * Example from user's diagram:
 *   MO1 (tested mother) + AF (untested father) → K1.1.1 Rudenberth Salgado
 *   AF (untested father) + MO2 (tested mother) → K2.1.1 Rosenberg Salgado, B01.1B Victim
 * 
 * Detection logic:
 * 1. Find all tested individuals of the same gender who have PC edges to children
 * 2. Verify they don't have FS or PC edges to each other (not siblings or parent-child)
 * 3. Verify children of different parents don't have FS support (half-siblings, not full)
 * 4. Generate ONE blended structure with all qualifying same-gender parents
 *    sharing a single untested parent of opposite gender
 * 
 * Key design: Only ONE permutation is generated since LR values are identical
 * regardless of which tested parent is labeled "Mother 1" vs "Mother 2".
 */
function generateBlendedHalfSiblingPermutations(graph: KinshipGraph): Permutation[] {
  const permutations: Permutation[] = [];
  const { individuals, pcEdges, fsEdges } = graph;
  
  // Find parent clusters: individuals with 1+ PC edges (potential parents)
  const parentCandidates = individuals.filter(ind => {
    const pcNeighbors = Array.from(pcEdges.get(ind.id) || []);
    return pcNeighbors.length >= 1;
  });
  
  if (parentCandidates.length < 2) return permutations;
  
  // Group parent candidates by gender
  const maleParents = parentCandidates.filter(p => p.gender === 'M');
  const femaleParents = parentCandidates.filter(p => p.gender === 'F');
  
  // Process same-gender parent groups (2+ tested parents of same gender)
  const parentGroups = [
    { parents: maleParents, sharedGender: 'female' as const, parentLabel: 'Father', sharedLabel: 'Mother' },
    { parents: femaleParents, sharedGender: 'male' as const, parentLabel: 'Mother', sharedLabel: 'Father' }
  ];
  
  for (const { parents, sharedGender, parentLabel, sharedLabel } of parentGroups) {
    if (parents.length < 2) continue;
    
    // Build list of qualifying parents: those who don't have FS/PC edges to each other
    // and whose children don't have FS edges across groups
    const qualifyingParents: TestedIndividual[] = [];
    const parentToChildren = new Map<string, TestedIndividual[]>();
    
    for (const parent of parents) {
      // Get children of this parent (individuals with PC edge to parent, excluding other parents)
      const children = individuals.filter(ind => 
        !parents.some(p => p.id === ind.id) && // Exclude other parents
        hasEdge(pcEdges, parent.id, ind.id)
      );
      
      if (children.length > 0) {
        parentToChildren.set(parent.id, children);
      }
    }
    
    // Filter to parents who have children
    const parentsWithChildren = parents.filter(p => parentToChildren.has(p.id));
    if (parentsWithChildren.length < 2) continue;
    
    // Check all pairs: parents must NOT have FS or PC edges between them
    // AND children from different parents must NOT have FS edges (they're half-siblings)
    let allValid = true;
    
    for (let i = 0; i < parentsWithChildren.length && allValid; i++) {
      for (let j = i + 1; j < parentsWithChildren.length && allValid; j++) {
        const p1 = parentsWithChildren[i];
        const p2 = parentsWithChildren[j];
        
        // Parents must not be siblings or parent-child of each other
        if (hasEdge(fsEdges, p1.id, p2.id) || hasEdge(pcEdges, p1.id, p2.id)) {
          allValid = false;
          break;
        }
        
        // Children from different parents must NOT have FS support (half-siblings, not full)
        const children1 = parentToChildren.get(p1.id) || [];
        const children2 = parentToChildren.get(p2.id) || [];
        
        const hasInterGroupFS = children1.some(c1 => 
          children2.some(c2 => hasEdge(fsEdges, c1.id, c2.id))
        );
        
        if (hasInterGroupFS) {
          allValid = false;
          break;
        }
      }
    }
    
    if (!allValid || parentsWithChildren.length < 2) continue;
    
    // Generate ONE blended family permutation with ALL qualifying parents
    const prefix = `blended_hs_${sharedGender}_`;
    const sharedParentId = prefix + 'shared_parent';
    
    // Collect all children from all parents
    const allChildren: TestedIndividual[] = [];
    for (const parent of parentsWithChildren) {
      const children = parentToChildren.get(parent.id) || [];
      for (const child of children) {
        if (!allChildren.some(c => c.id === child.id)) {
          allChildren.push(child);
        }
      }
    }
    
    const nodes: PedigreeNode[] = [];
    
    // Add the untested shared parent
    nodes.push({
      id: sharedParentId,
      gender: sharedGender,
      parents: [],
      children: allChildren.map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' })),
      spouses: parentsWithChildren.map(p => ({ id: createNodeId(p.name, prefix), type: 'married' })),
      siblings: [],
      data: { name: `Untested ${sharedLabel}`, label: sharedLabel, isUntested: true }
    });
    
    // Add tested parents
    for (let pIdx = 0; pIdx < parentsWithChildren.length; pIdx++) {
      const testedParent = parentsWithChildren[pIdx];
      const myChildren = parentToChildren.get(testedParent.id) || [];
      
      nodes.push({
        id: createNodeId(testedParent.name, prefix),
        gender: genderToNodeGender(testedParent.gender),
        parents: [],
        children: myChildren.map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' })),
        spouses: [{ id: sharedParentId, type: 'married' }],
        siblings: [],
        data: { name: testedParent.name, label: `${parentLabel} ${pIdx + 1}`, isIndex: true }
      });
    }
    
    // Add children - children from the same parent are full siblings,
    // children from different parents are half-siblings
    for (const parent of parentsWithChildren) {
      const myChildren = parentToChildren.get(parent.id) || [];
      
      for (const child of myChildren) {
        const fullSiblings = myChildren.filter(c => c.id !== child.id);
        
        nodes.push({
          id: createNodeId(child.name, prefix),
          gender: genderToNodeGender(child.gender),
          parents: [
            { id: sharedGender === 'male' ? sharedParentId : createNodeId(parent.name, prefix), type: 'blood' },
            { id: sharedGender === 'female' ? sharedParentId : createNodeId(parent.name, prefix), type: 'blood' }
          ],
          children: [],
          spouses: [],
          siblings: fullSiblings.map(s => ({ id: createNodeId(s.name, prefix), type: 'blood' })),
          data: { name: child.name, label: `Child of ${parent.name.split(' ')[0]}`, isIndex: true }
        });
      }
    }
    
    // Build description
    const parentDescriptions = parentsWithChildren.map(p => {
      const children = parentToChildren.get(p.id) || [];
      const childNames = children.map(c => c.name.split(' ')[0]).join(' & ');
      return `${p.name.split(' ')[0]}'s children: ${childNames}`;
    });
    
    const parentNames = parentsWithChildren.map(p => p.name.split(' ')[0]).join(' + ');
    
    permutations.push({
      id: 0,
      nodes,
      rootId: sharedParentId,
      label: `Blended Half-Siblings: ${parentNames} + Untested ${sharedLabel}`,
      type: 'Blended Half-Sibling Family',
      category: 'parentChild',
      details: `Half-sibling groups sharing untested ${sharedLabel}: ${parentDescriptions.join('; ')}`
    });
  }
  
  return permutations;
}

// ============================================================================
// RULE 19: HALF-SIBLING + FULL-SIBLING GROUPS (ALL UNTESTED PARENTS)
// ============================================================================

/**
 * Rule 19: Half-Sibling + Full-Sibling Groups with All Untested Parents
 * 
 * Detects and generates permutations where:
 * - All parents are UNTESTED (no tested individuals have PC edges as parents)
 * - Some tested individuals have strong FS support (full siblings)
 * - Other tested individuals have weak FS/EXT support (half-siblings)
 * 
 * Example from SF079423.2:
 *   - Rudenberth ↔ Rosenberg: Strong FS (8.2e12) - close relatives
 *   - Rudenberth ↔ Victim: Weak FS (8833) - half-sibling range
 *   - Rosenberg ↔ Victim: Weak FS/EXT (4633/5079) - extended range
 * 
 * User's expected structure:
 *   MO1 (untested) + AF (untested) → Rudenberth
 *   AF (untested) + MO2 (untested) → Rosenberg, Victim
 *   Half-sibling relationship through shared untested parent (AF)
 * 
 * Detection logic:
 * 1. Find STRONG FS clusters (using LR ratio to distinguish strong from weak)
 * 2. Look for individuals connected by weak FS/EXT to other clusters (bridge)
 * 3. Generate structures where clusters share one untested parent
 */
function generateHalfSiblingUntestedParentsPermutations(graph: KinshipGraph, kinshipRecords?: any[]): Permutation[] {
  const permutations: Permutation[] = [];
  const { individuals, fsEdges, pcEdges, extEdges } = graph;
  
  // Need at least 2 individuals to form half-sibling groups
  if (individuals.length < 2) return permutations;
  
  // Check for ACTUAL parent-child relationships (where PC LR dominates over FS LR)
  // If PC LR > FS LR for a pair, that's a true parent-child relationship
  // If FS LR > PC LR, they're likely siblings even if PC LR > 1
  let hasTestedParentChild = false;
  if (kinshipRecords) {
    for (const record of kinshipRecords) {
      const pcLr = record.parentChildLR || 0;
      const fsLr = record.fullSiblingLR || 0;
      // A true parent-child relationship has PC LR as the dominant relationship
      if (pcLr > 1 && pcLr > fsLr) {
        hasTestedParentChild = true;
        break;
      }
    }
  }
  
  // If there are true parent-child relationships among tested individuals,
  // this generator doesn't apply (Rule 18 or other generators handle those)
  if (hasTestedParentChild) return permutations;
  
  // Build a map of FS LR values between pairs
  const fsLrMap = new Map<string, number>();
  if (kinshipRecords) {
    for (const record of kinshipRecords) {
      if (record.fullSiblingLR > 1) {
        const key = [record.relative1?.id, record.relative2?.id].sort().join('|');
        fsLrMap.set(key, record.fullSiblingLR);
      }
    }
  }
  
  // Find the maximum FS LR in this case to use as threshold basis
  let maxFsLr = 0;
  for (const lr of Array.from(fsLrMap.values())) {
    if (lr > maxFsLr) maxFsLr = lr;
  }
  
  // Strong FS threshold: at least 1/1000 of the maximum FS LR
  // This separates true full siblings from half-sibling/extended relationships
  // e.g., if max is 8e12, threshold would be 8e9
  const strongFsThreshold = maxFsLr / 1000;
  
  // Build STRONG FS edges map (only includes pairs with high LR)
  const strongFsEdges = new Map<string, Set<string>>();
  for (const ind of individuals) {
    strongFsEdges.set(ind.id, new Set());
  }
  
  for (let i = 0; i < individuals.length; i++) {
    for (let j = i + 1; j < individuals.length; j++) {
      const ind1 = individuals[i];
      const ind2 = individuals[j];
      const key = [ind1.id, ind2.id].sort().join('|');
      const lr = fsLrMap.get(key) || 0;
      
      if (lr >= strongFsThreshold) {
        strongFsEdges.get(ind1.id)!.add(ind2.id);
        strongFsEdges.get(ind2.id)!.add(ind1.id);
      }
    }
  }
  
  // Cluster by STRONG FS edges only
  const fsClusters = clusterByFsEdges(individuals, strongFsEdges);
  
  // Find all weak connections (FS or EXT with LR > 1 but below strong threshold)
  const weakConnections: Array<{
    ind1: TestedIndividual;
    ind2: TestedIndividual;
    type: 'FS' | 'EXT';
  }> = [];
  
  for (let i = 0; i < individuals.length; i++) {
    for (let j = i + 1; j < individuals.length; j++) {
      const ind1 = individuals[i];
      const ind2 = individuals[j];
      
      // Check if they're in different FS clusters
      const cluster1 = fsClusters.find(c => c.some(m => m.id === ind1.id));
      const cluster2 = fsClusters.find(c => c.some(m => m.id === ind2.id));
      
      if (cluster1 !== cluster2) {
        // They're in different clusters - check for weak FS or EXT connection
        if (hasEdge(fsEdges, ind1.id, ind2.id)) {
          weakConnections.push({ ind1, ind2, type: 'FS' });
        } else if (hasEdge(extEdges, ind1.id, ind2.id)) {
          weakConnections.push({ ind1, ind2, type: 'EXT' });
        }
      }
    }
  }
  
  // If we have multiple clusters connected by weak connections,
  // generate half-sibling structures
  if (fsClusters.length >= 2 && weakConnections.length > 0) {
    // Try to merge clusters through shared parent
    // For each pair of connected clusters, create a half-sibling permutation
    
    const processedClusterPairs = new Set<string>();
    
    for (const conn of weakConnections) {
      const cluster1 = fsClusters.find(c => c.some(m => m.id === conn.ind1.id))!;
      const cluster2 = fsClusters.find(c => c.some(m => m.id === conn.ind2.id))!;
      
      // Create unique key for this cluster pair
      const clusterKey = [
        cluster1.map(m => m.id).sort().join(','),
        cluster2.map(m => m.id).sort().join(',')
      ].sort().join('|');
      
      if (processedClusterPairs.has(clusterKey)) continue;
      processedClusterPairs.add(clusterKey);
      
      // Generate half-sibling structure
      const prefix = `hs_untested_${permutations.length}_`;
      
      // Shared untested parent (the one both groups share)
      const sharedFatherId = prefix + 'shared_father';
      
      // Different mothers for each cluster
      const mother1Id = prefix + 'mother1';
      const mother2Id = prefix + 'mother2';
      
      const allIndividuals = [...cluster1, ...cluster2];
      const nodes: PedigreeNode[] = [];
      
      // Add shared father (untested)
      nodes.push({
        id: sharedFatherId,
        gender: 'male',
        parents: [],
        children: allIndividuals.map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' })),
        spouses: [
          { id: mother1Id, type: 'married' },
          { id: mother2Id, type: 'married' }
        ],
        siblings: [],
        data: { name: 'Untested Father', label: 'Father', isUntested: true }
      });
      
      // Add mother 1 (untested)
      nodes.push({
        id: mother1Id,
        gender: 'female',
        parents: [],
        children: cluster1.map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' })),
        spouses: [{ id: sharedFatherId, type: 'married' }],
        siblings: [],
        data: { name: 'Untested Mother 1', label: 'Mother 1', isUntested: true }
      });
      
      // Add mother 2 (untested)
      nodes.push({
        id: mother2Id,
        gender: 'female',
        parents: [],
        children: cluster2.map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' })),
        spouses: [{ id: sharedFatherId, type: 'married' }],
        siblings: [],
        data: { name: 'Untested Mother 2', label: 'Mother 2', isUntested: true }
      });
      
      // Add cluster 1 children (full siblings with each other)
      for (const child of cluster1) {
        const fullSiblings = cluster1.filter(c => c.id !== child.id);
        
        nodes.push({
          id: createNodeId(child.name, prefix),
          gender: genderToNodeGender(child.gender),
          parents: [
            { id: sharedFatherId, type: 'blood' },
            { id: mother1Id, type: 'blood' }
          ],
          children: [],
          spouses: [],
          siblings: fullSiblings.map(s => ({ id: createNodeId(s.name, prefix), type: 'blood' })),
          data: { name: child.name, label: 'Child (Group 1)', isIndex: true }
        });
      }
      
      // Add cluster 2 children (full siblings with each other)
      for (const child of cluster2) {
        const fullSiblings = cluster2.filter(c => c.id !== child.id);
        
        nodes.push({
          id: createNodeId(child.name, prefix),
          gender: genderToNodeGender(child.gender),
          parents: [
            { id: sharedFatherId, type: 'blood' },
            { id: mother2Id, type: 'blood' }
          ],
          children: [],
          spouses: [],
          siblings: fullSiblings.map(s => ({ id: createNodeId(s.name, prefix), type: 'blood' })),
          data: { name: child.name, label: 'Child (Group 2)', isIndex: true }
        });
      }
      
      const cluster1Names = cluster1.map(c => c.name.split(' ')[0]).join(' & ');
      const cluster2Names = cluster2.map(c => c.name.split(' ')[0]).join(' & ');
      
      permutations.push({
        id: 0,
        nodes,
        rootId: sharedFatherId,
        label: `Half-Siblings: (${cluster1Names}) + (${cluster2Names})`,
        type: 'Half-Sibling Groups (Untested Parents)',
        category: 'fullSibling',
        details: `Group 1 (${cluster1Names}) and Group 2 (${cluster2Names}) are half-siblings sharing an untested father`
      });
      
      // Also generate the mirror structure (shared mother instead of shared father)
      const prefix2 = `hs_untested_mother_${permutations.length}_`;
      const sharedMotherId = prefix2 + 'shared_mother';
      const father1Id = prefix2 + 'father1';
      const father2Id = prefix2 + 'father2';
      
      const nodes2: PedigreeNode[] = [];
      
      // Add shared mother (untested)
      nodes2.push({
        id: sharedMotherId,
        gender: 'female',
        parents: [],
        children: allIndividuals.map(c => ({ id: createNodeId(c.name, prefix2), type: 'blood' })),
        spouses: [
          { id: father1Id, type: 'married' },
          { id: father2Id, type: 'married' }
        ],
        siblings: [],
        data: { name: 'Untested Mother', label: 'Mother', isUntested: true }
      });
      
      // Add father 1 (untested)
      nodes2.push({
        id: father1Id,
        gender: 'male',
        parents: [],
        children: cluster1.map(c => ({ id: createNodeId(c.name, prefix2), type: 'blood' })),
        spouses: [{ id: sharedMotherId, type: 'married' }],
        siblings: [],
        data: { name: 'Untested Father 1', label: 'Father 1', isUntested: true }
      });
      
      // Add father 2 (untested)
      nodes2.push({
        id: father2Id,
        gender: 'male',
        parents: [],
        children: cluster2.map(c => ({ id: createNodeId(c.name, prefix2), type: 'blood' })),
        spouses: [{ id: sharedMotherId, type: 'married' }],
        siblings: [],
        data: { name: 'Untested Father 2', label: 'Father 2', isUntested: true }
      });
      
      // Add cluster 1 children
      for (const child of cluster1) {
        const fullSiblings = cluster1.filter(c => c.id !== child.id);
        
        nodes2.push({
          id: createNodeId(child.name, prefix2),
          gender: genderToNodeGender(child.gender),
          parents: [
            { id: father1Id, type: 'blood' },
            { id: sharedMotherId, type: 'blood' }
          ],
          children: [],
          spouses: [],
          siblings: fullSiblings.map(s => ({ id: createNodeId(s.name, prefix2), type: 'blood' })),
          data: { name: child.name, label: 'Child (Group 1)', isIndex: true }
        });
      }
      
      // Add cluster 2 children
      for (const child of cluster2) {
        const fullSiblings = cluster2.filter(c => c.id !== child.id);
        
        nodes2.push({
          id: createNodeId(child.name, prefix2),
          gender: genderToNodeGender(child.gender),
          parents: [
            { id: father2Id, type: 'blood' },
            { id: sharedMotherId, type: 'blood' }
          ],
          children: [],
          spouses: [],
          siblings: fullSiblings.map(s => ({ id: createNodeId(s.name, prefix2), type: 'blood' })),
          data: { name: child.name, label: 'Child (Group 2)', isIndex: true }
        });
      }
      
      permutations.push({
        id: 0,
        nodes: nodes2,
        rootId: sharedMotherId,
        label: `Half-Siblings: (${cluster1Names}) + (${cluster2Names})`,
        type: 'Half-Sibling Groups (Untested Parents)',
        category: 'fullSibling',
        details: `Group 1 (${cluster1Names}) and Group 2 (${cluster2Names}) are half-siblings sharing an untested mother`
      });
    }
  }
  
  return permutations;
}

// ============================================================================
// RULE 16: CHILD ORDER DEDUPLICATION
// ============================================================================

/**
 * Rule 16: Child Order Deduplication
 * 
 * When multiple children share the same parent(s), the order of children
 * doesn't matter for pedigree reconstruction. Permutations that only differ
 * by which child is designated as the "primary" endpoint of a chain should
 * be collapsed into a single canonical representation.
 * 
 * Example: "Jane -> Kate -> Jonathan" and "Jane -> Kate -> Zoe" represent
 * the SAME family structure (Jane is grandmother of both through Kate).
 * We keep only one, using alphabetically sorted children.
 */
function computeCanonicalStructureSignature(perm: Permutation): string {
  const nodes = perm.nodes;
  
  // Build parent-to-children map for tested individuals only
  const parentToChildren = new Map<string, string[]>();
  const testedNames = new Set<string>();
  
  for (const node of nodes) {
    if (!node.data?.isUntested && node.data?.name) {
      testedNames.add(node.data.name);
    }
  }
  
  // Find all parent-child relationships
  for (const node of nodes) {
    if (node.data?.isUntested) continue;
    const nodeName = node.data?.name;
    if (!nodeName) continue;
    
    // Collect tested children of this node
    const testedChildren: string[] = [];
    for (const childRef of node.children) {
      const childNode = nodes.find(n => n.id === childRef.id);
      if (childNode && !childNode.data?.isUntested && childNode.data?.name) {
        if (testedNames.has(childNode.data.name)) {
          testedChildren.push(childNode.data.name);
        }
      }
    }
    
    if (testedChildren.length > 0) {
      // Sort children alphabetically for canonical ordering
      testedChildren.sort();
      parentToChildren.set(nodeName, testedChildren);
    }
  }
  
  // Build signature: type + category + sorted parent->children mappings
  const parentChildPairs: string[] = [];
  const sortedParents = Array.from(parentToChildren.keys()).sort();
  
  for (const parent of sortedParents) {
    const children = parentToChildren.get(parent)!;
    parentChildPairs.push(`${parent}->[${children.join(',')}]`);
  }
  
  // Include grandparent relationships in signature
  const ancestorPairs: string[] = [];
  for (const node of nodes) {
    if (node.data?.isUntested) continue;
    const nodeName = node.data?.name;
    if (!nodeName) continue;
    
    // Find grandparents (parents of parents)
    for (const parentRef of node.parents) {
      const parentNode = nodes.find(n => n.id === parentRef.id);
      if (!parentNode) continue;
      
      for (const gpRef of parentNode.parents) {
        const gpNode = nodes.find(n => n.id === gpRef.id);
        if (gpNode && !gpNode.data?.isUntested && gpNode.data?.name) {
          if (testedNames.has(gpNode.data.name)) {
            ancestorPairs.push(`GP:${gpNode.data.name}->${nodeName}`);
          }
        }
      }
    }
  }
  ancestorPairs.sort();
  
  // Combine into signature
  const signature = [
    perm.type,
    perm.category,
    ...parentChildPairs,
    ...ancestorPairs,
    // Include all tested individuals to ensure completeness
    Array.from(testedNames).sort().join(',')
  ].join('|');
  
  return signature;
}

/**
 * Deduplicate permutations that differ only by child ordering.
 * Keeps the first occurrence of each unique canonical structure.
 */
function deduplicateByChildOrder(permutations: Permutation[]): Permutation[] {
  const seen = new Map<string, Permutation>();
  
  for (const perm of permutations) {
    const signature = computeCanonicalStructureSignature(perm);
    
    if (!seen.has(signature)) {
      seen.set(signature, perm);
    }
  }
  
  return Array.from(seen.values());
}

/**
 * RULE 27: Structural Deduplication
 * 
 * Merges permutations that are structurally identical (same sibling groups,
 * same parent-sharing patterns) even if they have different labels/types.
 * 
 * Structural identity is determined by:
 * 1. Which tested individuals share the same set of parents (sibling groups)
 * 2. Whether individuals are half-siblings vs full siblings (different vs same parents)
 * 3. For disjointed structures: includes nodesB structure as well
 * 
 * When multiple permutations have identical structure AND same score,
 * only one representative is kept (preferring simpler/clearer labels).
 */
function computeTreeFingerprint(nodes: PedigreeNode[]): string {
  // Build a map of node ID to node for easy lookup
  const nodeById = new Map<string, PedigreeNode>();
  for (const node of nodes) {
    nodeById.set(node.id, node);
  }
  
  // Track tested individuals and their roles
  const testedNames: string[] = [];
  const testedParents: string[] = [];  // Tested individuals who are parents
  const parentChildPairs: string[] = [];  // Direct PC relationships between tested individuals
  
  // Build parent set for each tested individual
  const testedParentSets: { name: string; parentIds: string[]; testedParentNames: string[] }[] = [];
  
  for (const node of nodes) {
    if (node.data?.isUntested) continue;
    const name = node.data?.name || node.id;
    testedNames.push(name);
    
    const parentIds = node.parents.map(p => p.id).sort();
    
    // Check if any parents are tested
    const testedParentNames: string[] = [];
    for (const parentRef of node.parents) {
      const parentNode = nodeById.get(parentRef.id);
      if (parentNode && !parentNode.data?.isUntested && parentNode.data?.name) {
        testedParentNames.push(parentNode.data.name);
        parentChildPairs.push(`${parentNode.data.name}->${name}`);
        if (!testedParents.includes(parentNode.data.name)) {
          testedParents.push(parentNode.data.name);
        }
      }
    }
    
    testedParentSets.push({ name, parentIds, testedParentNames });
  }
  
  // Sort by name for consistency
  testedParentSets.sort((a, b) => a.name.localeCompare(b.name));
  
  // Create sibling groups: individuals sharing exact same parent set
  const parentSetToMembers = new Map<string, string[]>();
  for (const { name, parentIds } of testedParentSets) {
    const key = parentIds.join('+');
    if (!parentSetToMembers.has(key)) {
      parentSetToMembers.set(key, []);
    }
    parentSetToMembers.get(key)!.push(name);
  }
  
  // Create normalized sibling group representation
  const siblingGroups: string[] = [];
  let parentSetId = 1;
  const parentSetMap = new Map<string, number>();
  
  for (const [parentKey, members] of parentSetToMembers) {
    if (!parentSetMap.has(parentKey)) {
      parentSetMap.set(parentKey, parentSetId++);
    }
    const id = parentSetMap.get(parentKey)!;
    siblingGroups.push(`P${id}:[${members.sort().join(',')}]`);
  }
  
  // Combine into fingerprint:
  // 1. Tested individuals list (sorted)
  // 2. Sibling groups
  // 3. Parent-child pairs (tested parents to tested children)
  return [
    `T:[${testedNames.sort().join(',')}]`,
    siblingGroups.sort().join('|'),
    `PC:[${parentChildPairs.sort().join(',')}]`
  ].join('::');
}

function computeStructuralFingerprint(perm: Permutation): string {
  // Compute fingerprint for main nodes
  const mainFingerprint = computeTreeFingerprint(perm.nodes);
  
  // Include CATEGORY (not type) in fingerprint
  // Different categories represent fundamentally different interpretations:
  // - 'parentChild' focuses on PC relationships (Trio)
  // - 'fullSibling' focuses on FS relationships (Full Siblings)
  // Same category + same structure + same score = redundant, should merge
  // Different category = different insight, keep separate
  const categoryPrefix = `cat:${perm.category}::`;
  
  // If there's nodesB (disjointed structure), include it in the fingerprint
  if (perm.nodesB && perm.nodesB.length > 0) {
    const nodesBFingerprint = computeTreeFingerprint(perm.nodesB);
    return `${categoryPrefix}A:${mainFingerprint}||B:${nodesBFingerprint}`;
  }
  
  return `${categoryPrefix}${mainFingerprint}`;
}

/**
 * Deduplicate permutations with identical structure and same score.
 * Rule 27: Reduces clutter by merging redundant pedigree representations.
 */
function deduplicateByStructure(permutations: Permutation[]): Permutation[] {
  // Group by fingerprint + score
  const groups = new Map<string, Permutation[]>();
  
  for (const perm of permutations) {
    const fingerprint = computeStructuralFingerprint(perm);
    const score = perm.likelihoodScore?.compositeScore?.toFixed(2) ?? '0';
    const key = `${fingerprint}::score:${score}`;
    
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key)!.push(perm);
  }
  
  // From each group, keep only the best representative
  const result: Permutation[] = [];
  
  for (const [_key, perms] of groups) {
    if (perms.length === 1) {
      result.push(perms[0]);
    } else {
      // Multiple permutations with same structure and score
      // Prefer simpler/clearer types
      const typePreference: Record<string, number> = {
        'Full Siblings': 1,
        'Parent/Child': 2,
        'Half-Sibling Family': 3,
        'Half-Sibling Groups (Untested Parents)': 4,
        'Complex Nuclear Half-Sibling Family': 5,
        'Flexible Linear': 6,
      };
      
      perms.sort((a, b) => {
        const prefA = typePreference[a.type] ?? 100;
        const prefB = typePreference[b.type] ?? 100;
        return prefA - prefB;
      });
      
      result.push(perms[0]);
    }
  }
  
  return result;
}

// ============================================================================
// RULE 25: COMPLEX NUCLEAR HALF-SIBLING FAMILY GENERATOR
// ============================================================================

/**
 * Rule 25: Complex Nuclear Half-Sibling Family Generator
 * 
 * Generates complex nuclear family structures with:
 * - One shared untested parent (father or mother) as parent of ALL tested individuals
 * - Multiple untested parents of opposite gender (one per FS cluster, separate for singletons)
 * 
 * Detection criteria:
 * 1. At least one FS cluster (2+ individuals with FS LR > 1)
 * 2. Remaining individuals connected via EXT to at least one FS cluster member
 * 3. No PC relationships between tested individuals (all parents untested)
 * 
 * Example case 2024-071746:
 * - SB.2 + SB.5 have FS (full siblings through shared parents)
 * - SB.1, SB.3, SB.4 have EXT connections to SB.1 (half-siblings through shared father)
 * - Result: Untested Father → all 5; Untested Mother 1 → SB.2+SB.5; 
 *           Untested Mother 2 → SB.1; Untested Mother 3 → SB.4; Untested Mother 4 → SB.3
 */
function generateComplexNuclearHalfSiblingPermutations(
  graph: KinshipGraph, 
  kinshipPairs: KinshipPair[]
): Permutation[] {
  const permutations: Permutation[] = [];
  const { individuals, fsEdges, pcEdges, extEdges } = graph;
  
  const DEBUG = false; // Set to true for debugging
  
  if (DEBUG) console.log('[CNHS] Starting Complex Nuclear Half-Sibling generator');
  if (DEBUG) console.log('[CNHS] Individuals:', individuals.map(i => i.id));
  
  if (individuals.length < 3) {
    if (DEBUG) console.log('[CNHS] Exit: less than 3 individuals');
    return permutations;
  }
  
  // Step 1: Check that there are NO tested parent-child relationships
  // If there are PC relationships, this generator doesn't apply
  let hasTestedPC = false;
  for (const pair of kinshipPairs) {
    const pcLr = pair.parentChildLR || 0;
    const fsLr = pair.fullSiblingLR || 0;
    // A true parent-child relationship has PC LR as dominant (PC > FS and PC > 1)
    if (pcLr > 1 && pcLr > fsLr) {
      hasTestedPC = true;
      if (DEBUG) console.log('[CNHS] Found tested PC:', pair.relative1.id, '↔', pair.relative2.id);
      break;
    }
  }
  
  if (hasTestedPC) {
    if (DEBUG) console.log('[CNHS] Exit: has tested PC relationships');
    return permutations;
  }
  
  // Step 2: Find FS clusters (connected components with strong FS LR > 1)
  const fsClusters = clusterByFsEdges(individuals, fsEdges);
  
  if (DEBUG) console.log('[CNHS] FS Clusters:', fsClusters.map(c => c.map(i => i.id)));
  
  // Separate clusters with 2+ members (true FS groups) from singletons
  const fsGroups: TestedIndividual[][] = [];
  const singletons: TestedIndividual[] = [];
  
  for (const cluster of fsClusters) {
    if (cluster.length >= 2) {
      fsGroups.push(cluster);
    } else {
      singletons.push(cluster[0]);
    }
  }
  
  if (DEBUG) console.log('[CNHS] FS Groups:', fsGroups.map(g => g.map(i => i.id)));
  if (DEBUG) console.log('[CNHS] Singletons:', singletons.map(s => s.id));
  
  // Must have at least one FS cluster OR have singletons connected via EXT
  if (fsGroups.length === 0 && singletons.length < 2) {
    if (DEBUG) console.log('[CNHS] Exit: no FS groups and less than 2 singletons');
    return permutations;
  }
  
  // Step 3: Verify singletons have EXT connections to at least one FS group member
  // or to other singletons (indicating extended family relationship)
  const fsGroupMembers = new Set<string>();
  for (const group of fsGroups) {
    for (const member of group) {
      fsGroupMembers.add(member.id);
    }
  }
  
  if (DEBUG) console.log('[CNHS] FS Group Members:', Array.from(fsGroupMembers));
  if (DEBUG) console.log('[CNHS] extEdges keys:', Array.from(extEdges.keys()));
  
  // Check that each singleton has at least one EXT connection
  for (const singleton of singletons) {
    let hasConnection = false;
    
    if (DEBUG) console.log('[CNHS] Checking singleton:', singleton.id);
    if (DEBUG) console.log('[CNHS]   extEdges for singleton:', extEdges.get(singleton.id));
    
    // Check EXT connections to FS group members
    for (const memberId of fsGroupMembers) {
      if (hasEdge(extEdges, singleton.id, memberId)) {
        if (DEBUG) console.log('[CNHS]   Has EXT to FS member:', memberId);
        hasConnection = true;
        break;
      }
    }
    
    // Check EXT connections to other singletons
    if (!hasConnection) {
      for (const other of singletons) {
        if (other.id !== singleton.id && hasEdge(extEdges, singleton.id, other.id)) {
          if (DEBUG) console.log('[CNHS]   Has EXT to singleton:', other.id);
          hasConnection = true;
          break;
        }
      }
    }
    
    // Check FS connections (weak FS that didn't cluster)
    if (!hasConnection) {
      for (const memberId of fsGroupMembers) {
        if (hasEdge(fsEdges, singleton.id, memberId)) {
          if (DEBUG) console.log('[CNHS]   Has FS to member:', memberId);
          hasConnection = true;
          break;
        }
      }
    }
    
    if (!hasConnection) {
      if (DEBUG) console.log('[CNHS] Exit: singleton', singleton.id, 'has no connection');
      // This singleton has no relationship to anyone, skip this generator
      return permutations;
    }
  }
  
  if (DEBUG) console.log('[CNHS] All singletons connected, proceeding to generate permutations');
  
  // Step 4: Generate permutation with shared father + multiple mothers
  const prefix = 'cnhs_father_';
  const sharedFatherId = prefix + 'shared_father';
  const nodes: PedigreeNode[] = [];
  
  // All children for the shared father
  const allChildren: { id: string; type: string }[] = [];
  
  // Track mother assignments
  const motherNodes: PedigreeNode[] = [];
  const childToMotherId = new Map<string, string>();
  let motherCount = 0;
  
  // Assign mothers to FS groups (each group shares a mother)
  for (const group of fsGroups) {
    motherCount++;
    const motherId = prefix + 'mother_' + motherCount;
    
    for (const child of group) {
      childToMotherId.set(child.id, motherId);
      allChildren.push({ id: createNodeId(child.name, prefix), type: 'blood' });
    }
    
    motherNodes.push({
      id: motherId,
      gender: 'female',
      parents: [],
      children: group.map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' })),
      spouses: [{ id: sharedFatherId, type: 'married' }],
      siblings: [],
      data: { 
        name: `Untested Mother ${motherCount}`, 
        label: `Mother ${motherCount}`, 
        isUntested: true 
      }
    });
  }
  
  // Assign separate mothers to singletons
  for (const singleton of singletons) {
    motherCount++;
    const motherId = prefix + 'mother_' + motherCount;
    
    childToMotherId.set(singleton.id, motherId);
    allChildren.push({ id: createNodeId(singleton.name, prefix), type: 'blood' });
    
    motherNodes.push({
      id: motherId,
      gender: 'female',
      parents: [],
      children: [{ id: createNodeId(singleton.name, prefix), type: 'blood' }],
      spouses: [{ id: sharedFatherId, type: 'married' }],
      siblings: [],
      data: { 
        name: `Untested Mother ${motherCount}`, 
        label: `Mother ${motherCount}`, 
        isUntested: true 
      }
    });
  }
  
  // Add shared father (untested)
  nodes.push({
    id: sharedFatherId,
    gender: 'male',
    parents: [],
    children: allChildren,
    spouses: motherNodes.map(m => ({ id: m.id, type: 'married' })),
    siblings: [],
    data: { name: 'Untested Father', label: 'Father', isUntested: true }
  });
  
  // Add all mothers
  nodes.push(...motherNodes);
  
  // Add all children
  for (const group of fsGroups) {
    for (const child of group) {
      const motherId = childToMotherId.get(child.id)!;
      const fullSiblings = group.filter(c => c.id !== child.id);
      
      nodes.push({
        id: createNodeId(child.name, prefix),
        gender: genderToNodeGender(child.gender),
        parents: [
          { id: sharedFatherId, type: 'blood' },
          { id: motherId, type: 'blood' }
        ],
        children: [],
        spouses: [],
        siblings: fullSiblings.map(s => ({ id: createNodeId(s.name, prefix), type: 'blood' })),
        data: { name: child.name, label: 'Child (FS Group)', isIndex: true, kinshipId: child.id }
      });
    }
  }
  
  for (const singleton of singletons) {
    const motherId = childToMotherId.get(singleton.id)!;
    
    nodes.push({
      id: createNodeId(singleton.name, prefix),
      gender: genderToNodeGender(singleton.gender),
      parents: [
        { id: sharedFatherId, type: 'blood' },
        { id: motherId, type: 'blood' }
      ],
      children: [],
      spouses: [],
      siblings: [],
      data: { name: singleton.name, label: 'Child (Half-Sibling)', isIndex: true, kinshipId: singleton.id }
    });
  }
  
  // Build label
  const fsGroupNames = fsGroups.map(g => 
    g.map(c => c.name.split(' ')[0]).join('+')
  ).join(', ');
  const singletonNames = singletons.map(s => s.name.split(' ')[0]).join(', ');
  
  const labelParts = [];
  if (fsGroups.length > 0) labelParts.push(`FS: ${fsGroupNames}`);
  if (singletons.length > 0) labelParts.push(`Half-Siblings: ${singletonNames}`);
  
  permutations.push({
    id: 0,
    nodes,
    rootId: sharedFatherId,
    label: `Complex Nuclear: Shared Father (${labelParts.join(' + ')})`,
    type: 'Complex Nuclear Half-Sibling Family',
    category: 'fullSibling',
    details: `All individuals share an untested father; FS groups share mothers, half-siblings have separate mothers`
  });
  
  // Step 5: Generate mirror structure (shared mother + multiple fathers)
  const prefix2 = 'cnhs_mother_';
  const sharedMotherId = prefix2 + 'shared_mother';
  const nodes2: PedigreeNode[] = [];
  
  const allChildren2: { id: string; type: string }[] = [];
  const fatherNodes: PedigreeNode[] = [];
  const childToFatherId = new Map<string, string>();
  let fatherCount = 0;
  
  // Assign fathers to FS groups
  for (const group of fsGroups) {
    fatherCount++;
    const fatherId = prefix2 + 'father_' + fatherCount;
    
    for (const child of group) {
      childToFatherId.set(child.id, fatherId);
      allChildren2.push({ id: createNodeId(child.name, prefix2), type: 'blood' });
    }
    
    fatherNodes.push({
      id: fatherId,
      gender: 'male',
      parents: [],
      children: group.map(c => ({ id: createNodeId(c.name, prefix2), type: 'blood' })),
      spouses: [{ id: sharedMotherId, type: 'married' }],
      siblings: [],
      data: { 
        name: `Untested Father ${fatherCount}`, 
        label: `Father ${fatherCount}`, 
        isUntested: true 
      }
    });
  }
  
  // Assign separate fathers to singletons
  for (const singleton of singletons) {
    fatherCount++;
    const fatherId = prefix2 + 'father_' + fatherCount;
    
    childToFatherId.set(singleton.id, fatherId);
    allChildren2.push({ id: createNodeId(singleton.name, prefix2), type: 'blood' });
    
    fatherNodes.push({
      id: fatherId,
      gender: 'male',
      parents: [],
      children: [{ id: createNodeId(singleton.name, prefix2), type: 'blood' }],
      spouses: [{ id: sharedMotherId, type: 'married' }],
      siblings: [],
      data: { 
        name: `Untested Father ${fatherCount}`, 
        label: `Father ${fatherCount}`, 
        isUntested: true 
      }
    });
  }
  
  // Add shared mother (untested)
  nodes2.push({
    id: sharedMotherId,
    gender: 'female',
    parents: [],
    children: allChildren2,
    spouses: fatherNodes.map(f => ({ id: f.id, type: 'married' })),
    siblings: [],
    data: { name: 'Untested Mother', label: 'Mother', isUntested: true }
  });
  
  // Add all fathers
  nodes2.push(...fatherNodes);
  
  // Add all children
  for (const group of fsGroups) {
    for (const child of group) {
      const fatherId = childToFatherId.get(child.id)!;
      const fullSiblings = group.filter(c => c.id !== child.id);
      
      nodes2.push({
        id: createNodeId(child.name, prefix2),
        gender: genderToNodeGender(child.gender),
        parents: [
          { id: fatherId, type: 'blood' },
          { id: sharedMotherId, type: 'blood' }
        ],
        children: [],
        spouses: [],
        siblings: fullSiblings.map(s => ({ id: createNodeId(s.name, prefix2), type: 'blood' })),
        data: { name: child.name, label: 'Child (FS Group)', isIndex: true, kinshipId: child.id }
      });
    }
  }
  
  for (const singleton of singletons) {
    const fatherId = childToFatherId.get(singleton.id)!;
    
    nodes2.push({
      id: createNodeId(singleton.name, prefix2),
      gender: genderToNodeGender(singleton.gender),
      parents: [
        { id: fatherId, type: 'blood' },
        { id: sharedMotherId, type: 'blood' }
      ],
      children: [],
      spouses: [],
      siblings: [],
      data: { name: singleton.name, label: 'Child (Half-Sibling)', isIndex: true, kinshipId: singleton.id }
    });
  }
  
  permutations.push({
    id: 0,
    nodes: nodes2,
    rootId: sharedMotherId,
    label: `Complex Nuclear: Shared Mother (${labelParts.join(' + ')})`,
    type: 'Complex Nuclear Half-Sibling Family',
    category: 'fullSibling',
    details: `All individuals share an untested mother; FS groups share fathers, half-siblings have separate fathers`
  });
  
  if (DEBUG) console.log('[CNHS] Generated', permutations.length, 'permutations');
  if (DEBUG) permutations.forEach(p => console.log('[CNHS]  -', p.label, '(', p.type, ')'));
  
  return permutations;
}

// ============================================================================
// RULE 20: UNIVERSAL EXTENDED FAMILY GENERATOR
// ============================================================================

/**
 * Rule 20: Universal Extended Family Generator
 * 
 * This is a fallback generator for cases where specialized generators fail.
 * It handles complex scenarios like:
 * - All relationships are extended family (no PC or FS)
 * - Some individuals have no direct kinship to each other
 * - Complex multi-generation structures
 * 
 * Approach (inspired by PRIMUS/SEQUOIA algorithms):
 * 1. Identify clusters of individuals with strong relationships (FS/PC)
 * 2. Connect clusters via extended family relationships
 * 3. Generate all feasible pedigree topologies
 * 4. Score and rank by composite LR
 */
function generateUniversalExtendedPermutations(graph: KinshipGraph, kinshipPairs: KinshipPair[]): Permutation[] {
  const permutations: Permutation[] = [];
  const { individuals, fsEdges, pcEdges, extEdges } = graph;
  
  if (individuals.length < 2) return permutations;
  
  // Step 1: Find clusters of closely related individuals (FS or strong PC)
  const clusters: TestedIndividual[][] = [];
  const assigned = new Set<string>();
  
  // Build strong relationship graph (FS or PC where PC > FS)
  const strongEdges = new Map<string, Set<string>>();
  for (const ind of individuals) {
    strongEdges.set(ind.id, new Set());
  }
  
  for (const pair of kinshipPairs) {
    const id1 = pair.relative1.id;
    const id2 = pair.relative2.id;
    const pcLr = pair.parentChildLR || 0;
    const fsLr = pair.fullSiblingLR || 0;
    
    // Strong relationship: FS > 1000 or PC > 1000
    if (fsLr > 1000 || pcLr > 1000) {
      if (strongEdges.has(id1)) strongEdges.get(id1)!.add(id2);
      if (strongEdges.has(id2)) strongEdges.get(id2)!.add(id1);
    }
  }
  
  // Cluster by strong edges using BFS
  for (const ind of individuals) {
    if (assigned.has(ind.id)) continue;
    
    const cluster: TestedIndividual[] = [];
    const queue = [ind];
    
    while (queue.length > 0) {
      const current = queue.shift()!;
      if (assigned.has(current.id)) continue;
      
      assigned.add(current.id);
      cluster.push(current);
      
      const neighbors = strongEdges.get(current.id) || new Set();
      for (const neighborId of Array.from(neighbors)) {
        if (!assigned.has(neighborId)) {
          const neighbor = individuals.find(i => i.id === neighborId);
          if (neighbor) queue.push(neighbor);
        }
      }
    }
    
    if (cluster.length > 0) {
      clusters.push(cluster);
    }
  }
  
  // Step 2: Generate permutations based on cluster structure
  if (clusters.length === 1) {
    // All individuals in one cluster - they're all closely related
    // Generate sibling/parent-child structures
    const cluster = clusters[0];
    permutations.push(...generateClusterPermutation(cluster, 'nuclear', 0));
  } else if (clusters.length >= 2) {
    // Multiple clusters - generate extended family structures
    // Connect clusters via untested common ancestors
    
    // Generate permutations connecting each cluster pair
    for (let i = 0; i < clusters.length; i++) {
      for (let j = i + 1; j < clusters.length; j++) {
        // Check if clusters have any extended connection
        let hasConnection = false;
        for (const ind1 of clusters[i]) {
          for (const ind2 of clusters[j]) {
            if (hasEdge(extEdges, ind1.id, ind2.id) || 
                hasEdge(fsEdges, ind1.id, ind2.id) ||
                hasEdge(pcEdges, ind1.id, ind2.id)) {
              hasConnection = true;
              break;
            }
          }
          if (hasConnection) break;
        }
        
        if (hasConnection) {
          // Generate connected structure
          const allInds = [...clusters[i], ...clusters[j]];
          permutations.push(...generateConnectedClustersPermutation(
            clusters[i], clusters[j], extEdges, permutations.length
          ));
        }
      }
    }
    
    // Also generate a single structure with all individuals
    // where clusters are connected through untested common ancestors
    const allInds = clusters.flat();
    permutations.push(...generateMultiClusterPermutation(clusters, graph, permutations.length));
  }
  
  return permutations;
}

/**
 * Generate permutation for a single closely-related cluster
 */
function generateClusterPermutation(
  cluster: TestedIndividual[],
  type: string,
  startIdx: number
): Permutation[] {
  const permutations: Permutation[] = [];
  const prefix = `cluster_${startIdx}_`;
  
  // Check gender composition
  const males = cluster.filter(i => i.gender === 'M');
  const females = cluster.filter(i => i.gender === 'F');
  
  // Generate as siblings under untested parents
  const fatherId = prefix + 'father';
  const motherId = prefix + 'mother';
  
  const nodes: PedigreeNode[] = [];
  
  // Add untested parents
  nodes.push({
    id: fatherId,
    gender: 'male',
    parents: [],
    children: cluster.map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' })),
    spouses: [{ id: motherId, type: 'married' }],
    siblings: [],
    data: { name: 'Untested Father', label: 'Father', isUntested: true }
  });
  
  nodes.push({
    id: motherId,
    gender: 'female',
    parents: [],
    children: cluster.map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' })),
    spouses: [{ id: fatherId, type: 'married' }],
    siblings: [],
    data: { name: 'Untested Mother', label: 'Mother', isUntested: true }
  });
  
  // Add children
  for (const ind of cluster) {
    const siblings = cluster.filter(c => c.id !== ind.id);
    nodes.push({
      id: createNodeId(ind.name, prefix),
      gender: genderToNodeGender(ind.gender),
      parents: [
        { id: fatherId, type: 'blood' },
        { id: motherId, type: 'blood' }
      ],
      children: [],
      spouses: [],
      siblings: siblings.map(s => ({ id: createNodeId(s.name, prefix), type: 'blood' })),
      data: { name: ind.name, label: 'Sibling', isIndex: true }
    });
  }
  
  const names = cluster.map(c => c.name.split(' ')[0]).join(', ');
  
  permutations.push({
    id: 0,
    nodes,
    rootId: fatherId,
    label: `Siblings: ${names}`,
    type: 'Nuclear Family',
    category: 'fullSibling',
    details: `${cluster.length} siblings with untested parents`
  });
  
  return permutations;
}

/**
 * Generate permutation connecting two clusters via extended relationship
 */
function generateConnectedClustersPermutation(
  cluster1: TestedIndividual[],
  cluster2: TestedIndividual[],
  extEdges: Map<string, Set<string>>,
  startIdx: number
): Permutation[] {
  const permutations: Permutation[] = [];
  const prefix = `connected_${startIdx}_`;
  
  // Model as two nuclear families connected through common grandparents
  const grandfatherId = prefix + 'grandfather';
  const grandmotherId = prefix + 'grandmother';
  const father1Id = prefix + 'father1';
  const mother1Id = prefix + 'mother1';
  const father2Id = prefix + 'father2';
  const mother2Id = prefix + 'mother2';
  
  const nodes: PedigreeNode[] = [];
  
  // Add grandparents (common ancestors)
  nodes.push({
    id: grandfatherId,
    gender: 'male',
    parents: [],
    children: [
      { id: father1Id, type: 'blood' },
      { id: father2Id, type: 'blood' }
    ],
    spouses: [{ id: grandmotherId, type: 'married' }],
    siblings: [],
    data: { name: 'Untested Grandfather', label: 'Grandfather', isUntested: true }
  });
  
  nodes.push({
    id: grandmotherId,
    gender: 'female',
    parents: [],
    children: [
      { id: father1Id, type: 'blood' },
      { id: father2Id, type: 'blood' }
    ],
    spouses: [{ id: grandfatherId, type: 'married' }],
    siblings: [],
    data: { name: 'Untested Grandmother', label: 'Grandmother', isUntested: true }
  });
  
  // Add parents for cluster 1
  nodes.push({
    id: father1Id,
    gender: 'male',
    parents: [
      { id: grandfatherId, type: 'blood' },
      { id: grandmotherId, type: 'blood' }
    ],
    children: cluster1.map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' })),
    spouses: [{ id: mother1Id, type: 'married' }],
    siblings: [{ id: father2Id, type: 'blood' }],
    data: { name: 'Untested Father 1', label: 'Father 1', isUntested: true }
  });
  
  nodes.push({
    id: mother1Id,
    gender: 'female',
    parents: [],
    children: cluster1.map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' })),
    spouses: [{ id: father1Id, type: 'married' }],
    siblings: [],
    data: { name: 'Untested Mother 1', label: 'Mother 1', isUntested: true }
  });
  
  // Add parents for cluster 2
  nodes.push({
    id: father2Id,
    gender: 'male',
    parents: [
      { id: grandfatherId, type: 'blood' },
      { id: grandmotherId, type: 'blood' }
    ],
    children: cluster2.map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' })),
    spouses: [{ id: mother2Id, type: 'married' }],
    siblings: [{ id: father1Id, type: 'blood' }],
    data: { name: 'Untested Father 2', label: 'Father 2', isUntested: true }
  });
  
  nodes.push({
    id: mother2Id,
    gender: 'female',
    parents: [],
    children: cluster2.map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' })),
    spouses: [{ id: father2Id, type: 'married' }],
    siblings: [],
    data: { name: 'Untested Mother 2', label: 'Mother 2', isUntested: true }
  });
  
  // Add cluster 1 children (cousins to cluster 2)
  for (const ind of cluster1) {
    const siblings = cluster1.filter(c => c.id !== ind.id);
    nodes.push({
      id: createNodeId(ind.name, prefix),
      gender: genderToNodeGender(ind.gender),
      parents: [
        { id: father1Id, type: 'blood' },
        { id: mother1Id, type: 'blood' }
      ],
      children: [],
      spouses: [],
      siblings: siblings.map(s => ({ id: createNodeId(s.name, prefix), type: 'blood' })),
      data: { name: ind.name, label: 'Cousin (Group 1)', isIndex: true }
    });
  }
  
  // Add cluster 2 children (cousins to cluster 1)
  for (const ind of cluster2) {
    const siblings = cluster2.filter(c => c.id !== ind.id);
    nodes.push({
      id: createNodeId(ind.name, prefix),
      gender: genderToNodeGender(ind.gender),
      parents: [
        { id: father2Id, type: 'blood' },
        { id: mother2Id, type: 'blood' }
      ],
      children: [],
      spouses: [],
      siblings: siblings.map(s => ({ id: createNodeId(s.name, prefix), type: 'blood' })),
      data: { name: ind.name, label: 'Cousin (Group 2)', isIndex: true }
    });
  }
  
  const names1 = cluster1.map(c => c.name.split(' ')[0]).join(' & ');
  const names2 = cluster2.map(c => c.name.split(' ')[0]).join(' & ');
  
  permutations.push({
    id: 0,
    nodes,
    rootId: grandfatherId,
    label: `Extended Family: (${names1}) & (${names2})`,
    type: 'Extended Family (Cousins)',
    category: 'extended',
    details: `Two families connected as first cousins through common grandparents`
  });
  
  return permutations;
}

/**
 * Generate permutation for multiple clusters connected through common ancestors
 */
function generateMultiClusterPermutation(
  clusters: TestedIndividual[][],
  graph: KinshipGraph,
  startIdx: number
): Permutation[] {
  const permutations: Permutation[] = [];
  
  if (clusters.length < 2) return permutations;
  
  const prefix = `multi_${startIdx}_`;
  const nodes: PedigreeNode[] = [];
  
  // Create common ancestor pair
  const ancestorId = prefix + 'common_ancestor_m';
  const ancestorSpouseId = prefix + 'common_ancestor_f';
  
  nodes.push({
    id: ancestorId,
    gender: 'male',
    parents: [],
    children: clusters.map((_, i) => ({ id: `${prefix}parent_${i}`, type: 'blood' })),
    spouses: [{ id: ancestorSpouseId, type: 'married' }],
    siblings: [],
    data: { name: 'Common Ancestor', label: 'Ancestor', isUntested: true }
  });
  
  nodes.push({
    id: ancestorSpouseId,
    gender: 'female',
    parents: [],
    children: clusters.map((_, i) => ({ id: `${prefix}parent_${i}`, type: 'blood' })),
    spouses: [{ id: ancestorId, type: 'married' }],
    siblings: [],
    data: { name: 'Common Ancestor Spouse', label: 'Ancestor', isUntested: true }
  });
  
  // Create parent for each cluster
  for (let i = 0; i < clusters.length; i++) {
    const parentId = `${prefix}parent_${i}`;
    const spouseId = `${prefix}spouse_${i}`;
    const cluster = clusters[i];
    
    // Parent siblings
    const parentSiblings = clusters
      .map((_, j) => ({ id: `${prefix}parent_${j}`, type: 'blood' }))
      .filter((_, j) => j !== i);
    
    nodes.push({
      id: parentId,
      gender: 'male',
      parents: [
        { id: ancestorId, type: 'blood' },
        { id: ancestorSpouseId, type: 'blood' }
      ],
      children: cluster.map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' })),
      spouses: [{ id: spouseId, type: 'married' }],
      siblings: parentSiblings,
      data: { name: `Untested Parent ${i + 1}`, label: `Parent ${i + 1}`, isUntested: true }
    });
    
    nodes.push({
      id: spouseId,
      gender: 'female',
      parents: [],
      children: cluster.map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' })),
      spouses: [{ id: parentId, type: 'married' }],
      siblings: [],
      data: { name: `Untested Spouse ${i + 1}`, label: `Spouse ${i + 1}`, isUntested: true }
    });
    
    // Add cluster members
    for (const ind of cluster) {
      const siblings = cluster.filter(c => c.id !== ind.id);
      nodes.push({
        id: createNodeId(ind.name, prefix),
        gender: genderToNodeGender(ind.gender),
        parents: [
          { id: parentId, type: 'blood' },
          { id: spouseId, type: 'blood' }
        ],
        children: [],
        spouses: [],
        siblings: siblings.map(s => ({ id: createNodeId(s.name, prefix), type: 'blood' })),
        data: { name: ind.name, label: `Family ${i + 1}`, isIndex: true }
      });
    }
  }
  
  const allNames = clusters.flat().map(c => c.name.split(' ')[0]).join(', ');
  
  permutations.push({
    id: 0,
    nodes,
    rootId: ancestorId,
    label: `Extended Family Network: ${allNames}`,
    type: 'Extended Family Network',
    category: 'extended',
    details: `${clusters.length} family groups connected through common ancestors`
  });
  
  return permutations;
}

// ============================================================================
// DISJOINTED PERMUTATION ASSEMBLY (for multi-island cases)
// ============================================================================

/**
 * Finds "kinship islands" - maximal groups where all pairs have kinship evidence.
 * An island is NOT the same as a connected component - an island requires EVERY
 * pair within the group to have at least one LR > 1 edge.
 * 
 * For case 2024-071746:
 * - SB.1 has EXT edges to SB.2, SB.3, SB.4
 * - SB.5 has FS edge to SB.2 only
 * - Island 1: {SB.5, SB.2} (all pairs have kinship: SB.5-SB.2 FS)
 * - Island 2: {SB.1, SB.2, SB.3, SB.4}? No - SB.2-SB.3, SB.2-SB.4, SB.3-SB.4 have no direct edges
 * - Actually: SB.1-SB.2 (EXT), SB.1-SB.3 (EXT), SB.1-SB.4 (EXT) - that's it
 * - So islands are based on which pairs have evidence
 */
function findKinshipIslands(graph: KinshipGraph): TestedIndividual[][] {
  const { individuals, pcEdges, fsEdges, extEdges } = graph;
  
  // Build pairwise evidence map
  const hasEvidence = (id1: string, id2: string): boolean => {
    return hasEdge(pcEdges, id1, id2) || 
           hasEdge(fsEdges, id1, id2) || 
           hasEdge(extEdges, id1, id2);
  };
  
  // For each individual, find who they have kinship evidence with
  const neighbors = new Map<string, Set<string>>();
  individuals.forEach(ind => {
    const connected = new Set<string>();
    individuals.forEach(other => {
      if (other.id !== ind.id && hasEvidence(ind.id, other.id)) {
        connected.add(other.id);
      }
    });
    neighbors.set(ind.id, connected);
  });
  
  // Find individuals who don't have pairwise evidence with everyone else
  // These need to be in separate "disjointed" structures
  const islands: TestedIndividual[][] = [];
  const used = new Set<string>();
  
  // Strategy: For each individual, find the maximal set they can be with
  // where they have kinship evidence with everyone in the set
  individuals.forEach(ind => {
    if (used.has(ind.id)) return;
    
    // Start with this individual
    const island = [ind];
    const islandIds = new Set([ind.id]);
    
    // Add others who have evidence with ALL current island members
    individuals.forEach(other => {
      if (other.id === ind.id || used.has(other.id)) return;
      
      // Check if other has evidence with all current island members
      const hasEvidenceWithAll = [...islandIds].every(memberId => 
        hasEvidence(memberId, other.id)
      );
      
      if (hasEvidenceWithAll) {
        island.push(other);
        islandIds.add(other.id);
      }
    });
    
    // Mark as used
    island.forEach(i => used.add(i.id));
    
    if (island.length >= 2) {
      islands.push(island);
    }
  });
  
  // Handle any remaining individuals not in islands
  individuals.forEach(ind => {
    if (!used.has(ind.id)) {
      // Find who they connect to
      const myNeighbors = neighbors.get(ind.id) || new Set();
      if (myNeighbors.size > 0) {
        // Add as a singleton island with their first neighbor
        const neighbor = individuals.find(i => myNeighbors.has(i.id));
        if (neighbor) {
          islands.push([ind, neighbor]);
          used.add(ind.id);
        }
      }
    }
  });
  
  return islands;
}

/**
 * Generate disjointed permutations that include ALL tested individuals.
 * When individuals can't all fit in one pedigree (due to missing pairwise evidence),
 * we create disjointed structures (nodes + nodesB) that together cover everyone.
 */
function generateDisjointedPermutations(graph: KinshipGraph): Permutation[] {
  const permutations: Permutation[] = [];
  const { individuals, pcEdges, fsEdges, extEdges } = graph;
  
  // Check if we need disjointed structures
  // We need them if NOT all pairs have kinship evidence
  const allPairsHaveEvidence = individuals.every((ind1, i) =>
    individuals.slice(i + 1).every(ind2 =>
      hasEdge(pcEdges, ind1.id, ind2.id) ||
      hasEdge(fsEdges, ind1.id, ind2.id) ||
      hasEdge(extEdges, ind1.id, ind2.id)
    )
  );
  
  if (allPairsHaveEvidence) {
    // No need for disjointed structures - all pairs have evidence
    return [];
  }
  
  // Find individuals with limited connectivity (like SB.5)
  const limitedConnectivity: TestedIndividual[] = [];
  const fullConnectivity: TestedIndividual[] = [];
  
  individuals.forEach(ind => {
    const connections = individuals.filter(other =>
      other.id !== ind.id && (
        hasEdge(pcEdges, ind.id, other.id) ||
        hasEdge(fsEdges, ind.id, other.id) ||
        hasEdge(extEdges, ind.id, other.id)
      )
    );
    
    if (connections.length < individuals.length - 1) {
      limitedConnectivity.push(ind);
    } else {
      fullConnectivity.push(ind);
    }
  });
  
  if (limitedConnectivity.length === 0) {
    return [];
  }
  
  // Generate structures for limited connectivity individuals with their partners
  // Then combine with structures for the rest
  
  limitedConnectivity.forEach(limited => {
    // Find who this individual connects to
    const partners = individuals.filter(other =>
      other.id !== limited.id && (
        hasEdge(pcEdges, limited.id, other.id) ||
        hasEdge(fsEdges, limited.id, other.id) ||
        hasEdge(extEdges, limited.id, other.id)
      )
    );
    
    if (partners.length === 0) return;
    
    // Determine relationship type with partners
    partners.forEach(partner => {
      const hasPc = hasEdge(pcEdges, limited.id, partner.id);
      const hasFs = hasEdge(fsEdges, limited.id, partner.id);
      const hasExt = hasEdge(extEdges, limited.id, partner.id);
      
      // Find all OTHER individuals (excluding the limited one) to form the extended family structure
      // These are individuals who connect through the graph but NOT to the limited individual
      const others = individuals.filter(other =>
        other.id !== limited.id &&
        other.id !== partner.id
      );
      
      if (others.length === 0) return;
      
      // Check that the others form a connected group through the partner or hub
      const othersConnectedToPartner = others;
      
      // Generate Structure A: limited + partner (based on their relationship)
      const prefixA = `disj_a_${limited.name.replace(/\s+/g, '_').toLowerCase()}_`;
      const nodesA: PedigreeNode[] = [];
      
      if (hasFs) {
        // Siblings structure
        const fatherId = prefixA + 'father';
        const motherId = prefixA + 'mother';
        
        nodesA.push({
          id: fatherId,
          gender: 'male',
          parents: [],
          children: [
            { id: createNodeId(limited.name, prefixA), type: 'blood' },
            { id: createNodeId(partner.name, prefixA), type: 'blood' }
          ],
          spouses: [{ id: motherId, type: 'married' }],
          siblings: [],
          data: { name: 'Untested Father', label: 'Father', isUntested: true }
        });
        
        nodesA.push({
          id: motherId,
          gender: 'female',
          parents: [],
          children: [
            { id: createNodeId(limited.name, prefixA), type: 'blood' },
            { id: createNodeId(partner.name, prefixA), type: 'blood' }
          ],
          spouses: [{ id: fatherId, type: 'married' }],
          siblings: [],
          data: { name: 'Untested Mother', label: 'Mother', isUntested: true }
        });
        
        nodesA.push({
          id: createNodeId(limited.name, prefixA),
          gender: genderToNodeGender(limited.gender),
          parents: [{ id: fatherId, type: 'blood' }, { id: motherId, type: 'blood' }],
          children: [],
          spouses: [],
          siblings: [{ id: createNodeId(partner.name, prefixA), type: 'blood' }],
          data: { name: limited.name, label: 'Sibling 1', isIndex: true, kinshipId: limited.id }
        });
        
        nodesA.push({
          id: createNodeId(partner.name, prefixA),
          gender: genderToNodeGender(partner.gender),
          parents: [{ id: fatherId, type: 'blood' }, { id: motherId, type: 'blood' }],
          children: [],
          spouses: [],
          siblings: [{ id: createNodeId(limited.name, prefixA), type: 'blood' }],
          data: { name: partner.name, label: 'Sibling 2', isIndex: true, kinshipId: partner.id }
        });
      } else if (hasPc) {
        // Parent-child structure
        const parentGender = limited.gender === 'M' ? 'male' : 'female';
        const spouseGender = limited.gender === 'M' ? 'female' : 'male';
        const spouseId = prefixA + 'spouse';
        
        nodesA.push({
          id: createNodeId(limited.name, prefixA),
          gender: parentGender,
          parents: [],
          children: [{ id: createNodeId(partner.name, prefixA), type: 'blood' }],
          spouses: [{ id: spouseId, type: 'married' }],
          siblings: [],
          data: { name: limited.name, label: 'Parent', isIndex: true, kinshipId: limited.id }
        });
        
        nodesA.push({
          id: spouseId,
          gender: spouseGender,
          parents: [],
          children: [{ id: createNodeId(partner.name, prefixA), type: 'blood' }],
          spouses: [{ id: createNodeId(limited.name, prefixA), type: 'married' }],
          siblings: [],
          data: { name: 'Untested Spouse', isUntested: true }
        });
        
        nodesA.push({
          id: createNodeId(partner.name, prefixA),
          gender: genderToNodeGender(partner.gender),
          parents: [
            { id: createNodeId(limited.name, prefixA), type: 'blood' },
            { id: spouseId, type: 'blood' }
          ],
          children: [],
          spouses: [],
          siblings: [],
          data: { name: partner.name, label: 'Child', isIndex: true, kinshipId: partner.id }
        });
      } else {
        // Extended family - skip for now (more complex)
        return;
      }
      
      // Generate Structure B: Extended Family structure using COUSIN pattern
      // CRITICAL: We must NOT assert parent-child between tested individuals where only EXT evidence exists
      // Instead, use shared grandparents with each tested individual having their own untested parent
      const prefixB = `disj_b_${partner.name.replace(/\s+/g, '_').toLowerCase()}_`;
      const nodesB: PedigreeNode[] = [];
      
      const allInB = [partner, ...othersConnectedToPartner];
      
      // For EXT-only relationships, use a cousin structure:
      // - Shared grandparents (untested)
      // - Each tested individual has their own untested parent
      // - The untested parents are siblings (share grandparents)
      // This represents Extended Family without asserting direct PC between tested individuals
      
      const grandparentMaleId = prefixB + 'grandfather';
      const grandparentFemaleId = prefixB + 'grandmother';
      
      // Create grandparents
      const untestedParentIds: string[] = [];
      allInB.forEach((ind, idx) => {
        untestedParentIds.push(prefixB + `parent_${idx}`);
      });
      
      nodesB.push({
        id: grandparentMaleId,
        gender: 'male',
        parents: [],
        children: untestedParentIds.map(pid => ({ id: pid, type: 'blood' })),
        spouses: [{ id: grandparentFemaleId, type: 'married' }],
        siblings: [],
        data: { name: 'Shared Grandfather', label: 'Grandfather', isUntested: true }
      });
      
      nodesB.push({
        id: grandparentFemaleId,
        gender: 'female',
        parents: [],
        children: untestedParentIds.map(pid => ({ id: pid, type: 'blood' })),
        spouses: [{ id: grandparentMaleId, type: 'married' }],
        siblings: [],
        data: { name: 'Shared Grandmother', label: 'Grandmother', isUntested: true }
      });
      
      // Create untested parents (siblings of each other) and tested individuals as their children
      allInB.forEach((ind, idx) => {
        const parentId = untestedParentIds[idx];
        const spouseId = prefixB + `spouse_${idx}`;
        const testedNodeId = createNodeId(ind.name, prefixB);
        
        // Other untested parents (siblings)
        const siblingParentIds = untestedParentIds.filter((_, i) => i !== idx);
        
        // Untested parent (child of grandparents, parent of tested individual)
        nodesB.push({
          id: parentId,
          gender: ind.gender === 'M' ? 'male' : 'female', // Same gender for visual clarity
          parents: [
            { id: grandparentMaleId, type: 'blood' },
            { id: grandparentFemaleId, type: 'blood' }
          ],
          children: [{ id: testedNodeId, type: 'blood' }],
          spouses: [{ id: spouseId, type: 'married' }],
          siblings: siblingParentIds.map(sid => ({ id: sid, type: 'blood' as const })),
          data: { name: `Parent of ${ind.name.split(' ')[0]}`, isUntested: true }
        });
        
        // Spouse of untested parent
        nodesB.push({
          id: spouseId,
          gender: ind.gender === 'M' ? 'female' : 'male',
          parents: [],
          children: [{ id: testedNodeId, type: 'blood' }],
          spouses: [{ id: parentId, type: 'married' }],
          siblings: [],
          data: { name: 'Untested In-Law', isUntested: true }
        });
        
        // Tested individual (child of untested parent)
        nodesB.push({
          id: testedNodeId,
          gender: genderToNodeGender(ind.gender),
          parents: [
            { id: parentId, type: 'blood' },
            { id: spouseId, type: 'blood' }
          ],
          children: [],
          spouses: [],
          siblings: [],
          data: { name: ind.name, label: `Cousin ${idx + 1}`, isIndex: true, kinshipId: ind.id }
        });
      });
      
      // Combine into disjointed permutation
      const allNames = [limited.name, ...allInB.map(i => i.name)].map(n => n.split(' ')[0]).join(', ');
      
      permutations.push({
        id: 0,
        nodes: nodesA,
        nodesB: nodesB,
        rootId: nodesA[0].id,
        rootIdB: grandparentMaleId,
        label: `Disjointed: ${limited.name.split(' ')[0]} (${hasFs ? 'FS' : hasPc ? 'PC' : 'EXT'}) + Extended Cousins`,
        type: 'Disjointed Family',
        category: 'extended',
        details: `${limited.name} connects to ${partner.name} only; others are extended family (cousins)`
      });
    });
  });
  
  return permutations;
}

// ============================================================================
// FULL COVERAGE ENFORCEMENT (Rule 21)
// ============================================================================

/**
 * Ensures a permutation includes ALL individuals from the kinship graph.
 * If individuals are missing, attempts to add them as a disjointed structure (nodesB).
 * Returns the augmented permutation, or null if full coverage is impossible.
 */
function enforceFullCoverage(
  perm: Permutation,
  graph: KinshipGraph
): Permutation | null {
  const allIndividuals = new Set(graph.individuals.map(i => i.name.toLowerCase()));
  const permIndividuals = getTestedNamesFromPermutation(perm.nodes, perm.nodesB);
  
  // Check if all individuals are already included
  const missing = Array.from(allIndividuals).filter(name => !permIndividuals.has(name));
  
  if (missing.length === 0) {
    // All individuals included - permutation is complete
    return perm;
  }
  
  // If permutation already has nodesB, we can't easily augment it further
  // In this case, skip this permutation
  if (perm.nodesB && perm.nodesB.length > 0) {
    return null;
  }
  
  // Try to create a nodesB structure for the missing individuals
  const missingIndividuals = graph.individuals.filter(
    i => missing.includes(i.name.toLowerCase())
  );
  
  if (missingIndividuals.length === 0) {
    return perm; // No valid missing individuals found
  }
  
  // Build nodesB using COUSIN structure (Extended Family without asserting PC between tested)
  // Each missing individual has their own untested parent, all sharing grandparents
  const prefixB = `augment_`;
  const nodesB: PedigreeNode[] = [];
  
  const grandparentMaleId = prefixB + 'grandfather';
  const grandparentFemaleId = prefixB + 'grandmother';
  
  // Create untested parent IDs for each missing individual
  const untestedParentIds: string[] = [];
  missingIndividuals.forEach((_, idx) => {
    untestedParentIds.push(prefixB + `parent_${idx}`);
  });
  
  // Create grandparents
  nodesB.push({
    id: grandparentMaleId,
    gender: 'male',
    parents: [],
    children: untestedParentIds.map(pid => ({ id: pid, type: 'blood' })),
    spouses: [{ id: grandparentFemaleId, type: 'married' }],
    siblings: [],
    data: { name: 'Shared Grandfather', label: 'Grandfather', isUntested: true }
  });
  
  nodesB.push({
    id: grandparentFemaleId,
    gender: 'female',
    parents: [],
    children: untestedParentIds.map(pid => ({ id: pid, type: 'blood' })),
    spouses: [{ id: grandparentMaleId, type: 'married' }],
    siblings: [],
    data: { name: 'Shared Grandmother', label: 'Grandmother', isUntested: true }
  });
  
  // Create untested parents and tested individuals
  missingIndividuals.forEach((ind, idx) => {
    const parentId = untestedParentIds[idx];
    const spouseId = prefixB + `spouse_${idx}`;
    const testedNodeId = createNodeId(ind.name, prefixB);
    
    // Other untested parents (siblings)
    const siblingParentIds = untestedParentIds.filter((_, i) => i !== idx);
    
    // Untested parent
    nodesB.push({
      id: parentId,
      gender: ind.gender === 'M' ? 'male' : 'female',
      parents: [
        { id: grandparentMaleId, type: 'blood' },
        { id: grandparentFemaleId, type: 'blood' }
      ],
      children: [{ id: testedNodeId, type: 'blood' }],
      spouses: [{ id: spouseId, type: 'married' }],
      siblings: siblingParentIds.map(sid => ({ id: sid, type: 'blood' as const })),
      data: { name: `Parent of ${ind.name.split(' ')[0]}`, isUntested: true }
    });
    
    // Spouse of untested parent
    nodesB.push({
      id: spouseId,
      gender: ind.gender === 'M' ? 'female' : 'male',
      parents: [],
      children: [{ id: testedNodeId, type: 'blood' }],
      spouses: [{ id: parentId, type: 'married' }],
      siblings: [],
      data: { name: 'Untested In-Law', isUntested: true }
    });
    
    // Tested individual (child of untested parent - a cousin)
    nodesB.push({
      id: testedNodeId,
      gender: genderToNodeGender(ind.gender),
      parents: [
        { id: parentId, type: 'blood' },
        { id: spouseId, type: 'blood' }
      ],
      children: [],
      spouses: [],
      siblings: [],
      data: { name: ind.name, label: `Cousin ${idx + 1}`, isIndex: true, kinshipId: ind.id }
    });
  });
  
  // Return augmented permutation
  return {
    ...perm,
    nodesB: nodesB,
    rootIdB: grandparentMaleId,
    label: perm.label + ' + Extended Cousins',
    details: (perm.details || '') + ` (includes ${missingIndividuals.map(i => i.name).join(', ')} as cousins)`
  };
}

/**
 * Checks if a permutation includes ALL individuals from the kinship graph.
 */
function hasFullCoverage(perm: Permutation, graph: KinshipGraph): boolean {
  const allIndividuals = new Set(graph.individuals.map(i => i.name.toLowerCase()));
  const permIndividuals = getTestedNamesFromPermutation(perm.nodes, perm.nodesB);
  
  for (const name of allIndividuals) {
    if (!permIndividuals.has(name)) {
      return false;
    }
  }
  return true;
}

// ============================================================================
// NUCLEAR AVUNCULAR BRIDGE GENERATOR (BEST-FIT MODE)
// ============================================================================

/**
 * Generates a single nuclear 3-generation pedigree for cases with avuncular bridge pattern.
 * This handles cases like PAR 4897 where:
 * - Patricia is PC anchor (mother of David and Brian)
 * - Holly has FS to David/Brian (so Holly is their sibling)
 * - Holly also has FS to Dawn (so Dawn shares parents with Holly)
 * - James has EXT to Dawn (avuncular - James is Dawn's uncle)
 * 
 * KEY INSIGHT: James's avuncular EXT to Dawn means James is sibling to Dawn's parent.
 * Since Dawn shares parents with Holly/David/Brian, Dawn's parent is Patricia's spouse.
 * Therefore: James is sibling to Patricia's spouse.
 * 
 * STRUCTURE:
 * - Gen 1: ONE grandparent pair (James's parents = Spouse's parents)
 * - Gen 2: James + Untested Spouse (siblings), Patricia (married to Spouse), Donna (Patricia's FS)
 * - Gen 3: Dawn, Holly, David, Brian (all children of Patricia + Spouse)
 */
function generatePCPriorityDisjointPermutations(
  graph: KinshipGraph,
  kinshipPairs: KinshipPair[]
): Permutation[] {
  const permutations: Permutation[] = [];
  const { individuals, pcEdges, fsEdges, extEdges, lrLookup } = graph;
  
  if (individuals.length < 3) return [];
  
  // ========== STEP 1: Identify PC-dominant relationships ==========
  const pcDominantPairs: { parent: string; child: string; pcLR: number }[] = [];
  const childrenOfParent = new Map<string, Set<string>>();
  
  kinshipPairs.forEach(pair => {
    if (pair.parentChildLR > LR_THRESHOLD) {
      const id1 = pair.relative1.id;
      const id2 = pair.relative2.id;
      const key = makeLRKey(id1, id2);
      const fsLR = lrLookup.fs.get(key) || 0;
      
      // PC is dominant if PC LR > FS LR
      if (pair.parentChildLR > fsLR) {
        const pc1Count = kinshipPairs.filter(p => 
          (p.relative1.id === id1 || p.relative2.id === id1) && 
          p.parentChildLR > p.fullSiblingLR && p.parentChildLR > LR_THRESHOLD
        ).length;
        const pc2Count = kinshipPairs.filter(p => 
          (p.relative1.id === id2 || p.relative2.id === id2) && 
          p.parentChildLR > p.fullSiblingLR && p.parentChildLR > LR_THRESHOLD
        ).length;
        
        const parentId = pc1Count >= pc2Count ? id1 : id2;
        const childId = pc1Count >= pc2Count ? id2 : id1;
        
        pcDominantPairs.push({ parent: parentId, child: childId, pcLR: pair.parentChildLR });
        
        if (!childrenOfParent.has(parentId)) childrenOfParent.set(parentId, new Set());
        childrenOfParent.get(parentId)!.add(childId);
      }
    }
  });
  
  // If no PC-dominant pairs, fall back to FS-anchor approach
  if (pcDominantPairs.length === 0) {
    return generateFSAnchoredPermutations(graph, kinshipPairs);
  }
  
  // ========== STEP 2: Find the PC anchor ==========
  let anchorId: string | undefined;
  let maxScore = 0;
  
  for (const [parentId, children] of childrenOfParent) {
    const score = pcDominantPairs
      .filter(p => p.parent === parentId)
      .reduce((sum, p) => sum + Math.log10(p.pcLR), 0);
    if (score > maxScore) {
      maxScore = score;
      anchorId = parentId;
    }
  }
  
  if (!anchorId) return [];
  const anchor = individuals.find(i => i.id === anchorId);
  if (!anchor) return [];
  
  // ========== STEP 3: Identify family clusters ==========
  // Anchor's PC children (David, Brian)
  const anchorChildren = Array.from(childrenOfParent.get(anchorId) || [])
    .map(id => individuals.find(i => i.id === id))
    .filter((i): i is TestedIndividual => i !== undefined);
  
  // Anchor's FS siblings (Donna)
  const anchorFSSiblings = individuals.filter(ind => 
    ind.id !== anchorId &&
    !anchorChildren.some(c => c.id === ind.id) &&
    hasEdge(fsEdges, ind.id, anchorId)
  );
  
  // Individuals with FS to anchor's children - these are also children (Holly)
  const fsToChildren = individuals.filter(ind => {
    if (ind.id === anchorId) return false;
    if (anchorChildren.some(c => c.id === ind.id)) return false;
    if (anchorFSSiblings.some(s => s.id === ind.id)) return false;
    
    return anchorChildren.some(c => {
      const fsLR = lrLookup.fs.get(makeLRKey(ind.id, c.id)) || 0;
      return fsLR > 1000;
    });
  });
  
  // All children in Gen 3 (David, Brian, Holly)
  const allChildren = [...anchorChildren, ...fsToChildren];
  
  // ========== STEP 4: Detect Avuncular Bridge Pattern ==========
  // Key insight: Use DOMINANT relationship type for classification
  // - If FS > EXT for any child pair → individual is a sibling (Gen 3 child)
  // - If EXT > FS for all child pairs → individual is avuncular (Gen 2 uncle/aunt)
  
  const avuncularToChildren: TestedIndividual[] = [];
  const bridgeChildren: TestedIndividual[] = []; // Children connected via FS dominance
  
  // Find remaining individuals not yet placed
  const remainingIndividuals = individuals.filter(ind => {
    if (ind.id === anchorId) return false;
    if (allChildren.some(c => c.id === ind.id)) return false;
    if (anchorFSSiblings.some(s => s.id === ind.id)) return false;
    return true;
  });
  
  // Classify each remaining individual based on DOMINANT relationship type
  remainingIndividuals.forEach(ind => {
    let hasFSDominantToAnyChild = false;
    let hasEXTDominantToAnyChild = false;
    
    // Check relationship dominance against all current children
    allChildren.forEach(child => {
      const key = makeLRKey(ind.id, child.id);
      const fsLR = lrLookup.fs.get(key) || 0;
      const extLR = lrLookup.ext.get(key) || 0;
      
      // FS is dominant if FS > EXT and FS > threshold
      if (fsLR > extLR && fsLR > LR_THRESHOLD) {
        hasFSDominantToAnyChild = true;
      }
      // EXT is dominant if EXT > FS and EXT > threshold
      if (extLR > fsLR && extLR > LR_THRESHOLD) {
        hasEXTDominantToAnyChild = true;
      }
    });
    
    // Classify based on dominant relationship type
    if (hasFSDominantToAnyChild) {
      // FS dominant to at least one child → this is a sibling (Gen 3)
      bridgeChildren.push(ind);
    } else if (hasEXTDominantToAnyChild) {
      // EXT dominant to children (no FS dominant) → this is avuncular (Gen 2)
      avuncularToChildren.push(ind);
    }
  });
  
  // Combine all Gen 3 children
  const finalGen3Children = [...allChildren, ...bridgeChildren];
  
  // ========== STEP 5: Build Nuclear 3-Generation Pedigree ==========
  const nodes: PedigreeNode[] = [];
  const prefix = 'bf_';
  
  // Gen 1: ONE grandparent pair (parents of James and Spouse)
  const gfId = prefix + 'grandfather';
  const gmId = prefix + 'grandmother';
  
  // Gen 2: Spouse (untested, sibling to avuncular individuals like James)
  const spouseId = prefix + 'spouse';
  
  // Calculate sibling IDs for grandparents' children
  // If we have avuncular individuals (James), they are siblings to Spouse
  const gen2SiblingGroup = avuncularToChildren.length > 0 
    ? [...avuncularToChildren.map(a => createNodeId(a.name, prefix)), spouseId]
    : [spouseId];
  
  // Add grandparents (parents of James and Spouse)
  nodes.push({
    id: gfId,
    gender: 'male',
    parents: [],
    children: gen2SiblingGroup.map(id => ({ id, type: 'blood' })),
    spouses: [{ id: gmId, type: 'married' }],
    siblings: [],
    data: { name: 'Untested Grandfather', isUntested: true }
  });
  
  nodes.push({
    id: gmId,
    gender: 'female',
    parents: [],
    children: gen2SiblingGroup.map(id => ({ id, type: 'blood' })),
    spouses: [{ id: gfId, type: 'married' }],
    siblings: [],
    data: { name: 'Untested Grandmother', isUntested: true }
  });
  
  // Add avuncular individuals (James) as children of grandparents, siblings to Spouse
  avuncularToChildren.forEach(ind => {
    const indId = createNodeId(ind.name, prefix);
    const siblingIds = gen2SiblingGroup.filter(id => id !== indId);
    
    nodes.push({
      id: indId,
      gender: genderToNodeGender(ind.gender),
      parents: [{ id: gfId, type: 'blood' }, { id: gmId, type: 'blood' }],
      children: [],
      spouses: [],
      siblings: siblingIds.map(sid => ({ id: sid, type: 'blood' })),
      data: { name: ind.name, label: 'Avuncular (Uncle/Aunt)', isIndex: true, kinshipId: ind.id }
    });
  });
  
  // Add Spouse (untested, sibling to James, married to Patricia)
  const anchorNodeId = createNodeId(anchor.name, prefix);
  const allChildIds = finalGen3Children.map(c => createNodeId(c.name, prefix));
  const spouseSiblingIds = avuncularToChildren.map(a => createNodeId(a.name, prefix));
  
  nodes.push({
    id: spouseId,
    gender: anchor.gender === 'M' ? 'female' : 'male',
    parents: [{ id: gfId, type: 'blood' }, { id: gmId, type: 'blood' }],
    children: allChildIds.map(id => ({ id, type: 'blood' })),
    spouses: [{ id: anchorNodeId, type: 'married' }],
    siblings: spouseSiblingIds.map(sid => ({ id: sid, type: 'blood' })),
    data: { name: 'Untested Spouse', isUntested: true }
  });
  
  // ========== Patricia's Family (Anchor + FS Siblings like Donna) ==========
  // CRITICAL: To avoid duplicate rendering, Patricia should NOT be a child of grandparents
  // Only Donna (FS sibling) is added as child of grandparents for tree connectivity
  // Patricia is connected ONLY via marriage to Spouse
  
  // Update grandparents to include ONLY Donna (not Patricia) as children
  // This prevents duplicate family unit rendering
  const gfNode = nodes.find(n => n.id === gfId);
  const gmNode = nodes.find(n => n.id === gmId);
  
  if (gfNode && gmNode && anchorFSSiblings.length > 0) {
    // Add ONLY FS siblings (Donna) as children of grandparents - NOT Patricia
    anchorFSSiblings.forEach(sib => {
      const sibId = createNodeId(sib.name, prefix);
      gfNode.children.push({ id: sibId, type: 'blood' });
      gmNode.children.push({ id: sibId, type: 'blood' });
    });
  }
  
  // Patricia's sibling references (ONLY Donna, connected via sibling relationship)
  const anchorSiblingRefs = anchorFSSiblings.map(s => ({ 
    id: createNodeId(s.name, prefix), 
    type: 'blood' as const 
  }));
  
  // Add Patricia (anchor) - married to Spouse, NO parents (connected via marriage only)
  // Children are connected to Patricia AND Spouse
  nodes.push({
    id: anchorNodeId,
    gender: genderToNodeGender(anchor.gender),
    parents: [], // Patricia is NOT a child of grandparents - prevents duplicate rendering
    children: allChildIds.map(id => ({ id, type: 'blood' })),
    spouses: [{ id: spouseId, type: 'married' }],
    siblings: anchorSiblingRefs,
    data: { 
      name: anchor.name, 
      label: 'Parent (PC Anchor)',
      isIndex: true, 
      kinshipId: anchor.id 
    }
  });
  
  // Add Patricia's FS siblings (Donna) - children of grandparents for tree connectivity
  // Donna's siblings include Patricia (via sibling relationship, not parent relationship)
  anchorFSSiblings.forEach(sib => {
    const sibId = createNodeId(sib.name, prefix);
    // Siblings = Patricia + other FS siblings (excluding self)
    const siblingRefs = [
      { id: anchorNodeId, type: 'blood' as const },
      ...anchorFSSiblings
        .filter(s => s.id !== sib.id)
        .map(s => ({ id: createNodeId(s.name, prefix), type: 'blood' as const }))
    ];
    
    nodes.push({
      id: sibId,
      gender: genderToNodeGender(sib.gender),
      parents: [{ id: gfId, type: 'blood' }, { id: gmId, type: 'blood' }],
      children: [],
      spouses: [],
      siblings: siblingRefs,
      data: { 
        name: sib.name, 
        label: 'FS Sibling of Anchor',
        isIndex: true, 
        kinshipId: sib.id 
      }
    });
  });
  
  // Add Gen 3 children (Dawn, Holly, David, Brian)
  finalGen3Children.forEach((child, idx) => {
    const childId = createNodeId(child.name, prefix);
    const siblingIds = finalGen3Children
      .filter(s => s.id !== child.id)
      .map(s => createNodeId(s.name, prefix));
    
    nodes.push({
      id: childId,
      gender: genderToNodeGender(child.gender),
      parents: [{ id: anchorNodeId, type: 'blood' }, { id: spouseId, type: 'blood' }],
      children: [],
      spouses: [],
      siblings: siblingIds.map(sid => ({ id: sid, type: 'blood' })),
      data: { name: child.name, label: `Child ${idx + 1}`, isIndex: true, kinshipId: child.id }
    });
  });
  
  // Create the permutation
  const allTestedNames = individuals.map(i => i.name);
  const avuncularNames = avuncularToChildren.map(a => a.name).join(', ');
  
  permutations.push({
    id: 0,
    nodes: nodes,
    rootId: gfId,
    label: `Best-Fit Nuclear Family: ${anchor.name}`,
    type: 'Best-Fit Nuclear Avuncular Bridge',
    category: 'extended',
    details: `PC anchor: ${anchor.name}; Avuncular: ${avuncularNames || 'none'}; Children: ${finalGen3Children.map(c => c.name).join(', ')}`
  });
  
  return permutations;
}

// ============================================================================
// FS-ANCHORED BEST-FIT MODE (NO PC PAIRS)
// ============================================================================

/**
 * Generates permutations when no PC-dominant pairs exist, using FS pairs as anchors.
 * This handles cases like PAR FC2963 where:
 * - Rosolia ↔ Sebastiano: FS (198,000) - the FS anchor pair
 * - Nina: EXT only to Rosolia and Paolo (NO FS to anyone)
 * - Paolo: EXT to everyone (NO FS except none)
 * 
 * CRITICAL RULE: Only assert FS between individuals with FS LR > 1.
 * EXT relatives must NOT be placed as siblings unless they have FS evidence.
 * 
 * STRUCTURE (3-generation):
 * - Gen 1: Grandparent(s) that connect the different branches
 * - Gen 2: FS siblings' parent(s) + EXT relatives in separate branches
 * - Gen 3: FS siblings (Rosolia, Sebastiano)
 * 
 * For PAR FC2963:
 * - Gen 1: Untested GM1 (shared ancestor), Untested GF1 (Paolo's father), Untested GM2 (Nina's mother)
 * - Gen 2: Paolo (child of GF1+GM1), Untested Father (child of GM1), Untested Mother, Nina (child of GM2)
 * - Gen 3: Rosolia & Sebastiano (children of Untested Father + Untested Mother)
 */
function generateFSAnchoredPermutations(
  graph: KinshipGraph,
  kinshipPairs: KinshipPair[]
): Permutation[] {
  const permutations: Permutation[] = [];
  const { individuals, lrLookup } = graph;
  
  if (individuals.length < 2) return [];
  
  // ========== STEP 1: Find FS-dominant pairs ==========
  const fsDominantPairs: { id1: string; id2: string; fsLR: number }[] = [];
  
  kinshipPairs.forEach(pair => {
    if (pair.fullSiblingLR > LR_THRESHOLD) {
      const id1 = pair.relative1.id;
      const id2 = pair.relative2.id;
      const key = makeLRKey(id1, id2);
      const extLR = lrLookup.ext.get(key) || 0;
      
      // FS is dominant if FS LR > EXT LR
      if (pair.fullSiblingLR > extLR) {
        fsDominantPairs.push({ id1, id2, fsLR: pair.fullSiblingLR });
      }
    }
  });
  
  if (fsDominantPairs.length === 0) return [];
  
  // ========== STEP 2: Build FS sibling cluster ==========
  const fsSiblingSet = new Set<string>();
  fsDominantPairs.forEach(pair => {
    fsSiblingSet.add(pair.id1);
    fsSiblingSet.add(pair.id2);
  });
  
  const fsSiblings = Array.from(fsSiblingSet)
    .map(id => individuals.find(i => i.id === id))
    .filter((i): i is TestedIndividual => i !== undefined);
  
  if (fsSiblings.length === 0) return [];
  
  // ========== STEP 3: Find EXT-connected individuals ==========
  // Build EXT relationship data sorted by LR (descending)
  interface ExtRelation {
    individual: TestedIndividual;
    maxExtLR: number;
    extToFS: { sibId: string; lr: number }[];
    extToOthers: { otherId: string; lr: number }[];
  }
  
  const extRelations: ExtRelation[] = [];
  
  individuals.forEach(ind => {
    if (fsSiblingSet.has(ind.id)) return; // Skip FS siblings
    
    const extToFS: { sibId: string; lr: number }[] = [];
    let maxExtLR = 0;
    
    // Check EXT relationships to FS siblings
    fsSiblings.forEach(sib => {
      const key = makeLRKey(ind.id, sib.id);
      const extLR = lrLookup.ext.get(key) || 0;
      if (extLR > LR_THRESHOLD) {
        extToFS.push({ sibId: sib.id, lr: extLR });
        maxExtLR = Math.max(maxExtLR, extLR);
      }
    });
    
    if (extToFS.length > 0) {
      extRelations.push({
        individual: ind,
        maxExtLR,
        extToFS,
        extToOthers: []
      });
    }
  });
  
  // Sort EXT relations by max LR descending
  extRelations.sort((a, b) => b.maxExtLR - a.maxExtLR);
  
  // ========== STEP 4: Check FS between EXT individuals ==========
  // Critical: Only place two EXT individuals as siblings if they have FS LR > 1
  const hasFS = (id1: string, id2: string): boolean => {
    const key = makeLRKey(id1, id2);
    const fsLR = lrLookup.fs.get(key) || 0;
    return fsLR > LR_THRESHOLD;
  };
  
  // ========== STEP 5: Build 3-generation pedigree structure ==========
  const nodes: PedigreeNode[] = [];
  const prefix = 'bf_fs_';
  
  // Gen 2: Parents of FS siblings (will be placed as Gen 2, with FS siblings as Gen 3)
  const fatherId = prefix + 'father';
  const motherId = prefix + 'mother';
  
  const fsSiblingIds = fsSiblings.map(s => createNodeId(s.name, prefix));
  
  // If we have EXT relatives, we need a 3-gen structure
  if (extRelations.length > 0) {
    // Gen 1: Create grandparent nodes
    // GM1 is shared (connects Paolo and Untested Father)
    const gm1Id = prefix + 'gm1';
    
    // Create separate parent nodes for each EXT individual who doesn't share FS with others
    // Group EXT individuals by FS relationships
    const extGroups: TestedIndividual[][] = [];
    const assigned = new Set<string>();
    
    extRelations.forEach(rel => {
      if (assigned.has(rel.individual.id)) return;
      
      const group = [rel.individual];
      assigned.add(rel.individual.id);
      
      // Find others with FS relationship to this one
      extRelations.forEach(other => {
        if (assigned.has(other.individual.id)) return;
        if (hasFS(rel.individual.id, other.individual.id)) {
          group.push(other.individual);
          assigned.add(other.individual.id);
        }
      });
      
      extGroups.push(group);
    });
    
    // Build nodes for each group
    let gmCounter = 1;
    const gen2Nodes: { id: string; individual?: TestedIndividual; isParent?: boolean }[] = [];
    
    // First, add the father node (will be child of GM1)
    gen2Nodes.push({ id: fatherId, isParent: true });
    
    // For each EXT group, create appropriate parent(s)
    extGroups.forEach((group, groupIdx) => {
      if (group.length === 1) {
        // Single EXT individual - give them their own parent(s) in separate branch
        const ext = group[0];
        gen2Nodes.push({ id: createNodeId(ext.name, prefix), individual: ext });
      } else {
        // Multiple EXT individuals with FS between them - they share parents
        group.forEach(ext => {
          gen2Nodes.push({ id: createNodeId(ext.name, prefix), individual: ext });
        });
      }
    });
    
    // Create GM1 node - shared grandmother connecting multiple branches
    // GM1 has children: Untested Father + the EXT individual with highest LR to FS siblings
    // This creates half-uncle/aunt relationship
    
    // Find the EXT with strongest connection to FS siblings (likely half-uncle/aunt)
    const primaryExt = extRelations[0]; // Highest LR EXT relative
    const primaryExtId = createNodeId(primaryExt.individual.name, prefix);
    
    // Create grandfather GF1 - Paolo's father (if Paolo is the primary EXT)
    const gf1Id = prefix + 'gf1';
    
    // GM1 children: Untested Father (half-sib) and has spouse with primary EXT
    nodes.push({
      id: gm1Id,
      gender: 'female',
      parents: [],
      children: [
        { id: fatherId, type: 'blood' },  // Half-sibling path
        { id: primaryExtId, type: 'blood' }  // Primary EXT is also child of GM1
      ],
      spouses: [{ id: gf1Id, type: 'married' }],
      siblings: [],
      data: { name: 'Untested GM 1', isUntested: true }
    });
    
    // GF1 - father of primary EXT, spouse of GM1
    nodes.push({
      id: gf1Id,
      gender: 'male',
      parents: [],
      children: [{ id: primaryExtId, type: 'blood' }],
      spouses: [{ id: gm1Id, type: 'married' }],
      siblings: [],
      data: { name: 'Untested GF 1', isUntested: true }
    });
    
    // Father node (child of GM1 only - makes him half-sib of primary EXT)
    nodes.push({
      id: fatherId,
      gender: 'male',
      parents: [{ id: gm1Id, type: 'blood' }],
      children: fsSiblingIds.map(id => ({ id, type: 'blood' })),
      spouses: [{ id: motherId, type: 'married' }],
      siblings: [],  // Not full sibling of primary EXT
      data: { name: 'Untested Father', isUntested: true }
    });
    
    // Mother node
    nodes.push({
      id: motherId,
      gender: 'female',
      parents: [],
      children: fsSiblingIds.map(id => ({ id, type: 'blood' })),
      spouses: [{ id: fatherId, type: 'married' }],
      siblings: [],
      data: { name: 'Untested Mother', isUntested: true }
    });
    
    // Primary EXT node (child of GF1 + GM1, half-sib of father via GM1)
    nodes.push({
      id: primaryExtId,
      gender: genderToNodeGender(primaryExt.individual.gender),
      parents: [{ id: gf1Id, type: 'blood' }, { id: gm1Id, type: 'blood' }],
      children: [],
      spouses: [],
      siblings: [],
      data: { 
        name: primaryExt.individual.name, 
        label: 'Half-Uncle/Aunt (Extended)',
        isIndex: true, 
        kinshipId: primaryExt.individual.id 
      }
    });
    
    // Add remaining EXT individuals in separate branches
    extRelations.slice(1).forEach((rel, idx) => {
      const extId = createNodeId(rel.individual.name, prefix);
      const gmId = prefix + 'gm' + (idx + 2);
      
      // Each additional EXT gets their own parent node
      nodes.push({
        id: gmId,
        gender: 'female',
        parents: [],
        children: [{ id: extId, type: 'blood' }],
        spouses: [],
        siblings: [],
        data: { name: `Untested GM ${idx + 2}`, isUntested: true }
      });
      
      nodes.push({
        id: extId,
        gender: genderToNodeGender(rel.individual.gender),
        parents: [{ id: gmId, type: 'blood' }],
        children: [],
        spouses: [],
        siblings: [],
        data: { 
          name: rel.individual.name, 
          label: 'Extended Relative',
          isIndex: true, 
          kinshipId: rel.individual.id 
        }
      });
    });
    
    // Add FS siblings (Gen 3)
    fsSiblings.forEach(sib => {
      const sibId = createNodeId(sib.name, prefix);
      const otherSiblings = fsSiblingIds.filter(id => id !== sibId);
      
      nodes.push({
        id: sibId,
        gender: genderToNodeGender(sib.gender),
        parents: [{ id: fatherId, type: 'blood' }, { id: motherId, type: 'blood' }],
        children: [],
        spouses: [],
        siblings: otherSiblings.map(id => ({ id, type: 'blood' })),
        data: { 
          name: sib.name, 
          label: 'Full Sibling',
          isIndex: true, 
          kinshipId: sib.id 
        }
      });
    });
    
    const fsSiblingNames = fsSiblings.map(s => s.name).join(', ');
    const extNames = extRelations.map(e => e.individual.name).join(', ');
    
    permutations.push({
      id: 0,
      nodes: nodes,
      rootId: gm1Id,
      label: `Best-Fit FS Family: ${fsSiblings[0].name}`,
      type: 'Best-Fit FS Anchor with Extended',
      category: 'extended',
      details: `FS Siblings: ${fsSiblingNames}; Extended (separate branches): ${extNames}`
    });
  } else {
    // No EXT connected, just the FS siblings with parents (2-gen structure)
    nodes.push({
      id: fatherId,
      gender: 'male',
      parents: [],
      children: fsSiblingIds.map(id => ({ id, type: 'blood' })),
      spouses: [{ id: motherId, type: 'married' }],
      siblings: [],
      data: { name: 'Untested Father', isUntested: true }
    });
    
    nodes.push({
      id: motherId,
      gender: 'female',
      parents: [],
      children: fsSiblingIds.map(id => ({ id, type: 'blood' })),
      spouses: [{ id: fatherId, type: 'married' }],
      siblings: [],
      data: { name: 'Untested Mother', isUntested: true }
    });
    
    fsSiblings.forEach(sib => {
      const sibId = createNodeId(sib.name, prefix);
      const otherSiblings = fsSiblingIds.filter(id => id !== sibId);
      
      nodes.push({
        id: sibId,
        gender: genderToNodeGender(sib.gender),
        parents: [{ id: fatherId, type: 'blood' }, { id: motherId, type: 'blood' }],
        children: [],
        spouses: [],
        siblings: otherSiblings.map(id => ({ id, type: 'blood' })),
        data: { 
          name: sib.name, 
          label: 'Full Sibling',
          isIndex: true, 
          kinshipId: sib.id 
        }
      });
    });
    
    const fsSiblingNames = fsSiblings.map(s => s.name).join(', ');
    
    permutations.push({
      id: 0,
      nodes: nodes,
      rootId: fatherId,
      label: `Best-Fit FS Family: ${fsSiblings[0].name}`,
      type: 'Best-Fit FS Anchor',
      category: 'fullSibling',
      details: `FS Siblings: ${fsSiblingNames}`
    });
  }
  
  return permutations;
}

/**
 * Helper: Generate a nuclear family tree with parent, spouse, and children
 */
function generateNuclearFamilyTree(
  parent: TestedIndividual,
  children: TestedIndividual[],
  prefix: string
): PedigreeNode[] {
  const nodes: PedigreeNode[] = [];
  
  const parentId = createNodeId(parent.name, prefix);
  const spouseId = prefix + 'spouse';
  
  // Add untested spouse
  nodes.push({
    id: spouseId,
    gender: parent.gender === 'M' ? 'female' : 'male',
    parents: [],
    children: children.map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' })),
    spouses: [{ id: parentId, type: 'married' }],
    siblings: [],
    data: { name: parent.gender === 'M' ? 'Untested Mother' : 'Untested Father', isUntested: true }
  });
  
  // Add parent
  nodes.push({
    id: parentId,
    gender: genderToNodeGender(parent.gender),
    parents: [],
    children: children.map(c => ({ id: createNodeId(c.name, prefix), type: 'blood' })),
    spouses: [{ id: spouseId, type: 'married' }],
    siblings: [],
    data: { name: parent.name, label: 'Parent', isIndex: true, kinshipId: parent.id }
  });
  
  // Add children
  children.forEach((child, idx) => {
    const childId = createNodeId(child.name, prefix);
    const siblingIds = children.filter(c => c.id !== child.id).map(c => createNodeId(c.name, prefix));
    
    nodes.push({
      id: childId,
      gender: genderToNodeGender(child.gender),
      parents: [{ id: parentId, type: 'blood' }, { id: spouseId, type: 'blood' }],
      children: [],
      spouses: [],
      siblings: siblingIds.map(sid => ({ id: sid, type: 'blood' })),
      data: { name: child.name, label: `Child ${idx + 1}`, isIndex: true, kinshipId: child.id }
    });
  });
  
  return nodes;
}

/**
 * Helper: Generate extended family tree B with grandparents
 */
function generateExtendedFamilyTreeB(
  groups: TestedIndividual[][],
  avuncularIndividuals: TestedIndividual[],
  prefix: string
): PedigreeNode[] {
  const nodes: PedigreeNode[] = [];
  
  const gfId = prefix + 'grandfather';
  const gmId = prefix + 'grandmother';
  const afId = prefix + 'untested_af1';
  
  // Add grandparents
  nodes.push({
    id: gfId,
    gender: 'male',
    parents: [],
    children: [{ id: afId, type: 'blood' }],
    spouses: [{ id: gmId, type: 'married' }],
    siblings: [],
    data: { name: 'Untested Grandfather', isUntested: true }
  });
  
  nodes.push({
    id: gmId,
    gender: 'female',
    parents: [],
    children: [{ id: afId, type: 'blood' }],
    spouses: [{ id: gfId, type: 'married' }],
    siblings: [],
    data: { name: 'Untested Grandmother', isUntested: true }
  });
  
  // Add untested parent for the group
  const childIds = groups.flatMap(g => g.map(ind => createNodeId(ind.name, prefix)));
  nodes.push({
    id: afId,
    gender: 'male',
    parents: [{ id: gfId, type: 'blood' }, { id: gmId, type: 'blood' }],
    children: childIds.map(cid => ({ id: cid, type: 'blood' })),
    spouses: [],
    siblings: [],
    data: { name: 'Untested AF1', isUntested: true }
  });
  
  // Add tested individuals
  groups.forEach(group => {
    group.forEach((ind, idx) => {
      const indId = createNodeId(ind.name, prefix);
      const siblingIds = group.filter(g => g.id !== ind.id).map(g => createNodeId(g.name, prefix));
      
      nodes.push({
        id: indId,
        gender: genderToNodeGender(ind.gender),
        parents: [{ id: afId, type: 'blood' }],
        children: [],
        spouses: [],
        siblings: siblingIds.map(sid => ({ id: sid, type: 'blood' })),
        data: { name: ind.name, label: `Extended ${idx + 1}`, isIndex: true, kinshipId: ind.id }
      });
    });
  });
  
  // Add avuncular individuals as siblings of the untested parent
  avuncularIndividuals.forEach((av, idx) => {
    const avId = createNodeId(av.name, prefix);
    
    // Check if already added
    if (!nodes.some(n => n.id === avId)) {
      nodes.push({
        id: avId,
        gender: genderToNodeGender(av.gender),
        parents: [{ id: gfId, type: 'blood' }, { id: gmId, type: 'blood' }],
        children: [],
        spouses: [],
        siblings: [{ id: afId, type: 'blood' }],
        data: { name: av.name, label: `Avuncular`, isIndex: true, kinshipId: av.id }
      });
      
      // Update grandparents' children
      nodes[0].children.push({ id: avId, type: 'blood' });
      nodes[1].children.push({ id: avId, type: 'blood' });
    }
  });
  
  return nodes;
}

// ============================================================================
// MAIN GENERATOR
// ============================================================================

/**
 * Age profile for generational filtering
 */
export interface AgeProfile {
  id: string;
  label: string;
  motherAge: number;  // Average mother's age at first child's birth
  fatherAge: number;  // Average father's age at first child's birth
  maxSiblingGap: number;  // Maximum age difference between siblings
}

/**
 * Options for permutation generation
 */
export interface PermutationOptions {
  /**
   * When true (default), cousin-type permutations are suppressed from the output.
   * Cousin types include: 'Extended Family (Cousins)', 'Disjointed Family', 
   * and any permutation type containing 'Cousins'.
   */
  suppressCousins?: boolean;
  
  /**
   * Age profile for generational filtering. When provided with valid ages,
   * permutations where a child is older than or too close in age to a parent
   * will be filtered out based on the regional average parent age.
   */
  ageProfile?: AgeProfile;
  
  /**
   * When true, enables "best-fit" mode that prioritizes strong PC evidence over
   * conflicting FS/EXT relationships. This allows generation of pedigrees where:
   * - PC relationships determine generational placement
   * - Cross-generation FS evidence is treated as half-sibling relationships
   * - Disjoint multi-family structures are generated even with partial evidence
   * Default is false (strict mode requiring consistent kinship evidence).
   */
  bestFitMode?: boolean;
}

/**
 * Checks if a permutation is a cousin-type structure that should be suppressed.
 * This includes:
 * - Permutations with cousin-type types (Extended Family Cousins, Disjointed Family)
 * - Permutations augmented with "+ Extended Cousins" in their label (nodesB cousin augmentation)
 */
function isCousinTypePermutation(perm: Permutation): boolean {
  // Check type field
  const isCousinType = (
    perm.type === 'Extended Family (Cousins)' ||
    perm.type === 'Extended Family Network' ||
    perm.type === 'Disjointed Family' ||
    perm.type.includes('Cousins')
  );
  
  // Check label field for augmented cousin structures
  const hasCousinLabel = perm.label.includes('Cousins');
  
  return isCousinType || hasCousinLabel;
}

/**
 * Parse age string to numeric value.
 * Handles decimal ages like "50.4" and integer ages like "50".
 * Returns null if age is invalid, "Unspecified", or "Unknown".
 */
function parseAge(age: string | null | undefined): number | null {
  if (!age || age === 'Unspecified' || age === 'Unknown') {
    return null;
  }
  const parsed = parseFloat(age);
  return isNaN(parsed) ? null : parsed;
}

/**
 * Checks if a permutation has valid generational age relationships.
 * A parent must be at least [average parent age] years older than their child.
 * 
 * Rules:
 * 1. For each parent-child relationship in the permutation:
 *    - If both parent and child have numeric ages
 *    - Parent must be at least (motherAge or fatherAge based on parent gender) years older than child
 * 2. If either parent or child lacks age data, the relationship is allowed (no filtering)
 * 3. Only applies when ageProfile is provided and has non-zero values
 */
function hasValidGenerationalAges(
  perm: Permutation,
  ageProfile: AgeProfile | undefined,
  ageLookup: Map<string, number | null>
): boolean {
  // If no age profile or "No Age Data" profile, allow all permutations
  if (!ageProfile || ageProfile.id === 'none' || (ageProfile.motherAge === 0 && ageProfile.fatherAge === 0)) {
    return true;
  }
  
  // Check all nodes (including nodesB for disjointed permutations)
  const allNodes = [...perm.nodes, ...(perm.nodesB || [])];
  
  // Build a map of node ID to node for quick lookup
  const nodeById = new Map<string, PedigreeNode>();
  allNodes.forEach(node => nodeById.set(node.id, node));
  
  // For each node that has children, check parent-child age relationships
  for (const parentNode of allNodes) {
    // Skip untested individuals (they don't have age data)
    if (parentNode.data.isUntested) continue;
    
    const parentAge = ageLookup.get(parentNode.data.name.toLowerCase());
    if (parentAge === null || parentAge === undefined) continue; // No parent age data
    
    // Determine minimum age difference based on parent gender
    const minAgeDiff = parentNode.gender === 'male' ? ageProfile.fatherAge : ageProfile.motherAge;
    
    // Check each child relationship
    for (const childRef of parentNode.children) {
      const childNode = nodeById.get(childRef.id);
      if (!childNode || childNode.data.isUntested) continue;
      
      const childAge = ageLookup.get(childNode.data.name.toLowerCase());
      if (childAge === null || childAge === undefined) continue; // No child age data
      
      // Parent must be at least minAgeDiff years older than child
      const ageDifference = parentAge - childAge;
      
      if (ageDifference < minAgeDiff) {
        // Invalid: parent is not old enough relative to child
        // This covers: child older than parent, or parent too young to be biological parent
        return false;
      }
    }
  }
  
  return true;
}

/**
 * Checks if a permutation has valid sibling age gaps.
 * Siblings (nodes sharing the same parents) must not have an age difference
 * greater than the regional maximum sibling gap.
 * 
 * Rules:
 * 1. For each pair of siblings (nodes with same parents) in the permutation:
 *    - If both siblings have numeric ages
 *    - Their age difference must not exceed maxSiblingGap
 * 2. If either sibling lacks age data, the relationship is allowed (no filtering)
 * 3. Only applies when ageProfile is provided with a non-zero maxSiblingGap
 */
function hasValidSiblingAgeGaps(
  perm: Permutation,
  ageProfile: AgeProfile | undefined,
  ageLookup: Map<string, number | null>
): boolean {
  // If no age profile or "No Age Data" profile, allow all permutations
  if (!ageProfile || ageProfile.id === 'none' || ageProfile.maxSiblingGap === 0) {
    return true;
  }
  
  const maxGap = ageProfile.maxSiblingGap;
  
  // Check all nodes (including nodesB for disjointed permutations)
  const allNodes = [...perm.nodes, ...(perm.nodesB || [])];
  
  // Build a map of node ID to node for quick lookup
  const nodeById = new Map<string, PedigreeNode>();
  allNodes.forEach(node => nodeById.set(node.id, node));
  
  // Find sibling groups (nodes that share the same parents)
  // For each parent node, collect all its children
  for (const parentNode of allNodes) {
    if (parentNode.children.length < 2) continue; // Need at least 2 children for siblings
    
    // Get all children of this parent
    const childrenNodes: PedigreeNode[] = [];
    for (const childRef of parentNode.children) {
      const childNode = nodeById.get(childRef.id);
      if (childNode && !childNode.data.isUntested) {
        childrenNodes.push(childNode);
      }
    }
    
    // Check all pairs of siblings
    for (let i = 0; i < childrenNodes.length; i++) {
      const sibling1 = childrenNodes[i];
      const age1 = ageLookup.get(sibling1.data.name.toLowerCase());
      if (age1 === null || age1 === undefined) continue;
      
      for (let j = i + 1; j < childrenNodes.length; j++) {
        const sibling2 = childrenNodes[j];
        const age2 = ageLookup.get(sibling2.data.name.toLowerCase());
        if (age2 === null || age2 === undefined) continue;
        
        // Check age difference
        const ageDiff = Math.abs(age1 - age2);
        if (ageDiff > maxGap) {
          // Invalid: sibling age gap exceeds maximum
          return false;
        }
      }
    }
  }
  
  return true;
}

export function generateDynamicPermutations(
  kinshipPairs: KinshipPair[],
  options: PermutationOptions = {}
): Permutation[] {
  // Default: suppress cousins is ON (checked)
  const { suppressCousins = true, ageProfile, bestFitMode = false } = options;
  if (!kinshipPairs || kinshipPairs.length === 0) {
    return [];
  }
  
  const graph = buildKinshipGraph(kinshipPairs);
  const individuals = graph.individuals;
  const testedNames = new Set(individuals.map(i => i.name.toLowerCase()));
  
  // Create numeric age lookup for generational filtering
  const numericAgeLookup = new Map<string, number | null>();
  individuals.forEach(ind => {
    numericAgeLookup.set(ind.name.toLowerCase(), parseAge(ind.age));
  });
  
  const rawPermutations: Permutation[] = [];
  
  // Check if we have the standard quartet case (2M, 2F)
  const isQuartetCase = graph.males.length === 2 && graph.females.length === 2;
  
  if (isQuartetCase) {
    // Use existing quartet-specific generators for backward compatibility
    rawPermutations.push(...generateParentChildPermutations(graph));
    rawPermutations.push(...generateFullSiblingPermutations(graph));
    rawPermutations.push(...generateExtendedPermutations(graph));
    
    // BEST-FIT MODE (Rule 30): Also apply to quartet cases
    // When enabled, generates pedigrees prioritizing strong PC evidence over conflicting FS/EXT
    if (bestFitMode) {
      rawPermutations.push(...generatePCPriorityDisjointPermutations(graph, kinshipPairs));
    }
  } else {
    // Use generalized N-individual generators
    rawPermutations.push(...generateGeneralizedParentChildPermutations(graph));
    rawPermutations.push(...generateGeneralizedExtendedPermutations(graph));
    
    // Add flexible generators for non-quartet cases
    rawPermutations.push(...generateFlexibleParentChildPermutations(graph));
    rawPermutations.push(...generateFlexibleFullSiblingPermutations(graph));
    rawPermutations.push(...generateFlexibleExtendedPermutations(graph));
    rawPermutations.push(...generateMultiAvuncularPermutations(graph));
    rawPermutations.push(...generateBlendedHalfSiblingPermutations(graph));
    rawPermutations.push(...generateHalfSiblingUntestedParentsPermutations(graph, kinshipPairs));
    
    // RULE 25: Complex Nuclear Half-Sibling Family generator
    // Generates shared-parent structures with multiple untested parents
    rawPermutations.push(...generateComplexNuclearHalfSiblingPermutations(graph, kinshipPairs));
    
    // Universal fallback generator (Rule 20)
    // This handles cases where specialized generators fail
    rawPermutations.push(...generateUniversalExtendedPermutations(graph, kinshipPairs));
    
    // RULE 21: Generate disjointed permutations for cases with limited connectivity
    // This ensures ALL individuals from the combinatorial list are included
    rawPermutations.push(...generateDisjointedPermutations(graph));
    
    // BEST-FIT MODE (Rule 30): PC-priority disjoint generator
    // When enabled, generates pedigrees prioritizing strong PC evidence over conflicting FS/EXT
    if (bestFitMode) {
      rawPermutations.push(...generatePCPriorityDisjointPermutations(graph, kinshipPairs));
    }
    
    // Also try the quartet generators as fallback (they'll return empty if not applicable)
    // This ensures any edge cases are covered
    if (rawPermutations.length === 0) {
      rawPermutations.push(...generateParentChildPermutations(graph));
      rawPermutations.push(...generateFullSiblingPermutations(graph));
      rawPermutations.push(...generateExtendedPermutations(graph));
    }
  }
  
  // STEP 1: Enforce full coverage (Rule 21)
  // Every permutation must include ALL individuals from the kinship graph
  // If missing, augment with nodesB structure; if impossible, filter out
  const augmentedPermutations = rawPermutations
    .map(perm => enforceFullCoverage(perm, graph))
    .filter((perm): perm is Permutation => perm !== null);
  
  // Filter 1: Validate permutation individuals have kinship support
  const filteredByIndividuals = augmentedPermutations.filter(perm => {
    const permTestedNames = getTestedNamesFromPermutation(perm.nodes, perm.nodesB);
    return hasInternalKinshipSupport(permTestedNames, graph);
  });
  
  // Filter 2 (Rule 14): All sibling assertions must have FS support in kinship graph
  // In best-fit mode, skip this filter for best-fit permutation types
  const filteredBySiblings = filteredByIndividuals.filter(perm => {
    if (bestFitMode && perm.type.includes('Best-Fit')) {
      return true; // Skip sibling assertion check for best-fit permutations
    }
    return hasValidSiblingAssertions(perm, graph);
  });
  
  // Filter 3 (Rule 15): All tested individual pairs must have at least one kinship edge
  // Only applies to pairs that are blood-related in the pedigree structure
  // In best-fit mode, skip this filter for best-fit permutation types
  const filteredByPairwise = filteredBySiblings.filter(perm => {
    if (bestFitMode && perm.type.includes('Best-Fit')) {
      return true; // Skip pairwise support check for best-fit permutations
    }
    return hasCompletePairwiseSupport(perm, graph);
  });
  
  // Filter 4 (Rule 23): Prevent cousin structures when PC evidence indicates closer relationship
  // If two individuals in a "cousin" permutation both have PC edges to the same tested parent,
  // they should be siblings (not cousins) and the cousin permutation is invalid
  const filteredByCousinPC = filteredByPairwise.filter(perm =>
    isValidCousinStructure(perm, graph)
  );
  
  // Filter 5: Ensure full coverage (final check)
  const filteredByFullCoverage = filteredByCousinPC.filter(perm =>
    hasFullCoverage(perm, graph)
  );
  
  // Filter 6 (Rule 16): Deduplicate permutations that differ only by child ordering
  // When multiple children share the same parent, their ordering doesn't matter
  // We keep only one canonical representation (alphabetically ordered children)
  const deduplicatedPermutations = deduplicateByChildOrder(filteredByFullCoverage);
  
  // Filter 7 (Rule 28 - Age-Based Generational Filtering):
  // When age data is available and an Age Region is selected,
  // filter out biologically implausible pedigrees where a parent is not old enough
  // relative to their children (based on regional average parent age at first child's birth)
  const filteredByParentAge = deduplicatedPermutations.filter(perm =>
    hasValidGenerationalAges(perm, ageProfile, numericAgeLookup)
  );
  
  // Filter 8 (Rule 29 - Sibling Age Gap Filtering):
  // When age data is available and an Age Region is selected,
  // filter out pedigrees where sibling age differences exceed the regional maximum
  const validPermutations = filteredByParentAge.filter(perm =>
    hasValidSiblingAgeGaps(perm, ageProfile, numericAgeLookup)
  );
  
  // Compute likelihood scores for each permutation
  const scoredPermutations = validPermutations.map(perm => ({
    ...perm,
    likelihoodScore: computeCompositeLikelihoodScore(
      perm.nodes,
      perm.nodesB,
      perm.type,
      graph
    )
  }));
  
  // RULE 22: Remove permutations with zero score
  // A score of 0 indicates the permutation is missing kinship evidence
  // or doesn't properly include all individuals from the LR > 1 list
  const nonZeroScorePermutations = scoredPermutations.filter(perm => {
    const score = perm.likelihoodScore?.compositeScore ?? 0;
    return score > 0;
  });
  
  // RULE 24 Part 2: Prefer complete multi-avuncular structures over disjointed/cousin variants
  // If a Multi-Avuncular permutation exists with same or higher score, suppress disjointed cousins
  const hasMultiAvuncular = nonZeroScorePermutations.some(p => 
    p.type === 'Multi-Avuncular' || p.type.includes('Multi-Avuncular')
  );
  
  // RULE 26 Part 2: Prefer Complex Nuclear 2nd degree structures over disjointed/cousin variants
  // Complex nuclear structures (half-siblings, avuncular, etc.) are preferred as they represent 
  // complete 2-3 generation family structures with shared untested parents
  const hasComplexNuclear2ndDegree = nonZeroScorePermutations.some(p => 
    p.type.includes('Complex Nuclear')
  );
  
  // Get maximum score from complex nuclear structures for comparison
  const complexNuclearMaxScore = nonZeroScorePermutations
    .filter(p => p.type.includes('Complex Nuclear'))
    .reduce((max, p) => Math.max(max, p.likelihoodScore?.compositeScore ?? 0), 0);
  
  const preferredPermutations = (hasMultiAvuncular || hasComplexNuclear2ndDegree)
    ? nonZeroScorePermutations.filter(perm => {
        // Keep multi-avuncular permutations
        if (perm.type === 'Multi-Avuncular' || perm.type.includes('Multi-Avuncular')) {
          return true;
        }
        // Keep complex nuclear 2nd degree permutations (half-siblings, avuncular, etc.)
        if (perm.type.includes('Complex Nuclear')) {
          return true;
        }
        // Filter out disjointed/cousin structures when nuclear/multi-avuncular exists
        // Unless their score is significantly higher (> 1 log10 better)
        const isDisjointedOrCousin = perm.type === 'Disjointed Family' || 
            perm.type.includes('Disjointed') || 
            perm.type.includes('Cousins') || 
            perm.type === 'Extended Family Network';
        
        if (isDisjointedOrCousin) {
          const permScore = perm.likelihoodScore?.compositeScore ?? 0;
          // Only keep if score is significantly better than nuclear structures
          if (hasComplexNuclear2ndDegree && permScore <= complexNuclearMaxScore + 1) {
            return false;
          }
          // Also filter if multi-avuncular exists
          if (hasMultiAvuncular && !hasComplexNuclear2ndDegree) {
            return false;
          }
        }
        // Keep other permutation types (Parent/Child, Full Siblings, etc.)
        return true;
      })
    : nonZeroScorePermutations;
  
  // USER OPTION: Suppress cousin scenarios (default ON)
  // When enabled, filters out cousin-type permutations from the final output
  const cousinFiltered = suppressCousins
    ? preferredPermutations.filter(perm => !isCousinTypePermutation(perm))
    : preferredPermutations;
  
  // RULE 27: Structural deduplication
  // Merge permutations that have identical structure (same sibling groups, same parent sharing)
  // and same score, keeping only one representative to reduce clutter
  const finalPermutations = deduplicateByStructure(cousinFiltered);
  
  // Sort by composite score (descending - highest likelihood first)
  finalPermutations.sort((a, b) => {
    const scoreA = a.likelihoodScore?.compositeScore ?? -Infinity;
    const scoreB = b.likelihoodScore?.compositeScore ?? -Infinity;
    return scoreB - scoreA;
  });
  
  // Add age data to all nodes (lookup from graph.individualById)
  // Create age lookup by name (case-insensitive)
  const ageByName = new Map<string, string | null>();
  individuals.forEach(ind => {
    ageByName.set(ind.name.toLowerCase(), ind.age ?? null);
  });
  
  // Helper function to add age to node data
  const addAgeToNodes = (nodes: PedigreeNode[]): PedigreeNode[] => {
    return nodes.map(node => {
      if (node.data && !node.data.isUntested) {
        const nodeName = node.data.name.toLowerCase();
        const age = ageByName.get(nodeName);
        return {
          ...node,
          data: {
            ...node.data,
            age: age
          }
        };
      }
      return node;
    });
  };
  
  // Apply age data to all permutation nodes
  const permutationsWithAge = finalPermutations.map(perm => ({
    ...perm,
    nodes: addAgeToNodes(perm.nodes),
    nodesB: perm.nodesB ? addAgeToNodes(perm.nodesB) : undefined
  }));
  
  // Assign sequential IDs (after sorting, so ID 1 = highest score)
  return permutationsWithAge.map((perm, idx) => ({
    ...perm,
    id: idx + 1
  }));
}
